import { useChatGptFilter, type ChatGptFilter } from "../lib/useChatGptFilter";
import { ChatGptFilterButton } from "../components/ChatGptFilterButton";
import { useState, useRef, useCallback, useEffect, useMemo, memo } from "react";
import { LtxVideoPanel } from "../components/video/LtxVideoPanel";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Konva from "konva";
import { useActiveShopId, useShopStore } from "../store/shop.store";
// react-image-crop removed — using custom canvas-based crop
import {
  Upload,
  Download,
  Trash2,
  CheckSquare,
  Check,
  Square,
  Package,
  Grid3X3,
  Loader2,
  CheckCircle,
  Image,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  DownloadCloud,
  Plus,
  Settings2,
  Move,
  Save,
  X,
  Search,
  ImagePlus,
  FolderOpen,
  Palette,
  Pipette,
  Undo2,
  Redo2,
  FlipHorizontal,
  FlipVertical,
  RotateCcw,
  Sun,
  Moon,
  ArrowRight,
  FileText,
  Eraser,
  Crop as CropIcon,
  ExternalLink,
  Sparkles,
  Camera,
  Wand2,
  RefreshCw,
  Star,
  Film,
} from "lucide-react";
import { MOCKUP_TEMPLATES, normalizeColorLabel, colorLabelText, colorLabelBox, colorLabelBrushPath, type MockupColorLabel } from "@erc/shared";
import type { CustomMockup, MockupCollection, MockupFolder } from "@erc/shared";
import { apiClient } from "../lib/api";
import { modelLabel } from "../lib/useDesignModel";
import { useSupplierVariations } from "../lib/useSupplierVariations";
import type { PersonalizationField, Analysis } from "@erc/shared";
import { PersonalizationFieldsEditor } from "../components/analysis/PersonalizationFieldsEditor";
import env from "../env";
import { DesignAreaEditor, type AreaRect } from "../components/mockup/DesignAreaEditor";
import { FreeDesignEditor } from "../components/mockup/FreeDesignEditor";
import { DesignConsistencyModal } from "../components/mockup/DesignConsistencyModal";
import { useShopVertical } from "../lib/useShopVertical";
import { authHeaders } from "../lib/http";
import { WarpedAreaImage } from "../components/mockup/WarpedAreaImage";
import { finishDesignCanvas, maskToAlpha, surfaceCover, SURFACE_MIN_COVER } from "../lib/mockup-finish-canvas";
import { type AreaShape, type AreaFit, type AreaWarp, isShaped, placeInArea, traceAreaShape, hasWarp, renderWarpedArea } from "../lib/area-shape";

const API_BASE = env.API_URL;

// ======== Types ========

interface DesignState {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

type BuiltinTemplate = (typeof MOCKUP_TEMPLATES)[number];

interface UnifiedTemplate {
  id: string;
  label: string;
  category: string;
  bgColor: string;
  canvasWidth: number;
  canvasHeight: number;
  designArea: { x: number; y: number; width: number; height: number; rotation?: number };
  // All design areas (custom mockups can have many). For builtin/single-area
  // this is omitted and the single `designArea` is used.
  designAreas?: { id: string; x: number; y: number; width: number; height: number; rotation?: number; label?: string; theme?: "light" | "dark"; shape?: AreaShape; fit?: AreaFit; warp?: AreaWarp | null; surfaceUrl?: string }[];
  isCustom: boolean;
  imageUrl?: string;
  thumbnailUrl?: string; // kucuk onizleme (picker icin)
  brand?: string | null; // katalogdan atanan marka
  model?: string | null; // katalogdan atanan model/kategori
  color?: string | null; // katalogdan atanan renk
  customMockupId?: string;
  /** Mockup Yonetimi'ndeki klasor (dosya) — picker'da klasore gore secim icin */
  folderId?: string | null;
  opacity: number;
  theme?: "light" | "dark";
  /** Tasarimin USTUNE cizilecek on plan PNG'si (sac, kumas kivrimi) */
  foregroundUrl?: string;
  /** Isik haritasi (hard-light) ve gucu — tasarima urunun parlama/golgesi */
  shadeUrl?: string;
  shading?: number;
  /** Kirisik: egim haritasi + gucu; fotografin greni (tasarima eklenir) */
  dispUrl?: string;
  displace?: number;
  photoNoise?: number;
  /** Renk adi etiketi (sunucu render'iyla ayni geometri) */
  colorLabel?: MockupColorLabel | null;
}

function builtinToUnified(t: BuiltinTemplate): UnifiedTemplate {
  const isDark = t.bgColor === "#1a1a1a" || t.bgColor === "#1b2a4a";
  return {
    id: t.id,
    label: t.label,
    category: t.category,
    bgColor: t.bgColor,
    canvasWidth: t.canvasWidth,
    canvasHeight: t.canvasHeight,
    designArea: { ...t.designArea },
    isCustom: false,
    opacity: 100,
    theme: isDark ? "dark" : "light",
  };
}

function customToUnified(c: CustomMockup): UnifiedTemplate {
  const rawAreas = Array.isArray((c as any).designAreas) ? (c as any).designAreas as Array<{ id?: string; x: number; y: number; w: number; h: number; rotation?: number; label?: string; theme?: "light" | "dark"; shape?: AreaShape; fit?: AreaFit; warp?: AreaWarp | null; surfaceUrl?: string }> : [];
  const designAreas = rawAreas.length > 0
    ? rawAreas.map((a, i) => ({ id: a.id || `area-${i + 1}`, x: a.x, y: a.y, width: a.w, height: a.h, rotation: a.rotation || 0, label: a.label, theme: a.theme, shape: a.shape, fit: a.fit, warp: a.warp, surfaceUrl: a.surfaceUrl }))
    : [{ id: "area-1", x: c.designAreaX, y: c.designAreaY, width: c.designAreaW, height: c.designAreaH, rotation: c.designAreaRotation || 0 }];
  return {
    id: `custom_${c.id}`,
    label: c.label,
    category: c.category,
    bgColor: "#ffffff",
    canvasWidth: c.imageWidth,
    canvasHeight: c.imageHeight,
    designArea: { x: c.designAreaX, y: c.designAreaY, width: c.designAreaW, height: c.designAreaH, rotation: c.designAreaRotation || 0 },
    designAreas,
    isCustom: true,
    imageUrl: c.imageUrl.startsWith("http") ? c.imageUrl : `${API_BASE}${c.imageUrl}`,
    foregroundUrl: c.foregroundUrl
      ? (c.foregroundUrl.startsWith("http") ? c.foregroundUrl : `${API_BASE}${c.foregroundUrl}`)
      : undefined,
    shadeUrl: c.shadeUrl ? (c.shadeUrl.startsWith("http") ? c.shadeUrl : `${API_BASE}${c.shadeUrl}`) : undefined,
    shading: c.shading ?? 0,
    dispUrl: c.dispUrl ? (c.dispUrl.startsWith("http") ? c.dispUrl : `${API_BASE}${c.dispUrl}`) : undefined,
    displace: c.displace ?? 0,
    photoNoise: c.photoNoise ?? 0,
    colorLabel: (c.kind ?? "mockup") === "mockup" ? normalizeColorLabel(c.colorLabel) : null,
    thumbnailUrl: c.thumbnailUrl ? (c.thumbnailUrl.startsWith("http") ? c.thumbnailUrl : `${API_BASE}${c.thumbnailUrl}`) : undefined,
    brand: c.brand ?? null,
    model: c.model ?? null,
    color: c.color ?? null,
    customMockupId: c.id,
    folderId: c.folderId ?? null,
    opacity: c.opacity,
    theme: c.theme === "dark" ? "dark" : "light",
  };
}

// ======== Color Utils ========

function rgbToHex(r: number, g: number, b: number): string {
  return "#" + [r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("");
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function colorDistance(r1: number, g1: number, b1: number, r2: number, g2: number, b2: number): number {
  return Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2);
}

// ======== Fit design preserving aspect ratio ========

// Dik (ya da kareye yakin) alanda genis tasarim USTE hizalanir (tisort baskisi yakanin altindan
// baslar); yatay alanda ortalanir. Sunucudaki (mockup-render.service) ile ayni kural.
function fitDesignInArea(
  area: { x: number; y: number; width: number; height: number; rotation?: number },
  designNaturalWidth: number,
  designNaturalHeight: number
): DesignState {
  const rotation = area.rotation || 0;
  if (!designNaturalWidth || !designNaturalHeight) {
    return { x: area.x, y: area.y, width: area.width, height: area.height, rotation };
  }
  const areaRatio = area.width / area.height;
  const designRatio = designNaturalWidth / designNaturalHeight;
  let w: number, h: number;
  if (designRatio > areaRatio) {
    w = area.width;
    h = area.width / designRatio;
  } else {
    h = area.height;
    w = area.height * designRatio;
  }
  const x = area.x + (area.width - w) / 2;
  const y = area.height >= area.width * 0.95 ? area.y : area.y + (area.height - h) / 2;
  return { x, y, width: w, height: h, rotation };
}

// ======== Inline Design Editor Panel ========

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function DesignEditorPanel({
  designUrl,
  onSaveLight,
  onSaveDark,
  onClearLight,
  onClearDark,
  onClose,
  onApplyToCanvas,
  onCanvaDesign,
  lightDesignUrl,
  darkDesignUrl,
}: {
  designUrl: string | null;
  onSaveLight: (dataUrl: string) => void;
  onSaveDark: (dataUrl: string) => void;
  onClearLight: () => void;
  onClearDark: () => void;
  onClose: () => void;
  onApplyToCanvas: (dataUrl: string) => void;
  onCanvaDesign?: (designId: string, editUrl: string) => void;
  lightDesignUrl: string | null;
  darkDesignUrl: string | null;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [imageData, setImageData] = useState<ImageData | null>(null);
  const [originalImageData, setOriginalImageData] = useState<ImageData | null>(null);
  const [imageSize, setImageSize] = useState({ w: 0, h: 0 });
  const [history, setHistory] = useState<ImageData[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [pickingColor, setPickingColor] = useState(false);
  const [sourceColor, setSourceColor] = useState("#000000");
  const [targetColor, setTargetColor] = useState("#ffffff");
  const [tolerance, setTolerance] = useState(30);
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const [bgRemoveTolerance, setBgRemoveTolerance] = useState(40);
  const [showBgOptions, setShowBgOptions] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [cropImageUrl, setCropImageUrl] = useState<string | null>(null);
  const cropContainerRef = useRef<HTMLDivElement>(null);
  const [cropRect, setCropRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [cropDragging, setCropDragging] = useState(false);
  const [cropStart, setCropStart] = useState<{ x: number; y: number } | null>(null);
  const [cropMouse, setCropMouse] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (designUrl) loadImageFromUrl(designUrl);
  }, [designUrl]);

  function loadImageFromUrl(url: string) {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      setImageSize({ w: img.naturalWidth, h: img.naturalHeight });
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, img.naturalWidth, img.naturalHeight);
      setImageData(data);
      setOriginalImageData(new ImageData(new Uint8ClampedArray(data.data), data.width, data.height));
      pushHistory(data);
    };
    img.src = url;
  }

  function pushHistory(data: ImageData) {
    const clone = new ImageData(new Uint8ClampedArray(data.data), data.width, data.height);
    setHistory((prev) => {
      const next = prev.slice(0, historyIndex + 1);
      next.push(clone);
      if (next.length > 30) next.shift();
      return next;
    });
    setHistoryIndex((prev) => Math.min(prev + 1, 29));
  }

  function applyImageData(data: ImageData) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    ctx.putImageData(data, 0, 0);
    setImageData(new ImageData(new Uint8ClampedArray(data.data), data.width, data.height));
  }

  function undo() {
    if (historyIndex <= 0) return;
    const prev = history[historyIndex - 1];
    if (prev) { setHistoryIndex((i) => i - 1); applyImageData(prev); }
  }

  function redo() {
    if (historyIndex >= history.length - 1) return;
    const next = history[historyIndex + 1];
    if (next) { setHistoryIndex((i) => i + 1); applyImageData(next); }
  }

  function handleCanvasClick(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!pickingColor || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = Math.floor((e.clientX - rect.left) * scaleX);
    const y = Math.floor((e.clientY - rect.top) * scaleY);
    const ctx = canvas.getContext("2d")!;
    const pixel = ctx.getImageData(x, y, 1, 1).data;
    setSourceColor(rgbToHex(pixel[0], pixel[1], pixel[2]));
    setPickingColor(false);
  }

  function applyColorSwap() {
    if (!imageData) return;
    const src = hexToRgb(sourceColor);
    const tgt = hexToRgb(targetColor);
    const newData = new ImageData(new Uint8ClampedArray(imageData.data), imageData.width, imageData.height);
    const pixels = newData.data;
    for (let i = 0; i < pixels.length; i += 4) {
      const dist = colorDistance(pixels[i], pixels[i + 1], pixels[i + 2], src[0], src[1], src[2]);
      if (dist <= tolerance) {
        const blend = 1 - dist / tolerance;
        pixels[i] = Math.round(pixels[i] + (tgt[0] - pixels[i]) * blend);
        pixels[i + 1] = Math.round(pixels[i + 1] + (tgt[1] - pixels[i + 1]) * blend);
        pixels[i + 2] = Math.round(pixels[i + 2] + (tgt[2] - pixels[i + 2]) * blend);
      }
    }
    applyImageData(newData);
    pushHistory(newData);
  }

  function invertColors() {
    if (!imageData) return;
    const newData = new ImageData(new Uint8ClampedArray(imageData.data), imageData.width, imageData.height);
    const pixels = newData.data;
    for (let i = 0; i < pixels.length; i += 4) {
      pixels[i] = 255 - pixels[i];
      pixels[i + 1] = 255 - pixels[i + 1];
      pixels[i + 2] = 255 - pixels[i + 2];
    }
    applyImageData(newData);
    pushHistory(newData);
  }

  function flipH() {
    if (!imageData || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d")!;
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempCtx = tempCanvas.getContext("2d")!;
    tempCtx.putImageData(imageData, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(-1, 1);
    ctx.drawImage(tempCanvas, -canvas.width, 0);
    ctx.restore();
    const newData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    setImageData(newData);
    pushHistory(newData);
  }

  function flipV() {
    if (!imageData || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d")!;
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempCtx = tempCanvas.getContext("2d")!;
    tempCtx.putImageData(imageData, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(1, -1);
    ctx.drawImage(tempCanvas, 0, -canvas.height);
    ctx.restore();
    const newData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    setImageData(newData);
    pushHistory(newData);
  }

  function resetToOriginal() {
    if (!originalImageData) return;
    applyImageData(originalImageData);
    pushHistory(originalImageData);
  }

  // Color-based bg removal: makes pixels near the target color transparent
  function handleRemoveColorBg(color?: string) {
    if (!imageData || !canvasRef.current) return;
    const bgColor = color ?? "#ffffff";
    const [bgR, bgG, bgB] = hexToRgb(bgColor);
    const tol = bgRemoveTolerance;
    const newData = new ImageData(new Uint8ClampedArray(imageData.data), imageData.width, imageData.height);
    const pixels = newData.data;
    // Edge zone: only the last 20% of tolerance range gets partial alpha
    const hardCutoff = tol * 0.8;
    const edgeRange = tol - hardCutoff;
    for (let i = 0; i < pixels.length; i += 4) {
      const dist = colorDistance(pixels[i], pixels[i + 1], pixels[i + 2], bgR, bgG, bgB);
      if (dist <= tol) {
        if (dist <= hardCutoff) {
          // Fully transparent — clear match to bg
          pixels[i + 3] = 0;
        } else {
          // Steep edge transition for smooth anti-aliasing
          const t = (dist - hardCutoff) / edgeRange; // 0..1
          pixels[i + 3] = Math.min(pixels[i + 3], Math.round(t * t * 255));
        }
      }
    }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.putImageData(newData, 0, 0);
    setImageData(new ImageData(new Uint8ClampedArray(newData.data), newData.width, newData.height));
    pushHistory(newData);
    setShowBgOptions(false);
  }

  // AI-based bg removal (for photos, aggressive)
  async function handleRemoveBgAI() {
    if (!canvasRef.current) return;
    setIsRemovingBg(true);
    setShowBgOptions(false);
    try {
      const { removeBackground } = await import("@imgly/background-removal");
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const blob = await removeBackground(dataUrl, {
        output: { format: "image/png" },
      });
      const url = URL.createObjectURL(blob);
      const img = new window.Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d")!;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        const newData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        setImageData(newData);
        pushHistory(newData);
        URL.revokeObjectURL(url);
      };
      img.src = url;
    } catch (err) {
      console.error("Background removal failed:", err);
      alert("Arka plan kaldırma başarısız oldu.");
    } finally {
      setIsRemovingBg(false);
    }
  }

  function startCrop() {
    if (!canvasRef.current) return;
    const url = canvasRef.current.toDataURL("image/png");
    setCropImageUrl(url);
    setIsCropping(true);
    setCropRect(null);
    setCropStart(null);
    setCropMouse(null);
  }

  function getCropPos(e: React.MouseEvent): { x: number; y: number } | null {
    const container = cropContainerRef.current;
    const img = container?.querySelector("img");
    if (!img) return null;
    const rect = img.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(e.clientX - rect.left, rect.width)),
      y: Math.max(0, Math.min(e.clientY - rect.top, rect.height)),
    };
  }

  function handleCropMouseDown(e: React.MouseEvent) {
    const pos = getCropPos(e);
    if (!pos) return;
    setCropStart(pos);
    setCropRect(null);
    setCropDragging(true);
  }

  function handleCropMouseMove(e: React.MouseEvent) {
    const pos = getCropPos(e);
    if (pos) setCropMouse(pos);
    if (!cropDragging || !cropStart || !pos) return;
    const x = Math.min(cropStart.x, pos.x);
    const y = Math.min(cropStart.y, pos.y);
    const w = Math.abs(pos.x - cropStart.x);
    const h = Math.abs(pos.y - cropStart.y);
    setCropRect({ x, y, w, h });
  }

  function handleCropMouseUp() {
    setCropDragging(false);
  }

  function applyCrop() {
    if (!cropRect || cropRect.w < 5 || cropRect.h < 5 || !imageData) return;
    const container = cropContainerRef.current;
    const img = container?.querySelector("img") as HTMLImageElement | null;
    if (!img) return;

    const displayW = img.clientWidth;
    const displayH = img.clientHeight;
    if (displayW === 0 || displayH === 0) return;

    // Scale from display coordinates to actual pixel coordinates
    const scaleX = imageData.width / displayW;
    const scaleY = imageData.height / displayH;

    const sx = Math.round(cropRect.x * scaleX);
    const sy = Math.round(cropRect.y * scaleY);
    const sw = Math.round(cropRect.w * scaleX);
    const sh = Math.round(cropRect.h * scaleY);

    if (sw <= 0 || sh <= 0) return;

    // Extract crop from the existing imageData (pixel-level, no CORS issues)
    const srcPixels = imageData.data;
    const srcW = imageData.width;
    const newData = new ImageData(sw, sh);
    const dstPixels = newData.data;
    for (let row = 0; row < sh; row++) {
      for (let col = 0; col < sw; col++) {
        const srcIdx = ((sy + row) * srcW + (sx + col)) * 4;
        const dstIdx = (row * sw + col) * 4;
        dstPixels[dstIdx] = srcPixels[srcIdx];
        dstPixels[dstIdx + 1] = srcPixels[srcIdx + 1];
        dstPixels[dstIdx + 2] = srcPixels[srcIdx + 2];
        dstPixels[dstIdx + 3] = srcPixels[srcIdx + 3];
      }
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = sw;
    canvas.height = sh;
    setImageSize({ w: sw, h: sh });
    const ctx = canvas.getContext("2d")!;
    ctx.putImageData(newData, 0, 0);

    setImageData(new ImageData(new Uint8ClampedArray(newData.data), sw, sh));
    setOriginalImageData(new ImageData(new Uint8ClampedArray(newData.data), sw, sh));
    pushHistory(newData);
    setIsCropping(false);
    setCropImageUrl(null);
    setCropRect(null);
    setCropStart(null);
    setCropMouse(null);
  }

  function cancelCrop() {
    setIsCropping(false);
    setCropImageUrl(null);
    setCropRect(null);
    setCropStart(null);
    setCropMouse(null);
  }

  function getDataUrl(): string {
    const canvas = canvasRef.current;
    if (!canvas) return "";
    return canvas.toDataURL("image/png");
  }

  const displayScale = imageSize.w > 0 ? Math.min(1, 700 / imageSize.w, 500 / imageSize.h) : 1;

  if (!designUrl) return null;

  return (
    <div className="fixed inset-0 z-50 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-2xl w-[95vw] h-[92vh] max-w-[1400px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Palette className="h-5 w-5 text-indigo-600" />
            Tasarım Editörü
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onApplyToCanvas(getDataUrl())}
              disabled={!imageData}
              className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 transition-colors"
            >
              <Save className="h-4 w-4" />
              Kaydet
            </button>
            <button
              onClick={() => { onApplyToCanvas(getDataUrl()); onClose(); }}
              disabled={!imageData}
              className="flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 transition-colors"
            >
              <ArrowRight className="h-4 w-4" />
              Mockup'a Uygula
            </button>
            <button
              onClick={async () => {
                if (!canvasRef.current) return;
                const dataUrl = getDataUrl();
                try {
                  const apiBase = import.meta.env.VITE_API_URL || "";
                  const token = localStorage.getItem("erc_token") || "";

                  // Check if we already have a Canva design for this session
                  const existingDesign = sessionStorage.getItem("canva_last_design");
                  if (existingDesign) {
                    try {
                      const { editUrl, designId } = JSON.parse(existingDesign);
                      if (editUrl) {
                        window.open(editUrl, "_blank");
                        if (onCanvaDesign) onCanvaDesign(designId, editUrl);
                        return;
                      }
                    } catch { /* ignore parse error */ }
                  }

                  // Check Canva connection
                  const statusRes = await fetch(`${apiBase}/api/canva/status`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  const statusData = await statusRes.json();

                  if (statusData.data?.connected) {
                    // Connected — try creating design via API
                    const createRes = await fetch(`${apiBase}/api/canva/create-design`, {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                      },
                      body: JSON.stringify({ imageUrl: dataUrl, title: "Etsy Design" }),
                    });
                    const createData = await createRes.json();

                    if (createData.data?.editUrl) {
                      // Save design info so we can reopen without re-uploading
                      sessionStorage.setItem("canva_last_design", JSON.stringify({
                        designId: createData.data.designId,
                        editUrl: createData.data.editUrl,
                      }));
                      window.open(createData.data.editUrl, "_blank");
                      if (onCanvaDesign) {
                        onCanvaDesign(createData.data.designId || "", createData.data.editUrl);
                      }
                      return;
                    } else {
                      console.error("[Canva] create-design error:", createData);
                    }
                  }

                  // Fallback: download + open Canva manually
                  const a = document.createElement("a");
                  a.href = dataUrl;
                  a.download = "design.png";
                  a.click();
                  try {
                    const res = await fetch(dataUrl);
                    const blob = await res.blob();
                    await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
                  } catch { /* ignore */ }
                  alert("Canva API bağlantısı kurulamadı.\n\nGörsel indirildi. Canva'da manuel olarak yükleyebilirsiniz:\n1. 'Tasarım oluştur' > 'Özel boyut' (4500x5400)\n2. İndirilen dosyayı sürükleyin veya Ctrl+V yapın");
                  window.open("https://www.canva.com", "_blank");
                } catch (err) {
                  console.error("[Canva] error:", err);
                  const a = document.createElement("a");
                  a.href = dataUrl;
                  a.download = "design.png";
                  a.click();
                  alert("Görsel indirildi. Canva'da manuel olarak yükleyin.");
                  window.open("https://www.canva.com", "_blank");
                }
              }}
              disabled={!imageData}
              className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium bg-[#7B2FF2] text-white hover:bg-[#6B21D6] disabled:opacity-40 transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              Canva'da Aç
            </button>
            <button
              onClick={onClose}
              className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 transition-colors"
            >
              <X className="h-4 w-4" />
              Kapat
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left: Canvas Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-100 bg-white flex-shrink-0 flex-wrap">
              {/* Bg Remove dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowBgOptions(!showBgOptions)}
                  disabled={isRemovingBg || !imageData}
                  className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors disabled:opacity-40 border border-gray-200"
                >
                  {isRemovingBg ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eraser className="h-4 w-4" />}
                  {isRemovingBg ? "Kaldırılıyor..." : "Arka Plan Kaldır"}
                  <ChevronDown className="h-3 w-3" />
                </button>
                {showBgOptions && (
                  <div className="absolute top-full left-0 mt-1 z-50 w-72 rounded-xl border border-gray-200 bg-white shadow-xl p-4 space-y-3">
                    {/* Color-based removal */}
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-2">Renk Bazlı (Tasarımlar için)</p>
                      <p className="text-[10px] text-gray-400 mb-2">Belirli bir rengi şeffaf yapar. Tasarım detaylarını korur.</p>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] text-gray-500">Tolerans: {bgRemoveTolerance}</span>
                        <input type="range" min={10} max={120} value={bgRemoveTolerance} onChange={(e) => setBgRemoveTolerance(Number(e.target.value))} className="flex-1 h-1.5 accent-purple-600" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => handleRemoveColorBg("#ffffff")}
                          className="flex items-center justify-center gap-2 rounded-lg border-2 border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:border-purple-300 hover:bg-purple-50 transition-colors"
                        >
                          <div className="w-4 h-4 rounded border border-gray-300 bg-white" />
                          Beyaz Kaldır
                        </button>
                        <button
                          onClick={() => handleRemoveColorBg("#000000")}
                          className="flex items-center justify-center gap-2 rounded-lg border-2 border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:border-purple-300 hover:bg-purple-50 transition-colors"
                        >
                          <div className="w-4 h-4 rounded border border-gray-300 bg-black" />
                          Siyah Kaldır
                        </button>
                      </div>
                      <button
                        onClick={() => handleRemoveColorBg(sourceColor)}
                        className="w-full mt-2 flex items-center justify-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-purple-50 transition-colors"
                      >
                        <div className="w-4 h-4 rounded border border-gray-300" style={{ backgroundColor: sourceColor }} />
                        Seçili Rengi Kaldır ({sourceColor})
                      </button>
                    </div>
                    <div className="border-t border-gray-100 pt-3">
                      <p className="text-xs font-semibold text-gray-700 mb-1">AI ile Kaldır (Fotoğraflar için)</p>
                      <p className="text-[10px] text-gray-400 mb-2">Yapay zeka ile arka planı algılayıp kaldırır. Fotoğraflarda iyi çalışır, grafik tasarımlarda detay kaybedebilir.</p>
                      <button
                        onClick={handleRemoveBgAI}
                        disabled={isRemovingBg}
                        className="w-full flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-3 py-2 text-xs font-medium text-white hover:bg-purple-700 disabled:opacity-50 transition-colors"
                      >
                        {isRemovingBg ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eraser className="h-3.5 w-3.5" />}
                        AI Arka Plan Kaldır
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {/* Crop */}
              <button
                onClick={isCropping ? cancelCrop : startCrop}
                disabled={!imageData}
                className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors border ${
                  isCropping ? "bg-indigo-50 text-indigo-700 border-indigo-300" : "text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 border-gray-200"
                } disabled:opacity-40`}
              >
                <CropIcon className="h-4 w-4" />
                {isCropping ? "Kırpmayı İptal Et" : "Kırp"}
              </button>
              {isCropping && cropRect && cropRect.w > 5 && cropRect.h > 5 && (
                <button
                  onClick={applyCrop}
                  className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                >
                  Kırpmayı Uygula
                </button>
              )}

              <div className="w-px h-6 bg-gray-200 mx-1" />

              {/* Undo/Redo */}
              <button onClick={undo} disabled={historyIndex <= 0} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors" title="Geri Al">
                <Undo2 className="h-4 w-4 text-gray-600" />
              </button>
              <button onClick={redo} disabled={historyIndex >= history.length - 1} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors" title="İleri Al">
                <Redo2 className="h-4 w-4 text-gray-600" />
              </button>

              <div className="w-px h-6 bg-gray-200 mx-1" />

              {/* Flip */}
              <button onClick={flipH} disabled={!imageData} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors" title="Yatay Çevir">
                <FlipHorizontal className="h-4 w-4 text-gray-600" />
              </button>
              <button onClick={flipV} disabled={!imageData} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors" title="Dikey Çevir">
                <FlipVertical className="h-4 w-4 text-gray-600" />
              </button>
              <button onClick={invertColors} disabled={!imageData} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors" title="Renkleri Ters Çevir">
                <span className="text-xs font-bold text-gray-500">INV</span>
              </button>

              <div className="w-px h-6 bg-gray-200 mx-1" />

              {/* Reset */}
              <button onClick={resetToOriginal} disabled={!originalImageData} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition-colors" title="Orijinale Dön">
                <RotateCcw className="h-4 w-4" />
                Sıfırla
              </button>
            </div>

            {/* Loading banner */}
            {isRemovingBg && (
              <div className="flex items-center gap-3 px-6 py-3 bg-purple-50 border-b border-purple-200 flex-shrink-0">
                <Loader2 className="h-5 w-5 animate-spin text-purple-500" />
                <p className="text-sm text-purple-700 font-medium">Arka plan kaldırılıyor... Bu işlem birkaç dakika sürebilir.</p>
              </div>
            )}

            {/* Canvas Area */}
            <div className="flex-1 flex items-center justify-center overflow-auto p-6">
              {/* Crop overlay */}
              {isCropping && cropImageUrl && (
                <div
                  ref={cropContainerRef}
                  className="relative select-none"
                  style={{ cursor: "crosshair" }}
                  onMouseDown={handleCropMouseDown}
                  onMouseMove={handleCropMouseMove}
                  onMouseUp={handleCropMouseUp}
                  onMouseLeave={handleCropMouseUp}
                >
                  <img
                    src={cropImageUrl}
                    alt="Crop"
                    draggable={false}
                    style={{ maxWidth: "100%", maxHeight: "calc(92vh - 200px)", display: "block" }}
                  />
                  {/* Dark overlay outside crop area */}
                  {cropRect && (
                    <>
                      <div className="absolute inset-0 bg-black/50 pointer-events-none" style={{
                        clipPath: `polygon(0% 0%, 0% 100%, ${cropRect.x}px 100%, ${cropRect.x}px ${cropRect.y}px, ${cropRect.x + cropRect.w}px ${cropRect.y}px, ${cropRect.x + cropRect.w}px ${cropRect.y + cropRect.h}px, ${cropRect.x}px ${cropRect.y + cropRect.h}px, ${cropRect.x}px 100%, 100% 100%, 100% 0%)`,
                      }} />
                      {/* Crop border */}
                      <div className="absolute pointer-events-none border-2 border-white" style={{
                        left: cropRect.x, top: cropRect.y, width: cropRect.w, height: cropRect.h,
                        boxShadow: "0 0 0 1px rgba(0,0,0,0.3), inset 0 0 0 1px rgba(0,0,0,0.3)",
                      }}>
                        {/* Dashed grid lines (rule of thirds) */}
                        <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/40" />
                        <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/40" />
                        <div className="absolute top-1/3 left-0 right-0 h-px bg-white/40" />
                        <div className="absolute top-2/3 left-0 right-0 h-px bg-white/40" />
                        {/* Size indicator */}
                        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded whitespace-nowrap">
                          {Math.round(cropRect.w)} x {Math.round(cropRect.h)}
                        </div>
                      </div>
                    </>
                  )}
                  {/* Crosshair guides following mouse */}
                  {cropMouse && !cropRect && (
                    <>
                      <div className="absolute pointer-events-none" style={{ left: 0, right: 0, top: cropMouse.y, height: 1, background: "rgba(99,102,241,0.6)" }} />
                      <div className="absolute pointer-events-none" style={{ top: 0, bottom: 0, left: cropMouse.x, width: 1, background: "rgba(99,102,241,0.6)" }} />
                    </>
                  )}
                  {/* Crosshair guides while dragging */}
                  {cropMouse && cropDragging && (
                    <>
                      <div className="absolute pointer-events-none" style={{ left: 0, right: 0, top: cropMouse.y, height: 1, background: "rgba(99,102,241,0.4)", borderTop: "1px dashed rgba(99,102,241,0.8)" }} />
                      <div className="absolute pointer-events-none" style={{ top: 0, bottom: 0, left: cropMouse.x, width: 1, background: "rgba(99,102,241,0.4)", borderLeft: "1px dashed rgba(99,102,241,0.8)" }} />
                    </>
                  )}
                </div>
              )}
              {/* Canvas - always in DOM, hidden during crop */}
              <div
                className={`flex justify-center items-center rounded-xl p-4 ${pickingColor ? "cursor-crosshair ring-4 ring-indigo-400" : ""}`}
                style={{
                  background: "repeating-conic-gradient(#e5e7eb 0% 25%, transparent 0% 50%) 50% / 20px 20px",
                  display: isCropping ? "none" : undefined,
                }}
              >
                <canvas
                  ref={canvasRef}
                  onClick={handleCanvasClick}
                  style={{
                    width: imageSize.w * displayScale,
                      height: imageSize.h * displayScale,
                      imageRendering: "auto",
                      maxWidth: "100%",
                      maxHeight: "calc(92vh - 200px)",
                    }}
                    className="rounded-lg shadow-lg"
                  />
              </div>
            </div>
          </div>

          {/* Right: Sidebar Tools */}
          <div className="w-80 border-l border-gray-200 bg-gray-50 overflow-y-auto flex-shrink-0">
            <div className="p-5 space-y-5">
              {/* Color Swap Section */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Renk Değiştir</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">Kaynak Renk</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={sourceColor} onChange={(e) => setSourceColor(e.target.value)} className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer" />
                      <input type="text" value={sourceColor} onChange={(e) => setSourceColor(e.target.value)} className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 font-mono uppercase" />
                      <button
                        onClick={() => setPickingColor(!pickingColor)}
                        className={`p-2.5 rounded-lg border transition-colors ${pickingColor ? "bg-indigo-100 border-indigo-400 text-indigo-700" : "border-gray-200 hover:bg-gray-100"}`}
                        title="Görsel üzerinden renk seç"
                      >
                        <Pipette className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">Hedef Renk</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={targetColor} onChange={(e) => setTargetColor(e.target.value)} className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer" />
                      <input type="text" value={targetColor} onChange={(e) => setTargetColor(e.target.value)} className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 font-mono uppercase" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">Tolerans: {tolerance}</label>
                    <input type="range" min={5} max={150} value={tolerance} onChange={(e) => setTolerance(Number(e.target.value))} className="w-full h-2 accent-indigo-600 rounded-lg" />
                  </div>
                  {/* Quick presets */}
                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={() => { setSourceColor("#000000"); setTargetColor("#ffffff"); }} disabled={!imageData}
                      className="flex items-center justify-center gap-1 rounded-lg bg-gray-800 px-2 py-2 text-xs font-medium text-white hover:bg-gray-700 disabled:opacity-40 transition-colors">
                      <Moon className="h-3 w-3" /><ArrowRight className="h-3 w-3" /><Sun className="h-3 w-3" />
                    </button>
                    <button onClick={() => { setSourceColor("#ffffff"); setTargetColor("#000000"); }} disabled={!imageData}
                      className="flex items-center justify-center gap-1 rounded-lg bg-white border border-gray-200 px-2 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 transition-colors">
                      <Sun className="h-3 w-3" /><ArrowRight className="h-3 w-3" /><Moon className="h-3 w-3" />
                    </button>
                    <button onClick={applyColorSwap} disabled={!imageData}
                      className="rounded-lg bg-indigo-600 px-2 py-2 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-40 transition-colors">
                      Uygula
                    </button>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200" />

              {/* Save Versions */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Mockup'lara Uygula</h3>

                {/* Mockup atama secenekleri */}
                <div className="space-y-2">
                  {/* Tum mockup'lara uygula — tek versiyon */}
                  <button
                    onClick={() => { onApplyToCanvas(getDataUrl()); onClearLight(); onClearDark(); }}
                    disabled={!imageData}
                    className="w-full flex items-center gap-3 rounded-xl bg-white border-2 border-gray-200 px-3 py-2.5 text-left hover:border-indigo-300 hover:bg-indigo-50/50 disabled:opacity-40 transition-colors"
                  >
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Save className="h-4 w-4 text-indigo-500" />
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-700">Tüm Mockup'lara Uygula</span>
                      <p className="text-[10px] text-gray-400">Tema ayrımı yapmadan tüm mockup'lara ata</p>
                    </div>
                  </button>

                  {/* Acik tema mockup'larina ata */}
                  <button
                    onClick={() => onSaveLight(getDataUrl())}
                    disabled={!imageData}
                    className="w-full flex items-center gap-3 rounded-xl bg-white border-2 border-gray-200 px-3 py-2.5 text-left hover:border-amber-300 hover:bg-amber-50/50 disabled:opacity-40 transition-colors"
                  >
                    <Sun className="h-4 w-4 text-amber-500 flex-shrink-0" />
                    <div>
                      <span className="text-xs font-medium text-gray-700">Açık Mockup'lara Ata</span>
                      <p className="text-[10px] text-gray-400">Beyaz/açık renkli mockup'larda kullan</p>
                    </div>
                  </button>

                  {/* Koyu tema mockup'larina ata */}
                  <button
                    onClick={() => onSaveDark(getDataUrl())}
                    disabled={!imageData}
                    className="w-full flex items-center gap-3 rounded-xl bg-gray-800 border-2 border-gray-600 px-3 py-2.5 text-left hover:border-blue-400 hover:bg-gray-700 disabled:opacity-40 transition-colors"
                  >
                    <Moon className="h-4 w-4 text-blue-300 flex-shrink-0" />
                    <div>
                      <span className="text-xs font-medium text-gray-200">Koyu Mockup'lara Ata</span>
                      <p className="text-[10px] text-gray-500">Siyah/koyu renkli mockup'larda kullan</p>
                    </div>
                  </button>
                </div>

                {/* Saved versions preview */}
                {(lightDesignUrl || darkDesignUrl) && (
                  <div className="mt-3 space-y-2">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Atanan Tema Versiyonları</p>
                    <div className="grid grid-cols-2 gap-2">
                      {/* Acik versiyon */}
                      <div className="relative rounded-xl bg-white border border-gray-200 p-2 flex flex-col items-center justify-center group" style={{ minHeight: 70 }}>
                        {lightDesignUrl ? (
                          <>
                            <img src={lightDesignUrl} alt="Light" className="max-h-14 max-w-full object-contain rounded" />
                            <span className="text-[9px] text-amber-600 font-medium mt-1 flex items-center gap-0.5"><Sun className="h-2.5 w-2.5" /> Açık</span>
                            {/* Islemler */}
                            <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => { if (lightDesignUrl) loadImageFromUrl(lightDesignUrl); }}
                                className="p-1 rounded bg-white/90 shadow-sm border border-gray-200 hover:bg-indigo-50 text-gray-500 hover:text-indigo-600"
                                title="Editöre yükle"
                              >
                                <Undo2 className="h-3 w-3" />
                              </button>
                              <button
                                onClick={onClearLight}
                                className="p-1 rounded bg-white/90 shadow-sm border border-gray-200 hover:bg-red-50 text-gray-500 hover:text-red-500"
                                title="Sil"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          </>
                        ) : (
                          <span className="text-[10px] text-gray-300">Açık yok</span>
                        )}
                      </div>

                      {/* Koyu versiyon */}
                      <div className="relative rounded-xl bg-gray-800 border border-gray-600 p-2 flex flex-col items-center justify-center group" style={{ minHeight: 70 }}>
                        {darkDesignUrl ? (
                          <>
                            <img src={darkDesignUrl} alt="Dark" className="max-h-14 max-w-full object-contain rounded" />
                            <span className="text-[9px] text-blue-300 font-medium mt-1 flex items-center gap-0.5"><Moon className="h-2.5 w-2.5" /> Koyu</span>
                            {/* Islemler */}
                            <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => { if (darkDesignUrl) loadImageFromUrl(darkDesignUrl); }}
                                className="p-1 rounded bg-gray-700/90 shadow-sm border border-gray-600 hover:bg-indigo-900 text-gray-400 hover:text-indigo-300"
                                title="Editöre yükle"
                              >
                                <Undo2 className="h-3 w-3" />
                              </button>
                              <button
                                onClick={onClearDark}
                                className="p-1 rounded bg-gray-700/90 shadow-sm border border-gray-600 hover:bg-red-900 text-gray-400 hover:text-red-400"
                                title="Sil"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          </>
                        ) : (
                          <span className="text-[10px] text-gray-500">Koyu yok</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ======== Thumbnail ========

// Lightweight CSS thumbnail (no Konva) — one Konva Stage per thumbnail used to
// make the studio grid very heavy. We render the mockup as a plain <img> with
// the design overlaid in its design area; exact compositing still happens
// off-screen with Konva only at export time.
const MockupThumbnail = memo(function MockupThumbnail({ template, designUrl, areaUrls, selected, onToggle, onActivate, isActive, onConfigure, onDelete, onAddToImages, added }: {
  template: UnifiedTemplate; designUrl: string | null;
  /** Alan basina cozulmus tasarim — cok alanli mockupta her alan farkli olabilir */
  areaUrls?: (string | null)[];
  selected: boolean; onToggle: () => void; onActivate: () => void; isActive: boolean; onConfigure?: () => void; onDelete?: () => void;
  /** Bu mockup'i TEK BASINA urun gorsellerine ekle (grubun tamamini beklemeden) */
  onAddToImages?: () => void;
  /** Zaten urun gorsellerinde mi */
  added?: boolean;
}) {
  const scale = 110 / template.canvasWidth;
  const h = template.canvasHeight * scale;
  // Use the designAreas[] array (single source of truth, matches Mockup Yonetimi);
  // fall back to the legacy single designArea only when there is no array.
  const areas = (template.designAreas && template.designAreas.length > 0)
    ? template.designAreas
    : (template.designArea.width > 0 ? [{ id: "legacy", x: template.designArea.x, y: template.designArea.y, width: template.designArea.width, height: template.designArea.height, rotation: template.designArea.rotation }] : []);
  return (
    <div className="relative group">
      <button onClick={onActivate}
        className={`relative block rounded-lg border-2 overflow-hidden transition-all ${isActive ? "border-indigo-500 ring-2 ring-indigo-200" : selected ? "border-emerald-400" : "border-gray-200 hover:border-gray-300"}`}
        style={{ width: 110, height: h, backgroundColor: template.isCustom ? "#f3f4f6" : template.bgColor }}>
        {template.isCustom && (template.thumbnailUrl || template.imageUrl) && (
          <img src={template.thumbnailUrl || template.imageUrl} alt={template.label} loading="lazy" decoding="async" crossOrigin="anonymous" className="absolute inset-0 w-full h-full object-cover" />
        )}
        {areas.map((ar, i) => {
          const u = areaUrls?.[i] ?? designUrl;
          if (u && ar.width > 0 && ar.height > 0 && hasWarp(ar)) {
            return <WarpedAreaImage key={ar.id} src={u} imageW={template.canvasWidth} imageH={template.canvasHeight}
              area={{ x: ar.x, y: ar.y, w: ar.width, h: ar.height, rotation: ar.rotation, shape: (ar as any).shape, fit: (ar as any).fit, warp: (ar as any).warp }} />;
          }
          return ar.width > 0 && ar.height > 0 && u ? (
            <img key={ar.id} src={u} alt="" crossOrigin="anonymous" loading="lazy" decoding="async"
              style={{ position: "absolute", left: ar.x * scale, top: ar.y * scale, width: ar.width * scale, height: ar.height * scale, objectFit: "contain", objectPosition: !isShaped(ar as { shape?: AreaShape }) && ar.height >= ar.width * 0.95 ? "center top" : "center", transform: ar.rotation ? `rotate(${ar.rotation}deg)` : undefined, transformOrigin: "top left" }} />
          ) : null;
        })}
        {/* On plan katmani — tasarimin ustunde (sac, kumas kivrimi) */}
        {template.foregroundUrl && (
          <img src={template.foregroundUrl} alt="" crossOrigin="anonymous" loading="lazy" decoding="async"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
        )}
      </button>
      <button onClick={onToggle} className="absolute top-1 right-1 z-10">
        {selected ? <CheckSquare className="h-4 w-4 text-emerald-500 bg-white rounded shadow-sm" /> : <Square className="h-4 w-4 text-gray-300 bg-white rounded shadow-sm" />}
      </button>
      {template.isCustom && (
        <div className="absolute top-1 left-1 z-10 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {onConfigure && <button onClick={onConfigure} className="p-0.5 bg-white rounded shadow-sm hover:bg-indigo-50"><Settings2 className="h-3 w-3 text-indigo-500" /></button>}
          {onDelete && <button onClick={onDelete} className="p-0.5 bg-white rounded shadow-sm hover:bg-red-50"><Trash2 className="h-3 w-3 text-red-500" /></button>}
        </div>
      )}
      {/* Tek tek ekleme: gruptan yalnizca birkac mockup istendiginde tumunu
          uretip fazlasini silmek gerekmesin. */}
      {onAddToImages && (
        <button
          onClick={onAddToImages}
          title={added ? "Ürün görsellerinde — tekrar ekle" : "Ürün görsellerine ekle"}
          className={`absolute bottom-1 right-1 z-10 rounded p-0.5 shadow-sm transition-opacity ${
            added
              ? "bg-emerald-500 text-white opacity-100"
              : "bg-white text-indigo-600 opacity-0 group-hover:opacity-100 hover:bg-indigo-50"
          }`}
        >
          {added ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
        </button>
      )}
      <p className="text-[9px] text-gray-500 text-center mt-1 truncate" style={{ width: 110 }}>{template.label}</p>
    </div>
  );
}, (prev, next) =>
  prev.template.id === next.template.id &&
  prev.template.imageUrl === next.template.imageUrl &&
  prev.template.thumbnailUrl === next.template.thumbnailUrl &&
  prev.template.foregroundUrl === next.template.foregroundUrl &&
  prev.template.label === next.template.label &&
  prev.designUrl === next.designUrl &&
  (prev.areaUrls ?? []).join("|") === (next.areaUrls ?? []).join("|") &&
  prev.selected === next.selected &&
  prev.isActive === next.isActive &&
  prev.added === next.added &&
  // re-render when the saved area positions change (e.g. after editing)
  JSON.stringify(prev.template.designAreas) === JSON.stringify(next.template.designAreas)
);

// Hizli mockup secici thumbnail — SADECE ham mockup gorseli (tasarim bindirmesi YOK),
// boylece 100+ mockup aninda acilir (agir 2500x2500 kompozit yapilmaz).
const MockupPickThumb = memo(function MockupPickThumb({ template, selected, onToggle }: {
  template: UnifiedTemplate; selected: boolean; onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      title={template.label}
      className={`relative block overflow-hidden rounded-lg border-2 transition-colors ${selected ? "border-emerald-400 ring-2 ring-emerald-200" : "border-gray-200 hover:border-indigo-300"}`}
      style={{ contentVisibility: "auto", containIntrinsicSize: "120px" } as React.CSSProperties}
    >
      <div className="aspect-square w-full bg-gray-100">
        {(template.thumbnailUrl || template.imageUrl) ? (
          <img src={template.thumbnailUrl || template.imageUrl} alt={template.label} loading="lazy" decoding="async" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-gray-300"><ImagePlus className="h-5 w-5" /></div>
        )}
      </div>
      <span className="absolute right-1 top-1">
        {selected ? <CheckSquare className="h-4 w-4 rounded bg-white text-emerald-500 shadow-sm" /> : <Square className="h-4 w-4 rounded bg-white/80 text-gray-300 shadow-sm" />}
      </span>
      <p className="truncate px-1 py-0.5 text-center text-[9px] text-gray-500">{template.label}</p>
    </button>
  );
}, (p, n) => p.template.id === n.template.id && p.template.imageUrl === n.template.imageUrl && p.template.label === n.template.label && p.selected === n.selected);

// Buyuk, hizli acilan mockup secim modali — kademeli render (ilk 24, scroll'da +24)
// + tedarikci katalogundan marka/renk atama ve filtreleme.
const PICK_BATCH = 24;
type SupplierVars = ReturnType<typeof useSupplierVariations>;
function MockupPickerModal({ templates, selected, onToggle, onClose, vars, onAssign, folders }: {
  templates: UnifiedTemplate[]; selected: Set<string>; onToggle: (id: string) => void; onClose: () => void;
  vars: SupplierVars; onAssign: (mockupId: string, patch: { brand?: string | null; model?: string | null; color?: string | null }) => void;
  /** Mockup Yonetimi klasorleri — secim klasor klasor yapilabilsin */
  folders: MockupFolder[];
}) {
  const { brands, modelsForBrand, colorsFor, hasCatalog } = vars;
  const [q, setQ] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  const [colorFilter, setColorFilter] = useState("");
  // Klasor suzgeci: "" tumu, "__root__" klasorsuzler
  const [folderFilter, setFolderFilter] = useState("");

  // Klasor agaci girintili liste olarak (Mockup Yonetimi ile ayni gorunum)
  const folderOptions = useMemo(() => {
    const byParent: Record<string, MockupFolder[]> = {};
    folders.forEach((f) => { const k = f.parentId ?? "root"; (byParent[k] ||= []).push(f); });
    const out: { id: string; label: string }[] = [];
    const walk = (parent: string, depth: number) => {
      (byParent[parent] || []).forEach((f) => {
        out.push({ id: f.id, label: `${"— ".repeat(depth)}${f.name}` });
        walk(f.id, depth + 1);
      });
    };
    walk("root", 0);
    return out;
  }, [folders]);

  // Alt klasorler de secime dahil: ust klasor secilince altindakiler gelir
  const folderMatch = useMemo(() => {
    if (!folderFilter || folderFilter === "__root__") return null;
    const ids = new Set<string>([folderFilter]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const f of folders) {
        if (f.parentId && ids.has(f.parentId) && !ids.has(f.id)) { ids.add(f.id); grew = true; }
      }
    }
    return ids;
  }, [folderFilter, folders]);
  const [limit, setLimit] = useState(PICK_BATCH);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return templates.filter((t) =>
      (!s || t.label.toLowerCase().includes(s)) &&
      (!brandFilter || t.brand === brandFilter) &&
      (!modelFilter || t.model === modelFilter) &&
      (!colorFilter || t.color === colorFilter) &&
      (folderFilter === "" ||
        (folderFilter === "__root__" ? !t.folderId : !!t.folderId && !!folderMatch?.has(t.folderId)))
    );
  }, [templates, q, brandFilter, modelFilter, colorFilter, folderFilter, folderMatch]);
  const selCount = useMemo(() => templates.reduce((n, t) => n + (selected.has(t.id) ? 1 : 0), 0), [templates, selected]);
  const shown = filtered.slice(0, limit);

  // Arama/filtre degisince bastan basla
  useEffect(() => { setLimit(PICK_BATCH); }, [q, brandFilter, modelFilter, colorFilter, folderFilter]);

  // Alttaki sentinel gorunume girince +batch yukle (sonsuz kaydirma)
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) setLimit((l) => Math.min(filtered.length, l + PICK_BATCH));
    }, { rootMargin: "150px" });
    io.observe(el);
    return () => io.disconnect();
  }, [filtered.length, shown.length]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex h-[88vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-wrap items-center gap-2 border-b p-4">
          <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900"><ImagePlus className="h-4 w-4 text-indigo-600" /> Mockup Ekle</h3>
          <span className="text-xs text-gray-400">{selCount} seçili · {filtered.length}</span>
          {folders.length > 0 && (
            <select value={folderFilter} onChange={(e) => setFolderFilter(e.target.value)}
              title="Klasöre göre seç (alt klasörler dahil)"
              className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200">
              <option value="">Tüm klasörler</option>
              <option value="__root__">Klasörsüz</option>
              {folderOptions.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          )}
          {hasCatalog && (
            <>
              <select value={brandFilter} onChange={(e) => { setBrandFilter(e.target.value); setModelFilter(""); setColorFilter(""); }}
                className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200">
                <option value="">Tüm markalar</option>
                {brands.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
              <select value={modelFilter} onChange={(e) => { setModelFilter(e.target.value); setColorFilter(""); }}
                className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200">
                <option value="">Tüm modeller</option>
                {modelsForBrand(brandFilter).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <select value={colorFilter} onChange={(e) => setColorFilter(e.target.value)}
                className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200">
                <option value="">Tüm renkler</option>
                {colorsFor(brandFilter, modelFilter).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {(brandFilter || modelFilter || colorFilter) && (
                <button onClick={() => { setBrandFilter(""); setModelFilter(""); setColorFilter(""); }} className="text-[11px] text-indigo-600 hover:text-indigo-700">Temizle</button>
              )}
            </>
          )}
          <div className="relative ml-auto w-48">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara…"
              className="w-full rounded-lg border border-gray-200 py-2 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          </div>
          {/* Klasor secildiginde tek tek tiklamak zaman aliyor: gorunen tumu */}
          {filtered.length > 0 && (
            <button
              onClick={() => {
                const allOn = filtered.every((t) => selected.has(t.id));
                for (const t of filtered) {
                  if (allOn ? selected.has(t.id) : !selected.has(t.id)) onToggle(t.id);
                }
              }}
              className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              {filtered.every((t) => selected.has(t.id)) ? "Seçimi bırak" : `Tümünü seç (${filtered.length})`}
            </button>
          )}
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {filtered.length === 0 ? (
            <p className="py-16 text-center text-sm text-gray-400">Eşleşen mockup yok.</p>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8">
                {shown.map((t) => (
                  <div key={t.id} className="flex flex-col gap-1">
                    <MockupPickThumb template={t} selected={selected.has(t.id)} onToggle={() => onToggle(t.id)} />
                    {hasCatalog && t.customMockupId && (
                      <div className="flex flex-col gap-0.5">
                        <select value={t.brand || ""} title="Marka ata"
                          onChange={(e) => onAssign(t.customMockupId!, { brand: e.target.value || null, model: null, color: null })}
                          className="w-full rounded border border-gray-200 px-1 py-0.5 text-[9px] text-gray-600">
                          <option value="">Marka</option>
                          {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                        <select value={t.model || ""} title="Model ata"
                          onChange={(e) => onAssign(t.customMockupId!, { model: e.target.value || null, color: null })}
                          className="w-full rounded border border-gray-200 px-1 py-0.5 text-[9px] text-gray-600">
                          <option value="">Model</option>
                          {modelsForBrand(t.brand || "").map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <select value={t.color || ""} title="Renk ata"
                          onChange={(e) => onAssign(t.customMockupId!, { color: e.target.value || null })}
                          className="w-full rounded border border-gray-200 px-1 py-0.5 text-[9px] text-gray-600">
                          <option value="">Renk</option>
                          {colorsFor(t.brand || "", t.model || "").map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {shown.length < filtered.length && (
                <div ref={sentinelRef} className="flex items-center justify-center py-4 text-xs text-gray-400">
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Yükleniyor… ({shown.length}/{filtered.length})
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between border-t p-3">
          <span className="text-xs text-gray-500">{hasCatalog ? "Marka/model/renk ata (elle) · üstten filtrele · seçtiklerin sağdaki gride eklenir." : "Seçtiklerin sağdaki gride eklenir. (Marka/model/renk için tedarikçi kataloğu gerekli.)"}</span>
          <button onClick={onClose} className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-medium text-white hover:bg-indigo-700">Tamam ({selCount})</button>
        </div>
      </div>
    </div>
  );
}

/** Ilham alinan liste — Studio'da calisirken kaynak goz onunde dursun.
 *
 *  Kucuk kart: gorsel + baslik + fiyat. Gorselin uzerine gelince buyur,
 *  cunku kucuk karede tasarimin ayrintisi secilmiyor; karsilastirma icin
 *  sayfadan cikip Etsy'yi acmak gerekiyordu. */
function SourceListingCard({ source }: {
  source: { url: string; title: string | null; price: string | null; imageUrls: string[] };
}) {
  const img = source.imageUrls?.[0];
  if (!img && !source.title) return null;
  return (
    <div className="group/src relative flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/50 p-2">
      {img && (
        <div className="relative shrink-0">
          <img src={img} alt="" className="h-14 w-14 rounded object-cover" />
          {/* Buyutulmus onizleme — karta degil GORSELE gelince acilir.
              Kutuya ACIK genislik sart: mutlak konumlu kutu icerige gore
              buzulurken img'in max-width:100%'i onu 56px'lik kucuk resme
              kilitliyor, onizleme dar bir serit olarak aciliyordu. */}
          <div
            style={{ width: 460 }}
            className="pointer-events-none absolute left-0 top-full z-40 mt-1 hidden overflow-hidden rounded-lg border border-gray-200 bg-white p-1 shadow-2xl group-hover/src:block"
          >
            <img src={img} alt="" className="h-auto w-full rounded object-contain" />
          </div>
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">İlham alınan liste</p>
        <p className="truncate text-[11px] text-gray-800" title={source.title ?? undefined}>
          {source.title || "Başlık yok"}
        </p>
        <div className="flex items-center gap-2">
          {source.price && <span className="text-[10px] text-gray-500">{source.price}</span>}
          <a href={source.url} target="_blank" rel="noreferrer"
            className="text-[10px] font-medium text-indigo-600 hover:underline">
            Etsy'de aç ↗
          </a>
        </div>
      </div>
    </div>
  );
}

// ======== Main Page ========



export default function MockupStudioPage() {
  const { mockupKind, isConceptVertical } = useShopVertical();
  const [searchParams] = useSearchParams();
  const designFromUrl = searchParams.get("designUrl");
  const lightUrlFromUrl = searchParams.get("lightUrl");
  const darkUrlFromUrl = searchParams.get("darkUrl");
  const draftIdFromUrl = searchParams.get("draftId");
  const designIdFromUrl = searchParams.get("designId");
  const designSkuFromUrl = searchParams.get("designSku");
  // Bu mockup'in kaynak tasarimi (listing olusturunca baglamak + SKU icin)
  const [sourceDesignId, setSourceDesignId] = useState<string | null>(designIdFromUrl);
  const [sourceDesignSku, setSourceDesignSku] = useState<string | null>(designSkuFromUrl);

  const [designUrl, setDesignUrl] = useState<string | null>(null);
  const [lightDesignUrl, setLightDesignUrl] = useState<string | null>(null);
  const [darkDesignUrl, setDarkDesignUrl] = useState<string | null>(null);
  const [designNaturalSize, setDesignNaturalSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [selectedTemplates, setSelectedTemplates] = useState<Set<string>>(new Set(["tshirt_white", "tshirt_black"]));
  const [activeTemplate, setActiveTemplate] = useState<string>("tshirt_white");
  const [design, setDesign] = useState<DesignState>({ x: 200, y: 200, width: 400, height: 400, rotation: 0 });
  const [isExporting, setIsExporting] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(true);
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mockupFileInputRef = useRef<HTMLInputElement>(null);
  const productImageInputRef = useRef<HTMLInputElement>(null);

  const [customMockups, setCustomMockups] = useState<CustomMockup[]>([]);
  const [collections, setCollections] = useState<MockupCollection[]>([]);
  // Mockup klasorleri — "Mockup Ekle" modalinda klasore gore secim icin
  const [mockupFolders, setMockupFolders] = useState<MockupFolder[]>([]);
  const [loadingCustom, setLoadingCustom] = useState(false);
  // Tedarikci katalogu: marka/model/renk (stok) kademeli — mockup varyasyon atamasi
  const supplierVars = useSupplierVariations();
  const [uploadingMockup, setUploadingMockup] = useState(false);
  const [configuringMockup, setConfiguringMockup] = useState<CustomMockup | null>(null);
  const [showCustomTab, setShowCustomTab] = useState(false);
  // Collection-focused studio: the active mockup template (collection) drives the grid.
  // Coklu grup: ayni tasarim birden fazla urun grubuna (tisort + kupa + susleme)
  // birlikte uygulanabilir. Sira Etsy yukleme sirasidir; her grup KENDI taslagini
  // uretir. Tek grup seciliyken davranis eskisiyle birebir aynidir.
  const [selectedCollectionIds, setSelectedCollectionIds] = useState<string[]>([]);
  // Aktif urun tipi profilleri (tshirt, mug, ornament...) — grup etiketlemek icin
  const [productTypeProfiles, setProductTypeProfiles] = useState<Array<{ key: string; label: string; vertical: string }>>([]);
  // Ilham listesinden cozulen uretim zinciri (kategori, sablon, grup, satan mockup)
  const [productionPlan, setProductionPlan] = useState<{
    productTypeKey: string | null;
    listingTemplateId: string | null;
    mockupCollectionIds: string[];
    sellingMockupId: string | null;
    sellingMockupUrl: string | null;
  } | null>(null);
  // Aktif magaza — mockup listesini magazanin dikeyine gore filtrelemek icin
  const shops = useShopStore((s) => s.shops);
  const activeShopId = useActiveShopId();
  const [showAddMockups, setShowAddMockups] = useState(false); // reveal full pool to add extras
  // Listing template chosen before transfer (applied in the builder).
  // Sablonun kendi gorselleri (size chart, renk kartelasi) aktarimda listeye
  // EKLENIR; Etsy 20 gorselde kestigi icin toplam onceden gorunmeli.
  const [listingTemplates, setListingTemplates] = useState<{ id: string; name: string; imageCount: number }[]>([]);
  const [selectedListingTemplateId, setSelectedListingTemplateId] = useState<string>("");
  // Grup degisince sablon yeniden secilmeli: her mockup grubunun kendi
  // sablonu (size chart, aciklama, kargo) oluyor; onceki grubun sablonuyla
  // aktarmak yanlis liste uretiyordu.
  const [templateNeedsPick, setTemplateNeedsPick] = useState(false);
  // Kaynak analizin kisisellestirme alanlari (duzenlenebilir; analize kaydedilir)
  const [persoFields, setPersoFields] = useState<PersonalizationField[]>([]);
  const [persoDirty, setPersoDirty] = useState(false);
  const [persoSaving, setPersoSaving] = useState(false);

  // DB'den kayıtlı tasarım seçici
  const [showDesignPicker, setShowDesignPicker] = useState(false);
  const [myDesigns, setMyDesigns] = useState<Array<{ id: string; sku: string | null; imageUrl: string | null; analysisId?: string | null }>>([]);
  const [loadingMyDesigns, setLoadingMyDesigns] = useState(false);
  const [pickerChatgpt, setPickerChatgpt] = useChatGptFilter();
  async function loadPickerDesigns(chatgpt: ChatGptFilter) {
    setLoadingMyDesigns(true);
    try {
      if (chatgpt === "only") {
        // ChatGPT tasarimlari panel tasarimlaridir (analiz uretimi degil)
        const res = await fetch(`${API_BASE}/api/designs?chatgpt=1&limit=100`, { headers: authHeaders() });
        const json = await res.json();
        const rows = (res.ok ? (json.data ?? []) : []) as Array<{ id: string; sku: string | null; editedUrl: string | null; originalUrl: string; metadata?: { analysisId?: string } | null }>;
        setMyDesigns(rows.map((d) => ({ id: d.id, sku: d.sku, imageUrl: d.editedUrl || d.originalUrl, analysisId: d.metadata?.analysisId ?? null })));
      } else {
        const res = await fetch(`${API_BASE}/api/analyses/designs/mine`, {
          headers: authHeaders(),
        });
        const json = await res.json();
        // Analiz uretimleri ChatGPT tasarimi degildir; "Gizle"de liste aynen kalir
        setMyDesigns(res.ok ? (json.data ?? []) : []);
      }
    } catch {
      setMyDesigns([]);
    } finally {
      setLoadingMyDesigns(false);
    }
  }
  async function openDesignPicker() {
    setShowDesignPicker(true);
    await loadPickerDesigns(pickerChatgpt);
  }
  function pickDbDesign(d: { id?: string; sku?: string | null; imageUrl: string | null; analysisId?: string | null }) {
    if (!d.imageUrl) return;
    const url = d.imageUrl.startsWith("http") ? d.imageUrl : `${API_BASE}${d.imageUrl}`;
    setDesignUrl(url);
    setLightDesignUrl(url);
    if (d.id) setSourceDesignId(d.id);
    if (d.sku !== undefined) setSourceDesignSku(d.sku ?? null);
    if (d.analysisId) prefillFromAnalysis(d.analysisId); // analize bağlıysa otomatik doldur
    setShowDesignPicker(false);
  }

  // Product images - collected mockup renders and lifestyle photos
  const [productImages, setProductImages] = useState<Array<{ id: string; url: string; label: string; templateId?: string; thumbUrl?: string; poolStart?: number }>>([]);
  // COKLU TASARIM HAVUZU (custom liste): mockuplara tek tasarim yerine dagitilir.
  // Analiz sayfasindan "Studio'da ac" ile gelir.
  type PoolDesign = { label: string; lightUrl: string | null; darkUrl: string | null };
  const [designPool, setDesignPool] = useState<PoolDesign[]>(() => {
    try {
      const id = new URLSearchParams(window.location.search).get("designId");
      const raw = id ? localStorage.getItem(`erc:studio-pool:${id}`) : null;
      return raw ? (JSON.parse(raw) as PoolDesign[]) : [];
    } catch { return []; }
  });
  const [poolMode, setPoolMode] = useState<"cycle" | "random" | "single">("cycle");
  const [poolSeed, setPoolSeed] = useState(1);
  const poolActive = designPool.length > 1 && poolMode !== "single";
  // Farkli tasarimla acilirsa havuzu o tasarimin kaydindan yenile
  useEffect(() => {
    try {
      const raw = designIdFromUrl ? localStorage.getItem(`erc:studio-pool:${designIdFromUrl}`) : null;
      setDesignPool(raw ? (JSON.parse(raw) as PoolDesign[]) : []);
    } catch { setDesignPool([]); }
  }, [designIdFromUrl]);
  // Analiz sayfasindan (custom liste) gelen hazir gorseller: kolaj(lar) ANA
  // gorsel olarak basa, kartela arkasina. Ayni tasarim icin bir kez eklenir.
  useEffect(() => {
    if (!designIdFromUrl) return;
    let raw: string | null = null;
    try { raw = localStorage.getItem("erc:studio-import"); } catch { /* yok */ }
    if (!raw) return;
    try {
      const imp = JSON.parse(raw) as { designId: string; images: Array<{ url: string; label: string }> };
      if (imp.designId !== designIdFromUrl) return;
      localStorage.removeItem("erc:studio-import");
      const abs = (u: string) => (u.startsWith("http") || u.startsWith("data:") ? u : `${env.API_URL}${u}`);
      setProductImages((prev) => {
        const have = new Set(prev.map((p) => p.url));
        const add = imp.images
          .map((im, i) => ({ id: `img_import_${Date.now()}_${i}`, url: abs(im.url), label: im.label, templateId: `import_${i}` }))
          .filter((im) => !have.has(im.url));
        return [...add, ...prev].slice(0, 20);
      });
    } catch { /* bozuk kayit */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designIdFromUrl]);
  // Listing Builder'a arka plan aktarim kuyrugu
  const [exportQueue, setExportQueue] = useState<Array<{ id: string; title: string; status: "uploading" | "done" | "error"; draftId?: string; error?: string; video?: boolean }>>([]);
  // Fullscreen lightbox over a list of images with prev/next navigation.
  const [lightbox, setLightbox] = useState<{ urls: string[]; index: number } | null>(null);
  const openLightbox = (urls: string[], index: number) => setLightbox({ urls, index });
  const lbStep = (d: number) => setLightbox((lb) => lb ? { ...lb, index: (lb.index + d + lb.urls.length) % lb.urls.length } : lb);
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowRight") lbStep(1);
      else if (e.key === "ArrowLeft") lbStep(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox]);
  // Drag-and-drop reorder for product images.
  const [dragImageIdx, setDragImageIdx] = useState<number | null>(null);

  // Studio draft save/load
  const STUDIO_DRAFT_KEY = "erc_studio_draft";

  interface StudioDraft {
    designUrl: string | null;
    lightDesignUrl: string | null;
    darkDesignUrl: string | null;
    selectedTemplates: string[];
    activeTemplate: string;
    design: DesignState;
    productImages: Array<{ id: string; url: string; label: string }>;
    videoUrl: string | null;
    savedAt: string;
    // Analiz + SEO + AI lifestyle + kaynak baglam (geri yuklemede tam durum donsun)
    lifestyleAnalysis?: { analysis: string; keywords: string[]; targetAudience: string; lifestyleScenes: string[] } | null;
    seoContent?: { title: string; description: string; tags: string[]; titleAlternatives?: string[] } | null;
    lifestyleResults?: Array<{ scene: string; imageUrl?: string; prompt?: string }>;
    selectedListingTemplateId?: string;
    sourceDesignId?: string | null;
    sourceDesignSku?: string | null;
    autoFilled?: boolean;
    productTypes?: string[];
    productColors?: string[];
    printAreas?: string[];
  }

  async function uploadIfDataUrl(url: string | null): Promise<string | null> {
    if (!url) return null;
    // Already a full URL or API path - keep as is
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api/")) return `${API_BASE}${url}`;
    // Server-relative paths (e.g. /generated/..., /uploads/...) - make full URL
    if (url.startsWith("/generated/") || url.startsWith("/uploads/")) return `${API_BASE}${url}`;
    if (!url.startsWith("data:")) return null;
    try {
      const blob = await (await fetch(url)).blob();
      const mime = blob.type || (url.match(/^data:([^;]+)/)?.[1]) || "image/png";
      const ext = mime === "image/jpeg" ? "jpg" : mime === "image/webp" ? "webp" : "png";
      const file = new File([blob], `design_${Date.now()}_${Math.random().toString(36).slice(2, 6)}.${ext}`, { type: mime });
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API_BASE}/api/assets/upload-placeholder`, {
        method: "POST",
        headers: authHeaders(),
        body: formData,
      });
      if (res.ok) {
        const json = await res.json();
        return json.data?.url || null;
      }
    } catch { /* ignore */ }
    return null;
  }

  // Studio durumunu tek bir taslak nesnesine toplar (data URL'leri sunucuya yukler)
  async function buildDraftPayload(): Promise<StudioDraft> {
    // Upload design data URLs to server before saving
    const [savedDesignUrl, savedLightUrl, savedDarkUrl] = await Promise.all([
      uploadIfDataUrl(designUrl),
      uploadIfDataUrl(lightDesignUrl),
      uploadIfDataUrl(darkDesignUrl),
    ]);

    // Upload product image data URLs
    const savedProductImages: Array<{ id: string; url: string; label: string }> = [];
    for (const img of productImages) {
      const url = await uploadIfDataUrl(img.url);
      if (url) savedProductImages.push({ ...img, url });
    }

    // AI lifestyle gorsellerini de kalici hale getir
    const savedLifestyleResults: Array<{ scene: string; imageUrl?: string; prompt?: string }> = [];
    for (const r of lifestyleResults) {
      if (!r?.imageUrl) continue;
      const url = await uploadIfDataUrl(r.imageUrl);
      savedLifestyleResults.push({ scene: r.scene, prompt: r.prompt, ...(url ? { imageUrl: url } : {}) });
    }

    return {
      designUrl: savedDesignUrl,
      lightDesignUrl: savedLightUrl,
      darkDesignUrl: savedDarkUrl,
      selectedTemplates: Array.from(selectedTemplates),
      activeTemplate,
      design,
      productImages: savedProductImages,
      videoUrl: videoUrl,
      savedAt: new Date().toISOString(),
      lifestyleAnalysis,
      seoContent,
      lifestyleResults: savedLifestyleResults,
      selectedListingTemplateId,
      sourceDesignId,
      sourceDesignSku,
      autoFilled,
      productTypes: Array.from(productTypes),
      productColors,
      printAreas: Array.from(printAreas),
    };
  }

  function loadStudioDraft(): StudioDraft | null {
    try {
      const raw = localStorage.getItem(STUDIO_DRAFT_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as StudioDraft;
    } catch { return null; }
  }

  function clearStudioDraft() {
    localStorage.removeItem(STUDIO_DRAFT_KEY);
  }

  function restoreDraft(draft: StudioDraft) {
    if (draft.designUrl) setDesignUrl(draft.designUrl);
    if (draft.lightDesignUrl) setLightDesignUrl(draft.lightDesignUrl);
    if (draft.darkDesignUrl) setDarkDesignUrl(draft.darkDesignUrl);
    setSelectedTemplates(new Set(draft.selectedTemplates));
    setActiveTemplate(draft.activeTemplate);
    setDesign(draft.design);
    if (draft.productImages?.length > 0) setProductImages(draft.productImages);
    if (draft.videoUrl) setVideoUrl(draft.videoUrl);
    // Analiz + SEO + AI lifestyle + kaynak baglam geri yukle
    if (draft.lifestyleAnalysis !== undefined) setLifestyleAnalysis(draft.lifestyleAnalysis);
    if (draft.seoContent !== undefined) setSeoContent(draft.seoContent);
    if (draft.lifestyleResults?.length) setLifestyleResults(draft.lifestyleResults);
    if (draft.selectedListingTemplateId !== undefined) setSelectedListingTemplateId(draft.selectedListingTemplateId);
    if (draft.sourceDesignId !== undefined) setSourceDesignId(draft.sourceDesignId);
    if (draft.sourceDesignSku !== undefined) setSourceDesignSku(draft.sourceDesignSku);
    if (draft.autoFilled !== undefined) setAutoFilled(!!draft.autoFilled);
    if (draft.productTypes?.length) setProductTypes(new Set(draft.productTypes));
    if (draft.productColors?.length) setProductColors(draft.productColors);
    if (draft.printAreas?.length) setPrintAreas(new Set(draft.printAreas));
  }

  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const [draftSavedMsg, setDraftSavedMsg] = useState<string | null>(null);

  // Check for saved draft on mount
  useEffect(() => {
    const draft = loadStudioDraft();
    if (draft && !designFromUrl) {
  
      setShowDraftBanner(true);
    }
  }, []);

  function handleRestoreDraft() {
    const draft = loadStudioDraft();
    if (draft) {
      restoreDraft(draft);
      setShowDraftBanner(false);
    }
  }

  function handleDismissDraft() {
    setShowDraftBanner(false);
    clearStudioDraft();
  }

  // --- Sunucu taslaklari (cihazlar arasi, coklu, isimli) ---
  interface ServerDraftMeta { id: string; name: string; thumbnailUrl: string | null; createdAt: string; updatedAt: string }
  const [showDraftManager, setShowDraftManager] = useState(false);
  const [serverDrafts, setServerDrafts] = useState<ServerDraftMeta[]>([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [draftNameInput, setDraftNameInput] = useState("");
  const [savingServerDraft, setSavingServerDraft] = useState(false);
  const [loadingDraftId, setLoadingDraftId] = useState<string | null>(null);

  const draftAuthHeaders = () => (authHeaders());

  const fetchServerDrafts = useCallback(async () => {
    setDraftsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/mockup-drafts`, { headers: draftAuthHeaders() });
      if (res.ok) {
        const json = await res.json();
        setServerDrafts(Array.isArray(json.data) ? json.data : []);
      }
    } catch { /* ignore */ }
    setDraftsLoading(false);
  }, []);

  function openDraftManager() {
    const suggested = `Taslak ${new Date().toLocaleDateString("tr-TR")} ${new Date().toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}`;
    setDraftNameInput(suggested);
    setShowDraftManager(true);
    void fetchServerDrafts();
  }

  async function handleSaveServerDraft() {
    if (savingServerDraft) return;
    setSavingServerDraft(true);
    setDraftSavedMsg("Kaydediliyor...");
    try {
      const data = await buildDraftPayload();
      const res = await fetch(`${API_BASE}/api/mockup-drafts`, {
        method: "POST",
        headers: draftAuthHeaders(),
        body: JSON.stringify({
          name: draftNameInput.trim() || "Adsiz taslak",
          data,
          thumbnailUrl: data.designUrl || data.lightDesignUrl || data.darkDesignUrl || null,
        }),
      });
      if (res.ok) {
        setDraftSavedMsg("Taslak kaydedildi");
        await fetchServerDrafts();
      } else {
        setDraftSavedMsg("Kaydetme hatası");
      }
    } catch {
      setDraftSavedMsg("Kaydetme hatası");
    }
    setSavingServerDraft(false);
    setTimeout(() => setDraftSavedMsg(null), 2000);
  }

  async function handleLoadServerDraft(id: string) {
    setLoadingDraftId(id);
    try {
      const res = await fetch(`${API_BASE}/api/mockup-drafts/${id}`, { headers: draftAuthHeaders() });
      if (res.ok) {
        const json = await res.json();
        const data = json.data?.data as StudioDraft | undefined;
        if (data) {
          restoreDraft(data);
          setShowDraftManager(false);
        }
      }
    } catch { /* ignore */ }
    setLoadingDraftId(null);
  }

  async function handleDeleteServerDraft(id: string) {
    try {
      const res = await fetch(`${API_BASE}/api/mockup-drafts/${id}`, { method: "DELETE", headers: draftAuthHeaders() });
      if (res.ok) setServerDrafts((prev) => prev.filter((d) => d.id !== id));
    } catch { /* ignore */ }
  }

  // Per-area design assignment for multi-area custom mockups.
  // key: `${customMockupId}__${areaId}` -> design data URL
  const [areaDesignMap, setAreaDesignMap] = useState<Record<string, string>>({});
  const multiAreaInputRef = useRef<HTMLInputElement>(null);
  const [multiAreaTarget, setMultiAreaTarget] = useState<{ mockupId: string; areaId: string } | null>(null);

  const builtinTemplates = useMemo(() => MOCKUP_TEMPLATES.map(builtinToUnified), []);
  // Aktif magazanin dikeyine ait urun tipleri (tisort magazasinda kupa/susleme
  // mockup'lari gorunmesin). Tipi ATANMAMIS mockup'lar her zaman gorunur —
  // aksi halde eski kayitlar aniden kaybolurdu.
  const shopTypeKeys = useMemo(() => {
    const shop = shops.find((x) => x.id === activeShopId);
    if (!shop) return null; // magaza yoksa filtreleme yapma
    const keys = productTypeProfiles.filter((p) => p.vertical === shop.vertical).map((p) => p.key);
    return keys.length > 0 ? new Set(keys) : null;
  }, [shops, activeShopId, productTypeProfiles]);

  const customTemplates = useMemo(
    () =>
      customMockups
        .filter((m) => m.designAreaW > 20 && m.designAreaH > 20)
        .filter((m) => !shopTypeKeys || !m.productTypeKey || shopTypeKeys.has(m.productTypeKey))
        .map(customToUnified),
    [customMockups, shopTypeKeys]
  );
  const allTemplates = useMemo(() => [...builtinTemplates, ...customTemplates], [builtinTemplates, customTemplates]);

  // Collection-focused grid: when a collection (mockup template) is active, show
  // only its mockups (in order) plus any extras the user added; hide the rest.
  // Secili gruplar (secim sirasinda). activeCollection = ilki — tek grup
  // varsayan mevcut kod yollari bozulmasin diye turetilmis olarak duruyor.
  const activeCollections = selectedCollectionIds
    .map((id) => collections.find((c) => c.id === id))
    .filter((c): c is MockupCollection => !!c);
  const activeCollection = activeCollections[0] || null;
  // Tum secili gruplarin mockup'lari, grup sirasiyla
  const collectionCustomIds = activeCollections.flatMap((c) => c.mockups.map((m) => `custom_${m.id}`));
  const gridTemplates: UnifiedTemplate[] = activeCollection
    ? [
        ...collectionCustomIds.map((id) => customTemplates.find((t) => t.id === id)).filter((t): t is UnifiedTemplate => !!t),
        ...customTemplates.filter((t) => selectedTemplates.has(t.id) && !collectionCustomIds.includes(t.id)),
      ]
    : customTemplates.filter((t) => selectedTemplates.has(t.id));
  // Selected ids in collection order (extras appended) → Etsy upload order.
  const orderedSelectedIds = (): string[] => {
    if (activeCollection) {
      const inCol = collectionCustomIds.filter((id) => selectedTemplates.has(id));
      const extras = Array.from(selectedTemplates).filter((id) => !collectionCustomIds.includes(id));
      return [...inCol, ...extras];
    }
    return Array.from(selectedTemplates);
  };

  // ── Multi-area custom mockups: assign a design per area (e.g. a sheet of
  // several shirts, each printed with a different design on the front) ──
  // Resolve the design for one area: a manually-assigned design wins; otherwise
  // fall back to the sample that matches the AREA's own theme (dark area -> dark
  // sample, light area -> light sample), then to the generic design. This is what
  // makes a multi-shirt sheet auto-fill each shirt with the right light/dark
  // design instead of putting one uniform design on all of them.
  const resolveAreaUrl = (t: UnifiedTemplate, area: { id: string; label?: string; theme?: "light" | "dark" }): string | null => {
    const manual = t.customMockupId ? areaDesignMap[`${t.customMockupId}__${area.id}`] : undefined;
    if (manual) return manual;
    // Slot anahtari MOCKUP'IN KENDI alan sayisina gore secilir; global
    // singleAreaMode'a bakilmaz. Koleksiyon eklendikten hemen sonra uretim
    // eski kapanisla calisabiliyor (setTimeout) ve o an secim henuz bos
    // oldugu icin singleAreaMode=true kaliyordu — bu durumda alan-2'nin
    // tasarimi bulunamayip Alan 1'inki iki alana birden basiliyordu.
    const theme = area.theme ?? "light";
    const labelKey = `${labelKeyOf(area.label)}|${theme}`;
    const multi = (t.designAreas?.length ?? 0) > 1;
    for (const k of multi ? [labelKey, theme] : [theme, labelKey]) {
      const u = areaKeyDesigns[k];
      if (u) return u;
    }
    const themed = area.theme === "dark" ? darkDesignUrl : lightDesignUrl;
    return themed ?? designUrl ?? null;
  };

  function isMultiAreaTemplate(t: UnifiedTemplate): boolean {
    return !!t.isCustom && (t.designAreas?.length ?? 0) > 1;
  }

  /** Alan bazli render gereken sablon: coklu alan YA DA sekilli/kaplayan alan
   *  (tek alanli eski yol sekil/kirpma bilmez) */
  function usesAreaRender(t: UnifiedTemplate): boolean {
    return isMultiAreaTemplate(t)
      || (!!t.isCustom && (t.designAreas ?? []).some((a) => a.fit || isShaped(a) || hasWarp(a)))
      || (!!t.isCustom && (t.shading ?? 0) > 0 && !!t.shadeUrl)
      || (!!t.isCustom && (t.displace ?? 0) > 0 && !!t.dispUrl);
  }

  function handleMultiAreaUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !multiAreaTarget) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const url = ev.target?.result as string;
      setAreaDesignMap((prev) => ({ ...prev, [`${multiAreaTarget.mockupId}__${multiAreaTarget.areaId}`]: url }));
    };
    reader.readAsDataURL(file);
    if (multiAreaInputRef.current) multiAreaInputRef.current.value = "";
  }

  // Imperative render of all assigned area designs onto an off-screen layer
  // (used by export / add-to-images for multi-area mockups).
  // Cache loaded images by URL so the same mockup/design isn't fetched+decoded
  // once per render (big speedup for "Gorsellere Ekle").
  const imgCacheRef = useRef<Map<string, Promise<HTMLImageElement | null>>>(new Map());
  function loadImg(rawUrl: string): Promise<HTMLImageElement | null> {
    const url = rawUrl.startsWith("http") || rawUrl.startsWith("data:") ? rawUrl : `${API_BASE}${rawUrl}`;
    const cache = imgCacheRef.current;
    const hit = cache.get(url);
    if (hit) return hit;
    const p = new Promise<HTMLImageElement | null>((resolve) => {
      const im = new window.Image();
      im.crossOrigin = "anonymous";
      im.onload = () => resolve(im);
      im.onerror = () => resolve(null);
      im.src = url;
    });
    cache.set(url, p);
    return p;
  }

  /**
   * Occlusion: mockup'in on planda kalmasi gereken pikselleri (sac, kumas
   * kivrimi) tasarimin USTUNE cizilir. Tasarimi silmek yerine mockup'in kendi
   * pikselini geri koydugumuz icin doku ve golge bozulmaz.
   * Tasarim eklendikten SONRA, layer.draw()'dan once cagrilmali.
   */
  async function addForegroundToLayer(layer: Konva.Layer, t: UnifiedTemplate) {
    const fg = t.foregroundUrl ? await loadImg(t.foregroundUrl) : null;
    if (fg) layer.add(new Konva.Image({ image: fg, x: 0, y: 0, width: t.canvasWidth, height: t.canvasHeight, listening: false }));
    await addColorLabelToLayer(layer, t);
  }

  /** Renk adi etiketi en uste (sunucu render'i ile ayni geometri, Montserrat 600) */
  async function addColorLabelToLayer(layer: Konva.Layer, t: UnifiedTemplate) {
    const cl = t.colorLabel;
    const text = cl?.enabled ? colorLabelText(cl, t.color) : "";
    if (!cl || !text) return;
    await document.fonts?.load("600 48px Montserrat").catch(() => null);
    const ctx = document.createElement("canvas").getContext("2d")!;
    const b = colorLabelBox(cl, t.canvasWidth, t.canvasHeight, (fs) => { ctx.font = `600 ${fs}px Montserrat`; return ctx.measureText(text).width; });
    if (cl.shape === "brush") layer.add(new Konva.Path({ x: b.x, y: b.y, data: colorLabelBrushPath(b.w, b.h), fill: cl.bg, opacity: cl.bgOpacity, listening: false }));
    else if (cl.shape !== "none") layer.add(new Konva.Rect({ x: b.x, y: b.y, width: b.w, height: b.h, cornerRadius: b.radius, fill: cl.bg, opacity: cl.bgOpacity, listening: false }));
    layer.add(new Konva.Text({ x: b.x + b.pad, y: b.y, height: b.h, verticalAlign: "middle", text, fontSize: b.fontSize, fontFamily: "Montserrat", fontStyle: "600", fill: cl.textColor, listening: false }));
  }

  async function addAreaDesignsToLayer(layer: Konva.Layer, t: UnifiedTemplate, pick?: number[]) {
    const dOpacity = t.opacity / 100;
    const areasList = t.designAreas ?? [];
    // ISIK: tasarimlar kendi (cache'li) grubunda toplanir; isik haritasi
    // hard-light ile YALNIZCA onlara uygulanir, sonra tasarimin kendi alfasina
    // geri kirpilir (sunucudaki renderMockupComposite ile ayni sira)
    const shadeImg = (t.shading ?? 0) > 0 && t.shadeUrl ? await loadImg(t.shadeUrl) : null;
    const dispImg = (t.displace ?? 0) > 0 && t.dispUrl ? await loadImg(t.dispUrl) : null;
    const finish = !!(shadeImg || dispImg);
    const designNodes: Konva.Node[] = [];
    const putRaw = (n: Konva.Node) => { if (finish) designNodes.push(n); else layer.add(n as Konva.Shape); };
    for (let ai = 0; ai < areasList.length; ai++) {
      const a = areasList[ai];
      // Kendi urun yuzeyi: tasarim yalnizca orada (ust uste urun / katli kol)
      const surfRaw = a.surfaceUrl ? await loadImg(a.surfaceUrl) : null;
      // Sekilli alanda yuzey seklin %85'inden azini kapsiyorsa bozuk sayilir
      // (sunucudaki clipToSurface ile ayni kural): kirpma yapilmaz
      const surfAlpha = surfRaw ? maskToAlpha(surfRaw, t.canvasWidth, t.canvasHeight) : null;
      const surfImg = surfAlpha && isShaped(a) && surfaceCover(surfAlpha, a) < SURFACE_MIN_COVER ? null : surfRaw;
      const put = (n: Konva.Node) => {
        if (!surfImg) { putRaw(n); return; }
        const g = new Konva.Group({ listening: false });
        g.add(n as Konva.Shape);
        g.add(new Konva.Image({ image: maskToAlpha(surfImg, t.canvasWidth, t.canvasHeight), x: 0, y: 0, width: t.canvasWidth, height: t.canvasHeight, globalCompositeOperation: "destination-in" }));
        layer.add(g); // cache sahneye bagli dugumde guvenilir
        g.cache({ x: 0, y: 0, width: t.canvasWidth, height: t.canvasHeight, pixelRatio: 1 });
        g.remove();
        putRaw(g);
      };
      // Havuz dagilimi varsa k. alana havuzdaki tasarim
      const url = pick && designPool.length > 1 ? (poolUrl(pick[ai] ?? 0, a.theme) ?? resolveAreaUrl(t, a)) : resolveAreaUrl(t, a);
      if (!url) continue;
      const img = await loadImg(url);
      if (!img) continue;
      if (hasWarp(a)) {
        // Silindir/perspektif: ortak geometriyle (sunucuyla ayni) carpitilmis karo
        const r = renderWarpedArea(img, { x: a.x, y: a.y, w: a.width, h: a.height, rotation: a.rotation, shape: a.shape, fit: a.fit, warp: a.warp });
        put(new Konva.Image({ image: r.canvas, x: r.left, y: r.top, opacity: dOpacity }));
        continue;
      }
      if (a.fit || isShaped(a)) {
        // Sekilli/kaplayan alan: alanin kendi Group'u, sekle kirpilir
        // (sunucudaki prepareAreaTile ile ayni geometri)
        const box = { w: a.width, h: a.height, shape: a.shape, fit: a.fit };
        const p = placeInArea(box, img.naturalWidth, img.naturalHeight);
        const g = new Konva.Group({
          x: a.x, y: a.y, rotation: a.rotation || 0, opacity: dOpacity,
          clipFunc: isShaped(a) ? (ctx) => traceAreaShape(ctx._context as CanvasRenderingContext2D, a.shape, a.width, a.height) : undefined,
        });
        g.add(new Konva.Image({ image: img, x: p.x, y: p.y, width: p.width, height: p.height }));
        put(g);
        continue;
      }
      const f = fitDesignInArea(a, img.naturalWidth, img.naturalHeight);
      put(new Konva.Image({ image: img, x: f.x, y: f.y, width: f.width, height: f.height, rotation: f.rotation, opacity: dOpacity }));
    }
    if (finish && designNodes.length > 0) {
      // Sunucudaki renderMockupComposite ile ayni sira ve ayni piksel islemleri
      // (@erc/shared mockup-finish): kirisik -> isik (hard-light + dest-in) -> bitis
      const W = t.canvasWidth, H = t.canvasHeight;
      const dg = new Konva.Group({ listening: false });
      for (const n of designNodes) dg.add(n as Konva.Shape);
      layer.add(dg); // toCanvas sahneye bagli dugumde guvenilir
      const src = dg.toCanvas({ x: 0, y: 0, width: W, height: H, pixelRatio: 1 });
      dg.destroy();
      const cv = document.createElement("canvas");
      cv.width = W; cv.height = H;
      const ctx = cv.getContext("2d")!;
      ctx.drawImage(src, 0, 0, W, H);
      finishDesignCanvas(cv, { shadeImg, dispImg, displace: t.displace ?? 0, noise: t.photoNoise ?? 0 });
      layer.add(new Konva.Image({ image: cv, x: 0, y: 0, width: W, height: H, listening: false }));
    }
    layer.draw();
  }

  // Determine which design URL to use for current template theme
  const activeTemplateObj = allTemplates.find((t) => t.id === activeTemplate) ?? builtinTemplates[0];
  const activeDesignUrl = (() => {
    if (activeTemplateObj.theme === "dark" && darkDesignUrl) return darkDesignUrl;
    if (activeTemplateObj.theme === "light" && lightDesignUrl) return lightDesignUrl;
    return designUrl;
  })();

  // Tasarimlar sayfasindan gelirken Acik/Koyu slotlarini doldur.
  // lightUrl -> Acik slot, darkUrl -> Koyu slot; sadece designUrl varsa Acik'a koy.
  useEffect(() => {
    const abs = (u: string) => (u.startsWith("http") || u.startsWith("data:") ? u : `${API_BASE}${u}`);
    const next: Record<string, string> = {};
    if (lightUrlFromUrl) next.light = abs(lightUrlFromUrl);
    if (darkUrlFromUrl) next.dark = abs(darkUrlFromUrl);
    if (Object.keys(next).length === 0 && designFromUrl) next.light = abs(designFromUrl);
    if (Object.keys(next).length > 0) setAreaKeyDesigns((prev) => ({ ...prev, ...next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designFromUrl, lightUrlFromUrl, darkUrlFromUrl]);

  // Tasarım bir ürün analizinden geldiyse analiz + SEO'yu otomatik doldur
  const prefillFromAnalysis = useCallback(async (analysisId: string) => {
    try {
      const a = await apiClient.getAnalysis(analysisId);
      const bi = (v: unknown) => (v && typeof v === "object" ? String((v as Record<string, unknown>).tr ?? (v as Record<string, unknown>).en ?? "") : String(v ?? ""));
      const kw = (a.suggestedTags?.length ? a.suggestedTags : [...(a.keywordClusters?.primary ?? []), ...(a.keywordClusters?.longTail ?? [])]).slice(0, 13);
      // Sahne önerilerini analiz verisinden YEREL üret (ek AI/token yok)
      const product = (a.productType || "graphic t-shirt").toLowerCase();
      const audience = a.ideaBriefs?.[0]?.targetAudience || a.niche || "a young woman";
      const theme = (a.designAnalysis ? bi(a.designAnalysis.theme) : "") || a.niche || "";
      const mood = a.designAnalysis ? bi(a.designAnalysis.mood) : "";
      const scenes = [
        `${audience} wearing the ${product} in a cozy sunlit home, candid lifestyle, natural light${mood ? `, ${mood}` : ""}`,
        `${audience} outdoors wearing the ${product}${theme ? `, ${theme} setting` : ""}, golden hour, warm tones, relaxed smile`,
        `Aesthetic flat lay of the ${product} styled with accessories on a neutral background${theme ? `, ${theme} vibe` : ""}`,
        `Close-up of a person wearing the ${product} at a coffee shop, urban lifestyle, soft bokeh`,
        `${audience} wearing the ${product} at a casual gathering${theme ? `, ${theme} theme` : ""}, vibrant and joyful`,
        `Editorial model wearing the ${product}, minimal studio background, clean professional lighting`,
      ].map((s) => s.replace(/\s+/g, " ").trim());
      if (a.source?.url) {
        setSourceListing({
          url: a.source.url,
          title: a.source.title ?? null,
          price: a.source.price ?? null,
          imageUrls: a.source.imageUrls ?? [],
        });
      }
      setLifestyleAnalysis({
        analysis: (a.designAnalysis ? bi(a.designAnalysis.summary) : "") || a.styleNotes || a.niche || "",
        keywords: kw,
        targetAudience: a.ideaBriefs?.[0]?.targetAudience || a.niche || "",
        lifestyleScenes: scenes,
      });
      setSeoContent({
        title: a.suggestedTitle || "",
        description: a.suggestedDescription || "",
        tags: (a.suggestedTags ?? []).slice(0, 13),
        titleAlternatives: [],
      });
      setAutoFilled(true);
    } catch { /* analiz çekilemezse sessizce geç */ }

    // ── Uretim zinciri: kategoriden listing sablonu + mockup grubu OTOMATIK ──
    // Cozum sunucuda: analiz kaydi → magaza stratejisi → kategori varsayilani.
    // Buradaki secimler kullanicinin degistirmesine acik kalir.
    try {
      const res = await fetch(`${API_BASE}/api/analyses/${analysisId}/production-plan`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const plan = (await res.json()).data as {
        productTypeKey: string | null;
        listingTemplateId: string | null;
        mockupCollectionIds: string[];
        sellingMockupId: string | null;
        sellingMockupUrl: string | null;
      };
      setProductionPlan(plan);
      if (plan.listingTemplateId) setSelectedListingTemplateId(plan.listingTemplateId);
      if (plan.mockupCollectionIds.length > 0) setSelectedCollectionIds(plan.mockupCollectionIds);
    } catch { /* plan cozulemezse elle secim gecerli */ }
  }, []);

  const analysisIdFromUrl = searchParams.get("analysisId");
  useEffect(() => {
    if (analysisIdFromUrl) prefillFromAnalysis(analysisIdFromUrl);
  }, [analysisIdFromUrl, prefillFromAnalysis]);

  // Kaynak analizin kisisellestirme alanlarini yukle (duzenlenebilir)
  useEffect(() => {
    let cancelled = false;
    if (!analysisIdFromUrl) { setPersoFields([]); return; }
    apiClient.getAnalysis(analysisIdFromUrl).then((a) => {
      if (cancelled) return;
      setPersoFields(((a as Analysis).source?.rawData?.personalization as PersonalizationField[]) || []);
      setPersoDirty(false);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [analysisIdFromUrl]);
  async function savePersoFields() {
    if (!analysisIdFromUrl) return;
    setPersoSaving(true);
    try { await apiClient.setAnalysisPersonalization(analysisIdFromUrl, persoFields); setPersoDirty(false); }
    finally { setPersoSaving(false); }
  }

  useEffect(() => { fetchCustomMockups(); fetchCollections(); fetchMockupFolders(); fetchListingTemplatesList(); fetchProductTypes(); }, []);

  // Remember the last-used collection: once collections load, auto-select it.
  const lastCollectionRestoredRef = useRef(false);
  useEffect(() => {
    if (lastCollectionRestoredRef.current || collections.length === 0) return;
    lastCollectionRestoredRef.current = true;
    let lastIds: string[] = [];
    try {
      const multi = localStorage.getItem("erc_studio_last_collections");
      if (multi) lastIds = JSON.parse(multi) as string[];
      // Tek koleksiyonlu eski anahtardan gecis
      if (lastIds.length === 0) {
        const single = localStorage.getItem("erc_studio_last_collection");
        if (single) lastIds = [single];
      }
    } catch { /* ignore */ }

    const found = lastIds
      .map((id) => collections.find((x) => x.id === id))
      .filter((c): c is MockupCollection => !!c);
    if (found.length > 0) {
      setSelectedCollectionIds(found.map((c) => c.id));
      setSelectedTemplates(new Set(found.flatMap((c) => c.mockups.map((m) => `custom_${m.id}`))));
    }
  }, [collections]);

  useEffect(() => {
    const url = activeDesignUrl;
    if (!url) { setDesignNaturalSize({ w: 0, h: 0 }); return; }
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => setDesignNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = url;
  }, [activeDesignUrl]);

  // Bir mockup'a marka/model/renk ata (elle) — iyimser guncelleme + sunucuya yaz
  const assignMockupVariation = useCallback(async (mockupId: string, patch: { brand?: string | null; model?: string | null; color?: string | null }) => {
    setCustomMockups((prev) => prev.map((m) => (m.id === mockupId ? { ...m, ...patch } : m)));
    try {
      await apiClient.updateMockup(mockupId, patch);
    } catch {
      fetchCustomMockups();
    }
  }, []);

  async function fetchCustomMockups() {
    setLoadingCustom(true);
    try {
      // Taki magazasinda tekstil mockuplari listelenmez (uretim mantigi farkli)
      const data = await apiClient.listMockups(mockupKind);
      setCustomMockups(data ?? []);
      // Eksik onizlemeler varsa arka planda uret (picker'in hizli acilmasi icin)
      if ((data ?? []).some((m) => !m.thumbnailUrl)) {
        apiClient.backfillMockupThumbnails()
          .then((r) => { if (r.updated > 0) apiClient.listMockups(mockupKind).then((d) => setCustomMockups(d ?? [])).catch(() => {}); })
          .catch(() => {});
      }
    } catch { /* ignore */ }
    setLoadingCustom(false);
  }

  async function fetchMockupFolders() {
    try {
      setMockupFolders((await apiClient.listMockupFolders(mockupKind)) ?? []);
    } catch { /* klasorler alinamadi — picker klasorsuz calisir */ }
  }

  async function fetchCollections() {
    try {
      // Studio da aktif dikeyin koleksiyonlarini gosterir
      const data = await apiClient.listMockupCollections(mockupKind);
      setCollections(data ?? []);
    } catch { /* ignore */ }
  }

  // Aktif urun tipi profilleri — mockup gruplarini etiketlemek icin
  async function fetchProductTypes() {
    try {
      const res = await fetch(`${API_BASE}/api/product-types`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const json = await res.json();
      setProductTypeProfiles(json.data ?? []);
    } catch { /* ignore */ }
  }

  async function fetchListingTemplatesList() {
    try {
      const res = await fetch(`${API_BASE}/api/listing-templates`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const json = await res.json();
      const items = (json.data ?? []) as Array<{ id: string; name: string; imageUrls?: string[] }>;
      const mapped = items.map((t) => ({ id: t.id, name: t.name, imageCount: (t.imageUrls ?? []).length }));
      setListingTemplates(mapped);
      // Pre-select the last-used listing template if it still exists.
      try {
        const last = localStorage.getItem("erc_last_listing_template");
        if (last && mapped.some((t) => t.id === last)) setSelectedListingTemplateId((cur) => cur || last);
      } catch { /* ignore */ }
    } catch { /* ignore */ }
  }

  async function handleMockupUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadingMockup(true);
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try { await apiClient.uploadMockup(file, file.name.replace(/\.[^.]+$/, "")); } catch { /* skip */ }
    }
    await fetchCustomMockups();
    setUploadingMockup(false);
    setShowCustomTab(true);
    if (mockupFileInputRef.current) mockupFileInputRef.current.value = "";
  }

  async function handleDeleteCustomMockup(id: string) {
    try {
      await apiClient.deleteMockup(id);
      setCustomMockups((prev) => prev.filter((m) => m.id !== id));
      setSelectedTemplates((prev) => { const next = new Set(prev); next.delete(`custom_${id}`); return next; });
      if (activeTemplate === `custom_${id}`) setActiveTemplate("tshirt_white");
    } catch { /* ignore */ }
  }

  // Save the full designAreas array (same model as Mockup Yonetimi) so coordinates
  // match and the grid reflects the change immediately (customToUnified reads the array).
  async function saveMockupAreasStudio(mockupId: string, areas: AreaRect[]): Promise<CustomMockup | null> {
    try {
      const first = areas[0];
      const updated = await apiClient.updateMockup(mockupId, {
        designAreas: areas as any,
        theme: (((first as any)?.theme as "light" | "dark" | undefined) ?? "light"),
        ...(first ? {
          designAreaX: Math.round(first.x),
          designAreaY: Math.round(first.y),
          designAreaW: Math.round(first.w),
          designAreaH: Math.round(first.h),
          designAreaRotation: Math.round(first.rotation || 0),
        } : { designAreaX: 0, designAreaY: 0, designAreaW: 0, designAreaH: 0, designAreaRotation: 0 }),
      });
      setCustomMockups((prev) => prev.map((m) => (m.id === mockupId ? updated : m)));
      return updated;
    } catch (err) {
      console.error("Design area save failed:", err);
      alert("Alan kaydedilemedi: " + (err instanceof Error ? err.message : String(err)));
      return null;
    }
  }

  async function handleSaveDesignArea(mockupId: string, areas: AreaRect[]) {
    await saveMockupAreasStudio(mockupId, areas);
    setConfiguringMockup(null);
  }

  // Bir gruba urun tipi ata (iyimser guncelleme + sunucuya yaz)
  async function setCollectionProductType(collectionId: string, productTypeKey: string) {
    setCollections((prev) =>
      prev.map((c) => (c.id === collectionId ? { ...c, productTypeKey: productTypeKey || null } : c))
    );
    try {
      await fetch(`${API_BASE}/api/mockups/collections/${collectionId}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ productTypeKey: productTypeKey || null }),
      });
    } catch {
      /* ag hatasi — bir sonraki yuklemede sunucudaki deger gecerli olur */
    }
  }

  // Grup sec/kaldir. Birden fazla grup secilebilir: tisort + kupa + susleme
  // ayni tasarimla birlikte uretilir, sonra HER GRUP ayri taslak olur.
  function selectCollection(collection: MockupCollection) {
    const ids = collection.mockups.map((m) => `custom_${m.id}`); // already in sortOrder
    const isOn = selectedCollectionIds.includes(collection.id);

    if (isOn) {
      // Gruptan cik: sadece BU grubun mockup'lari secimden dusurulur,
      // diger gruplarinkiler kalir.
      const nextIds = selectedCollectionIds.filter((id) => id !== collection.id);
      setSelectedCollectionIds(nextIds);
      setTemplateNeedsPick(nextIds.length > 0 && !selectedListingTemplateId);
      setSelectedTemplates((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      });
      try {
        localStorage.setItem("erc_studio_last_collections", JSON.stringify(nextIds));
      } catch { /* ignore */ }
      return;
    }

    const nextIds = [...selectedCollectionIds, collection.id];
    setSelectedCollectionIds(nextIds);
    // Grup degisti: sablon yeniden secilsin (sessizce eskisi kullanilmasin)
    setSelectedListingTemplateId("");
    setTemplateNeedsPick(true);
    setSelectedTemplates((prev) => new Set([...prev, ...ids]));
    setShowAddMockups(false);
    try {
      localStorage.setItem("erc_studio_last_collections", JSON.stringify(nextIds));
    } catch { /* ignore */ }
    // Auto-generate product images (in collection order) when a design exists.
    if (designUrl || lightDesignUrl || darkDesignUrl || Object.keys(areaKeyDesigns).length > 0) {
      setTimeout(() => autoGenerateProductImages(ids), 100);
    }
  }

  // Auto-generate product images for given template IDs
  // Render one template to a PNG using cached images. No artificial delays.
  /** Havuz dagilimi: tek alanli sirayla, cok alanlida alanlar sirayla (sonrakinde devam) */
  function planPool(templates: UnifiedTemplate[], overrides: Record<string, number> = {}): Map<string, number[]> {
    const out = new Map<string, number[]>();
    const n = designPool.length;
    if (n < 2) return out;
    let a = poolSeed >>> 0;
    const rnd = () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
    let counter = 0;
    for (const t of templates) {
      const areas = isMultiAreaTemplate(t) ? (t.designAreas?.length ?? 1) : 1;
      const start = typeof overrides[t.id] === "number" ? ((overrides[t.id] % n) + n) % n
        : poolMode === "random" ? Math.floor(rnd() * n) : counter % n;
      out.set(t.id, Array.from({ length: areas }, (_, k) => (start + k) % n));
      counter += areas;
    }
    return out;
  }
  const poolUrl = (idx: number, theme?: string) => {
    const pd = designPool[idx];
    if (!pd) return null;
    return theme === "dark" ? (pd.darkUrl || pd.lightUrl) : (pd.lightUrl || pd.darkUrl);
  };

  async function renderTemplateComposite(template: UnifiedTemplate, pick?: number[]): Promise<{ url: string; thumbUrl: string; label: string; id: string } | null> {
    const container = document.createElement("div");
    container.style.cssText = "position:fixed;left:-9999px;top:-9999px";
    document.body.appendChild(container);
    const stage = new Konva.Stage({ container, width: template.canvasWidth, height: template.canvasHeight });
    const layer = new Konva.Layer(); stage.add(layer);
    try {
      if (template.imageUrl) {
        const bg = await loadImg(template.imageUrl);
        if (bg) layer.add(new Konva.Image({ image: bg, width: template.canvasWidth, height: template.canvasHeight }));
      }
      if (usesAreaRender(template)) {
        if (!pick && !template.designAreas?.some((a) => resolveAreaUrl(template, a))) return null;
        await addAreaDesignsToLayer(layer, template, pick);
      } else {
        const tDesignUrl = pick && designPool.length > 1
          ? poolUrl(pick[0] ?? 0, template.theme) ?? designUrl
          : template.theme === "dark" && darkDesignUrl ? darkDesignUrl
          : template.theme === "light" && lightDesignUrl ? lightDesignUrl
          : designUrl;
        if (!tDesignUrl) return null;
        const im = await loadImg(tDesignUrl);
        if (!im) return null;
        const fitted = fitDesignInArea(template.designArea, im.naturalWidth, im.naturalHeight);
        layer.add(new Konva.Image({ image: im, x: fitted.x, y: fitted.y, width: fitted.width, height: fitted.height, rotation: fitted.rotation, opacity: template.opacity / 100 }));
      }
      await addForegroundToLayer(layer, template);
      layer.draw();
      const maxDim = Math.max(template.canvasWidth, template.canvasHeight);
      // Full image (Etsy upload / lightbox), capped at ~2000px longest side.
      // Mockup = opak fotograf kompozisyonu → JPEG (PNG'nin ~1/10'u boyut, cok daha hizli yukleme).
      const fullPR = Math.min(1, 2000 / maxDim);
      const url = stage.toDataURL({ pixelRatio: fullPR, mimeType: "image/jpeg", quality: 0.9 });
      // Small JPEG thumbnail for the grid so scrolling doesn't re-decode the big image.
      const thumbPR = Math.min(fullPR, 400 / maxDim);
      const thumbUrl = stage.toDataURL({ pixelRatio: thumbPR, mimeType: "image/jpeg", quality: 0.82 });
      return { url, thumbUrl, label: template.label, id: template.id };
    } finally {
      stage.destroy(); document.body.removeChild(container);
    }
  }

  async function autoGenerateProductImages(templateIds: string[]) {
    if (!designUrl && !lightDesignUrl && !darkDesignUrl && Object.keys(areaDesignMap).length === 0) return;
    setIsAddingToImages(true);
    try {
      // Render with limited concurrency (image loads overlap); keep collection order.
      const results: ({ url: string; thumbUrl: string; label: string; id: string } | null)[] = new Array(templateIds.length).fill(null);
      // Coklu tasarim: her mockup'a havuzdan (sirayla/karisik) tasarim
      const tplList = templateIds.map((id) => allTemplates.find((x) => x.id === id)).filter((x): x is UnifiedTemplate => Boolean(x));
      const plan = poolActive ? planPool(tplList) : null;
      let next = 0;
      const CONCURRENCY = 6;
      const worker = async () => {
        while (next < templateIds.length) {
          const i = next++;
          const t = allTemplates.find((x) => x.id === templateIds[i]);
          results[i] = t ? await renderTemplateComposite(t, plan?.get(t.id)) : null;
        }
      };
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, templateIds.length) }, worker));
      for (const r of results) {
        if (!r) continue;
        addToProductImages(r.url, poolActive ? `${r.label} · ${designPool[plan?.get(r.id)?.[0] ?? 0]?.label ?? ""}` : r.label, r.id, r.thumbUrl);
        if (plan) {
          const start = plan.get(r.id)?.[0] ?? 0;
          setProductImages((prev) => prev.map((p) => (p.templateId === r.id && p.url === r.url ? { ...p, poolStart: start } : p)));
        }
      }
    } finally {
      setIsAddingToImages(false);
    }
  }

  /** TEK mockup'i urun gorsellerine ekle. Grubun tamamini uretmek
   *  istemeyen akis icin: sahneyi sec, ekle, digerini ekleme. */
  async function addSingleToProductImages(templateId: string) {
    const t = allTemplates.find((x) => x.id === templateId);
    if (!t) return;
    if (!designUrl && !lightDesignUrl && !darkDesignUrl && Object.keys(areaDesignMap).length === 0) return;
    setIsAddingToImages(true);
    try {
      const r = await renderTemplateComposite(t);
      if (r) addToProductImages(r.url, r.label, r.id, r.thumbUrl);
    } finally {
      setIsAddingToImages(false);
    }
  }

  const fitDesignToTemplate = useCallback((t: UnifiedTemplate) => {
    if (designNaturalSize.w > 0 && designNaturalSize.h > 0) {
      setDesign(fitDesignInArea(t.designArea, designNaturalSize.w, designNaturalSize.h));
    } else {
      setDesign({ x: t.designArea.x, y: t.designArea.y, width: t.designArea.width, height: t.designArea.height, rotation: t.designArea.rotation || 0 });
    }
  }, [designNaturalSize]);

  useEffect(() => {
    if (designNaturalSize.w > 0 && designNaturalSize.h > 0 && activeTemplateObj) {
      setDesign(fitDesignInArea(activeTemplateObj.designArea, designNaturalSize.w, designNaturalSize.h));
    }
  }, [designNaturalSize, activeTemplateObj]);

  // ── Design upload driven by the areas the SELECTED mockups actually need ──
  // Slot key: single-area selections collapse to theme-only ("light"/"dark");
  // multi-area selections use "labelKey|theme" (e.g. "on|light", "alan-1|dark").
  const AREA_NAMED: Record<string, string> = { on: "Ön", arka: "Arka", "sol-kol": "Sol Kol", "sag-kol": "Sağ Kol", kol: "Kol", yaka: "Yaka", cep: "Cep" };
  const labelDisplay = (label?: string) => {
    const m = /^alan-(\d+)$/.exec(label || "");
    return AREA_NAMED[label || ""] || (m ? `Alan ${m[1]}` : (label || "Alan"));
  };
  const labelKeyOf = (label?: string) => (label || "").toLowerCase();
  const areasOfMockup = (m: CustomMockup): Array<{ label?: string; theme?: "light" | "dark" }> => {
    const da = (m as any).designAreas;
    if (Array.isArray(da) && da.length > 0) return da;
    if (m.designAreaW > 20 && m.designAreaH > 20) return [{ label: "on", theme: m.theme }];
    return [];
  };

  const selectedCustomMockups = customMockups.filter((m) => selectedTemplates.has(`custom_${m.id}`));
  // Exception: every selected mockup has exactly ONE area -> just Acik + Koyu.
  const singleAreaMode = selectedCustomMockups.length === 0 || selectedCustomMockups.every((m) => areasOfMockup(m).length === 1);
  const slotKeyForArea = (area: { label?: string; theme?: "light" | "dark" }): string => {
    const theme = area.theme ?? "light";
    return singleAreaMode ? theme : `${labelKeyOf(area.label)}|${theme}`;
  };
  // Distinct upload slots needed by the current selection.
  const areaSlots: Array<{ key: string; label: string; theme: "light" | "dark" }> = (() => {
    if (singleAreaMode) {
      return [
        { key: "light", label: "Açık", theme: "light" },
        { key: "dark", label: "Koyu", theme: "dark" },
      ];
    }
    const seen = new Map<string, { key: string; label: string; theme: "light" | "dark" }>();
    for (const m of selectedCustomMockups) {
      for (const a of areasOfMockup(m)) {
        const key = slotKeyForArea(a);
        if (!seen.has(key)) seen.set(key, { key, label: labelDisplay(a.label), theme: a.theme ?? "light" });
      }
    }
    return Array.from(seen.values());
  })();

  const [areaKeyDesigns, setAreaKeyDesigns] = useState<Record<string, string>>({});
  const areaInputRef = useRef<HTMLInputElement>(null);
  const [areaUploadKey, setAreaUploadKey] = useState<string>("light");

  // Derive designUrl / lightDesignUrl / darkDesignUrl from slots so analysis,
  // draft and gating keep working. Skip when empty so a restored draft's direct
  // light/dark urls aren't wiped.
  useEffect(() => {
    const entries = Object.entries(areaKeyDesigns);
    if (entries.length === 0) return;
    const light = entries.find(([k]) => k === "light" || k.endsWith("|light"))?.[1] ?? entries[0][1];
    const dark = entries.find(([k]) => k === "dark" || k.endsWith("|dark"))?.[1] ?? null;
    setLightDesignUrl(light);
    setDarkDesignUrl(dark);
    setDesignUrl(light || dark);
  }, [areaKeyDesigns]);

  function handleAreaDesignUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const url = ev.target?.result as string;
      setAreaKeyDesigns((prev) => ({ ...prev, [areaUploadKey]: url }));
    };
    reader.readAsDataURL(file);
    if (areaInputRef.current) areaInputRef.current.value = "";
  }

  function clearAreaKeyDesign(key: string) {
    setAreaKeyDesigns((prev) => { const n = { ...prev }; delete n[key]; return n; });
  }

  function clearAllDesigns() {
    setAreaKeyDesigns({});
    setDesignUrl(null);
    setLightDesignUrl(null);
    setDarkDesignUrl(null);
  }

  const hasAnyDesign = Object.keys(areaKeyDesigns).length > 0 || !!lightDesignUrl || !!darkDesignUrl || !!designUrl;

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const url = ev.target?.result as string;
      setAreaKeyDesigns((prev) => ({ ...prev, light: url }));
    };
    reader.readAsDataURL(file);
  }, []);

  function toggleTemplate(id: string) {
    setSelectedTemplates((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  }

  function activateTemplate(id: string) {
    setActiveTemplate(id);
    const t = allTemplates.find((t) => t.id === id);
    if (t) fitDesignToTemplate(t);
  }

  async function handleBatchExport() {
    if (!designUrl && !lightDesignUrl && !darkDesignUrl && Object.keys(areaDesignMap).length === 0) return;
    if (selectedTemplates.size === 0) return;
    setIsExporting(true);

    const templates = allTemplates.filter((t) => selectedTemplates.has(t.id));

    for (const template of templates) {
      // Multi-area custom mockup: composite + download one image with all area designs.
      if (isMultiAreaTemplate(template)) {
        const hasAny = template.designAreas?.some(a => resolveAreaUrl(template, a));
        if (!hasAny) continue;
        const container = document.createElement("div");
        container.style.cssText = "position:fixed;left:-9999px";
        document.body.appendChild(container);
        const stage = new Konva.Stage({ container, width: template.canvasWidth, height: template.canvasHeight });
        const layer = new Konva.Layer(); stage.add(layer);
        if (template.imageUrl) {
          await new Promise<void>((resolve) => {
            const bgImg = new window.Image(); bgImg.crossOrigin = "anonymous";
            bgImg.onload = () => { layer.add(new Konva.Image({ image: bgImg, x: 0, y: 0, width: template.canvasWidth, height: template.canvasHeight })); resolve(); };
            bgImg.onerror = () => { layer.add(new Konva.Rect({ x: 0, y: 0, width: template.canvasWidth, height: template.canvasHeight, fill: "#f0f0f0" })); resolve(); };
            bgImg.src = template.imageUrl!;
          });
        }
        await addAreaDesignsToLayer(layer, template);
        await addForegroundToLayer(layer, template);
        const dataUrl = stage.toDataURL({ pixelRatio: 1, mimeType: "image/png" });
        const a = document.createElement("a");
        a.download = `mockup-${template.id}.png`;
        a.href = dataUrl;
        a.click();
        stage.destroy(); document.body.removeChild(container);
        await new Promise((r) => setTimeout(r, 300));
        continue;
      }

      // Pick the right design version for this template's theme
      const tDesignUrl = (() => {
        if (template.theme === "dark" && darkDesignUrl) return darkDesignUrl;
        if (template.theme === "light" && lightDesignUrl) return lightDesignUrl;
        return designUrl;
      })();
      if (!tDesignUrl) continue;

      const container = document.createElement("div");
      container.style.cssText = "position:fixed;left:-9999px";
      document.body.appendChild(container);

      const stage = new Konva.Stage({ container, width: template.canvasWidth, height: template.canvasHeight });
      const layer = new Konva.Layer();
      stage.add(layer);

      if (template.isCustom && template.imageUrl) {
        await new Promise<void>((resolve) => {
          const bgImg = new window.Image();
          bgImg.crossOrigin = "anonymous";
          bgImg.onload = () => { layer.add(new Konva.Image({ image: bgImg, x: 0, y: 0, width: template.canvasWidth, height: template.canvasHeight })); resolve(); };
          bgImg.onerror = () => { layer.add(new Konva.Rect({ x: 0, y: 0, width: template.canvasWidth, height: template.canvasHeight, fill: "#f0f0f0" })); resolve(); };
          bgImg.src = template.imageUrl!;
        });
      } else {
        layer.add(new Konva.Rect({ x: 0, y: 0, width: template.canvasWidth, height: template.canvasHeight, fill: template.bgColor }));
      }

      // Need natural size of the design used for this template
      const tNatSize = await new Promise<{ w: number; h: number }>((resolve) => {
        const img = new window.Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => resolve({ w: 0, h: 0 });
        img.src = tDesignUrl;
      });

      const fitted = fitDesignInArea(template.designArea, tNatSize.w, tNatSize.h);
      const dOpacity = template.opacity / 100;

      await new Promise<void>((resolve) => {
        const img = new window.Image();
        img.crossOrigin = "anonymous";
        img.onload = async () => {
          layer.add(new Konva.Image({ image: img, x: fitted.x, y: fitted.y, width: fitted.width, height: fitted.height, rotation: fitted.rotation, opacity: dOpacity }));
          await addForegroundToLayer(layer, template);
          layer.draw();
          const dataUrl = stage.toDataURL({ pixelRatio: 1, mimeType: "image/png" });
          const a = document.createElement("a");
          a.download = `mockup-${template.id}.png`;
          a.href = dataUrl;
          a.click();
          stage.destroy();
          document.body.removeChild(container);
          resolve();
        };
        img.onerror = () => { stage.destroy(); document.body.removeChild(container); resolve(); };
        img.src = tDesignUrl;
      });
      await new Promise((r) => setTimeout(r, 300));
    }
    setIsExporting(false);
  }

  const [isAddingToImages, setIsAddingToImages] = useState(false);

  async function handleBatchAddToImages() {
    if (selectedTemplates.size === 0 || (!designUrl && !lightDesignUrl && !darkDesignUrl)) return;
    await autoGenerateProductImages(orderedSelectedIds());
  }

  const [isExportingToListing, setIsExportingToListing] = useState(false);
  // Aktarim oncesi tasarim ↔ baslik/aciklama/tag tutarlilik denetimi
  const [showConsistency, setShowConsistency] = useState(false);
  const [consistencyImageUrl, setConsistencyImageUrl] = useState<string>("");
  const [isSavingToDesigns, setIsSavingToDesigns] = useState(false);
  const [canvaDesignId, setCanvaDesignId] = useState<string | null>(null);
  const [canvaEditUrl, setCanvaEditUrl] = useState<string | null>(null);
  const [isImportingFromCanva, setIsImportingFromCanva] = useState(false);

  // Lifestyle photo generation
  const [lifestyleAnalysis, setLifestyleAnalysis] = useState<{ analysis: string; keywords: string[]; targetAudience: string; lifestyleScenes: string[] } | null>(null);
  // Ilham alinan liste (analizin kaynagi) — kucuk kart olarak gosterilir
  const [sourceListing, setSourceListing] = useState<
    { url: string; title: string | null; price: string | null; imageUrls: string[] } | null
  >(null);
  const [autoFilled, setAutoFilled] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  // SEO/GEO title + description generated from the design analysis (Etsy 2026 rules).
  const [seoContent, setSeoContent] = useState<{ title: string; description: string; tags: string[]; titleAlternatives?: string[] } | null>(null);
  const [isGeneratingSeo, setIsGeneratingSeo] = useState(false);
  const [seoError, setSeoError] = useState<string | null>(null);

  // "Comfort Colors" temizligi: secili listing sablonunun adinda CC yoksa (yani urun CC degil)
  // ve SEO'da comfort colors geciyorsa, temizle butonu gosterilir.
  const CC_RE = /comfort\s*colors/i;
  const selectedListingTemplate = listingTemplates.find((t) => t.id === selectedListingTemplateId);
  const selectedListingTemplateName = selectedListingTemplate?.name || "";
  // Etsy bir listede en fazla 20 gorsel kabul eder. Aktarimda sablonun
  // gorselleri (size chart vb.) SONA eklendigi icin fazlasi sessizce dusuyordu.
  const templateImageCount = selectedListingTemplate?.imageCount ?? 0;
  const totalImageCount = productImages.length + templateImageCount;
  const overImageLimit = totalImageCount > 20;
  const listingIsComfortColors = CC_RE.test(selectedListingTemplateName);
  const seoHasComfortColors = !!seoContent && (
    CC_RE.test(seoContent.title) || CC_RE.test(seoContent.description) || seoContent.tags.some((t) => CC_RE.test(t))
  );
  const showCleanComfortColors = seoHasComfortColors && !listingIsComfortColors;

  function cleanComfortColors() {
    if (!seoContent) return;
    const tidy = (s: string) => s
      .replace(/comfort\s*colors/gi, "")
      .replace(/[ \t]{2,}/g, " ")
      .replace(/\s+,/g, ",")
      .replace(/,\s*,/g, ",")
      .replace(/^[\s,]+|[\s,]+$/g, "")
      .trim();
    // Etiket: "comfort colors" iceren etiketleri tamamen at
    const newTags = seoContent.tags.filter((t) => !CC_RE.test(t));
    setSeoContent({
      ...seoContent,
      title: tidy(seoContent.title),
      description: tidy(seoContent.description),
      tags: newTags,
    });
  }
  const [lifestyleResults, setLifestyleResults] = useState<Array<{ scene: string; imageUrl?: string; loading?: boolean; error?: string; prompt?: string }>>([]);
  // Lifestyle model seçimleri localStorage'da hatırlanır (analiz = vision, üretim = imageGen)
  const [lifestyleModel, setLifestyleModel] = useState(() => {
    try { return localStorage.getItem("erc:lifestyle-vision") || "google/gemini-3-flash-preview"; } catch { return "google/gemini-3-flash-preview"; }
  });
  const [lifestyleGenModel, setLifestyleGenModel] = useState(() => {
    try { return localStorage.getItem("erc:lifestyle-imagegen") || "google/gemini-3-pro-image-preview"; } catch { return "google/gemini-3-pro-image-preview"; }
  });
  useEffect(() => { try { localStorage.setItem("erc:lifestyle-vision", lifestyleModel); } catch { /* ignore */ } }, [lifestyleModel]);
  useEffect(() => { try { localStorage.setItem("erc:lifestyle-imagegen", lifestyleGenModel); } catch { /* ignore */ } }, [lifestyleGenModel]);
  const [aiModels, setAiModels] = useState<{ vision: Array<{id: string; name: string; price?: string}>; imageGen: Array<{id: string; name: string; price?: string}> } | null>(null);
  const [aiModelsError, setAiModelsError] = useState<string | null>(null);

  // Product config for lifestyle generation
  const [productTypes, setProductTypes] = useState<Set<string>>(new Set(["t-shirt"]));
  const [productColors, setProductColors] = useState<string[]>(["white", "black"]);
  const [printAreas, setPrintAreas] = useState<Set<string>>(new Set(["front"]));

  const PRODUCT_TYPE_OPTIONS = [
    { id: "t-shirt", label: "Tişört" },
    { id: "sweatshirt", label: "Sweatshirt" },
    { id: "hoodie", label: "Hoodie" },
    { id: "tank-top", label: "Atlet" },
    { id: "long-sleeve", label: "Uzun Kollu" },
  ];
  const PRINT_AREA_OPTIONS = [
    { id: "front", label: "Ön" },
    { id: "back", label: "Arka" },
    { id: "sleeve", label: "Kol" },
    { id: "pocket", label: "Cep" },
  ];
  const COLOR_OPTIONS = ["white", "black", "cream", "sand", "gray", "navy", "sage", "pink", "red", "royal-blue"];
  const [showLifestyle] = useState(true); // Lifestyle panel is always open (not collapsible)
  const [selectedScenes, setSelectedScenes] = useState<Set<number>>(new Set());
  const [editingPrompt, setEditingPrompt] = useState<{ index: number; text: string } | null>(null);
  const [isGeneratingBatch, setIsGeneratingBatch] = useState(false);

  // Video generation
  const [videoGenerating, setVideoGenerating] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [videoAspectRatio, setVideoAspectRatio] = useState<"1:1" | "16:9" | "9:16">("1:1");
  const [videoDuration, setVideoDuration] = useState<6 | 8>(8);
  const [videoZoom, setVideoZoom] = useState(8); // 0-15%

  // Load AI models
  useEffect(() => {
    apiClient.getDesignAIModels()
      .then((m) => { setAiModels(m); setAiModelsError(null); })
      .catch((e) => {
        console.error("[MockupStudio] getDesignAIModels failed:", e);
        setAiModelsError(e instanceof Error ? e.message : "bilinmeyen hata");
      });
  }, []);

  // Convert any image URL (blob, local, http) to a usable URL for AI API
  async function getImageUrlForAI(url: string): Promise<string> {
    // If already an http URL, use directly
    if (url.startsWith("http") && !url.startsWith("blob:")) return url;
    // If API-relative path, prefix with API URL
    if (url.startsWith("/uploads/") || url.startsWith("/generated/")) return `${env.API_URL}${url}`;
    // blob: or data: URL - convert to base64 data URL
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  // Analyze design for lifestyle photos
  async function handleAnalyzeDesign() {
    const url = designUrl || lightDesignUrl || darkDesignUrl;
    if (!url) return;
    setIsAnalyzing(true);
    setAutoFilled(false);
    setLifestyleAnalysis(null);
    setLifestyleResults([]);
    try {
      const imageUrl = await getImageUrlForAI(url);
      const productContext = {
        productTypes: Array.from(productTypes),
        printAreas: Array.from(printAreas),
        colors: productColors,
        hasLightDesign: !!lightDesignUrl,
        hasDarkDesign: !!darkDesignUrl,
      };
      const result = await apiClient.analyzeDesignImage(imageUrl, lifestyleModel, productContext);
      setLifestyleAnalysis(result);
      setSeoContent(null);
    } catch (err: unknown) {
      alert("Analiz hatası: " + ((err as Error).message || "Bilinmeyen hata"));
    } finally {
      setIsAnalyzing(false);
    }
  }

  // Generate an Etsy SEO/GEO-compliant title + description + tags from the design
  // analysis. Reuses the builder's 2026-rules seo-generate endpoint.
  async function handleGenerateSeo() {
    if (!lifestyleAnalysis) return;
    setIsGeneratingSeo(true);
    setSeoError(null);
    try {
      const keyword = lifestyleAnalysis.keywords.slice(0, 3).join(" ").trim()
        || Array.from(productTypes)[0] || "t-shirt";
      const res = await fetch(`${API_BASE}/api/listing-builder/seo-generate`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          keyword,
          productType: Array.from(productTypes)[0] || undefined,
          designAnalysis: {
            analysis: lifestyleAnalysis.analysis,
            keywords: lifestyleAnalysis.keywords,
            targetAudience: lifestyleAnalysis.targetAudience,
            productTypes: Array.from(productTypes),
            colors: productColors,
          },
          templateId: selectedListingTemplateId || undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok) { setSeoError(json.error || "SEO oluşturma hatası"); return; }
      const g = json.data?.generated;
      if (!g) { setSeoError("AI yanıtı parse edilemedi"); return; }
      setSeoContent({
        title: g.title || "",
        description: g.description || "",
        tags: Array.isArray(g.tags) ? g.tags.slice(0, 13) : [],
        titleAlternatives: Array.isArray(g.titleAlternatives) ? g.titleAlternatives : [],
      });
    } catch {
      setSeoError("Bağlantı hatası");
    } finally {
      setIsGeneratingSeo(false);
    }
  }

  // Generate a single lifestyle photo
  async function handleGenerateLifestyle(scene: string, index: number) {
    const url = designUrl || lightDesignUrl || darkDesignUrl;
    if (!url) return;
    const fullUrl = await getImageUrlForAI(url);

    setLifestyleResults(prev => {
      const next = [...prev];
      next[index] = { scene, loading: true, prompt: scene };
      return next;
    });

    try {
      const result = await apiClient.generateLifestyleImage(fullUrl, scene, lifestyleGenModel, {
        productTypes: Array.from(productTypes),
        colors: productColors,
      });
      setLifestyleResults(prev => {
        const next = [...prev];
        next[index] = { scene, prompt: scene, imageUrl: result.imageUrl ? (result.imageUrl.startsWith("http") ? result.imageUrl : `${env.API_URL}${result.imageUrl}`) : undefined };
        return next;
      });
    } catch (err: any) {
      setLifestyleResults(prev => {
        const next = [...prev];
        next[index] = { scene, prompt: scene, error: err.message || "Hata oluştu" };
        return next;
      });
    }
  }

  // Generate batch lifestyle photos for selected scenes
  async function handleBatchGenerate() {
    if (selectedScenes.size === 0 || !lifestyleAnalysis) return;
    setIsGeneratingBatch(true);
    const scenes = lifestyleAnalysis.lifestyleScenes;
    for (const idx of Array.from(selectedScenes)) {
      if (idx < scenes.length) {
        await handleGenerateLifestyle(scenes[idx], idx);
      }
    }
    setIsGeneratingBatch(false);
  }

  // Toggle scene selection
  function toggleSceneSelection(idx: number) {
    setSelectedScenes(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  // Select/deselect all scenes
  function toggleAllScenes() {
    if (!lifestyleAnalysis) return;
    if (selectedScenes.size === lifestyleAnalysis.lifestyleScenes.length) {
      setSelectedScenes(new Set());
    } else {
      setSelectedScenes(new Set(lifestyleAnalysis.lifestyleScenes.map((_, i) => i)));
    }
  }

  // Add image to product images collection. thumbUrl = small preview shown in the
  // grid (so scrolling doesn't re-decode the big full-res image); url = full image
  // used by the lightbox and the Listing Builder export.
  function addToProductImages(url: string, label: string, templateId?: string, thumbUrl?: string) {
    const id = `img_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setProductImages(prev => {
      if (prev.length >= 20) return prev; // Etsy max 20 images
      // Satan mockup zaten ANA GORSEL olarak ekliyse gruptaki kopyasini ekleme
      if (templateId?.startsWith("custom_")
        && prev.some((p) => p.templateId === `selling_${templateId.slice(7)}`)) return prev;
      // Don't add exact duplicate URLs (for lifestyle etc.)
      if (prev.some(p => p.url === url)) return prev;
      return [...prev, { id, url, label, templateId, thumbUrl }];
    });
  }

  // Downscale a full image data URL to a small JPEG thumbnail (for the grid).
  async function makeThumb(dataUrl: string, max = 400): Promise<string> {
    // Load directly (not via the shared cache) so the big bitmap isn't retained.
    const im = await new Promise<HTMLImageElement | null>((res) => {
      const i = new window.Image(); i.crossOrigin = "anonymous";
      i.onload = () => res(i); i.onerror = () => res(null); i.src = dataUrl;
    });
    if (!im || !im.naturalWidth) return dataUrl;
    const scale = Math.min(1, max / Math.max(im.naturalWidth, im.naturalHeight));
    const cw = Math.round(im.naturalWidth * scale), ch = Math.round(im.naturalHeight * scale);
    const canvas = document.createElement("canvas");
    canvas.width = cw; canvas.height = ch;
    const ctx = canvas.getContext("2d");
    if (!ctx) return dataUrl;
    ctx.drawImage(im, 0, 0, cw, ch);
    try { return canvas.toDataURL("image/jpeg", 0.82); } catch { return dataUrl; }
  }

  // Render ONE template to a PNG data URL off-screen, optionally with overridden
  // area positions (for re-editing a single product image). Mirrors the batch
  // generation render.
  async function renderTemplateToDataUrl(template: UnifiedTemplate, overrideAreas?: AreaRect[]): Promise<string | null> {
    let eff = template;
    if (overrideAreas && overrideAreas.length > 0) {
      const da = overrideAreas.map((a) => ({ id: a.id, x: a.x, y: a.y, width: a.w, height: a.h, rotation: a.rotation || 0, label: a.label, theme: a.theme, shape: a.shape, fit: a.fit, warp: a.warp, surfaceUrl: a.surfaceUrl }));
      const f = overrideAreas[0];
      eff = { ...template, designAreas: da, designArea: { x: f.x, y: f.y, width: f.w, height: f.h, rotation: f.rotation || 0 } };
    }
    const container = document.createElement("div");
    container.style.position = "fixed"; container.style.top = "-9999px";
    document.body.appendChild(container);
    const stage = new Konva.Stage({ container, width: eff.canvasWidth, height: eff.canvasHeight });
    const layer = new Konva.Layer(); stage.add(layer);
    try {
      if (eff.imageUrl) {
        await new Promise<void>((resolve) => {
          const bgImg = new window.Image(); bgImg.crossOrigin = "anonymous";
          bgImg.onload = () => { layer.add(new Konva.Image({ image: bgImg, width: eff.canvasWidth, height: eff.canvasHeight })); resolve(); };
          bgImg.onerror = () => resolve();
          bgImg.src = eff.imageUrl!.startsWith("http") ? eff.imageUrl! : `${API_BASE}${eff.imageUrl}`;
        });
      }
      if (usesAreaRender(eff)) {
        await addAreaDesignsToLayer(layer, eff);
      } else {
        const area0 = eff.designAreas?.[0];
        const url = area0 ? resolveAreaUrl(eff, area0) : (eff.theme === "dark" && darkDesignUrl ? darkDesignUrl : eff.theme === "light" && lightDesignUrl ? lightDesignUrl : designUrl);
        if (url) {
          const nat = await new Promise<{ w: number; h: number }>((res) => { const im = new window.Image(); im.crossOrigin = "anonymous"; im.onload = () => res({ w: im.naturalWidth, h: im.naturalHeight }); im.onerror = () => res({ w: 0, h: 0 }); im.src = url; });
          const fitted = fitDesignInArea(eff.designArea, nat.w, nat.h);
          await new Promise<void>((res) => { const im = new window.Image(); im.crossOrigin = "anonymous"; im.onload = () => { layer.add(new Konva.Image({ image: im, x: fitted.x, y: fitted.y, width: fitted.width, height: fitted.height, rotation: fitted.rotation, opacity: eff.opacity / 100 })); res(); }; im.onerror = () => res(); im.src = url; });
        }
      }
      await addForegroundToLayer(layer, eff);
      layer.draw();
      const exportPR = Math.min(1, 2000 / Math.max(eff.canvasWidth, eff.canvasHeight));
      return stage.toDataURL({ pixelRatio: exportPR, mimeType: "image/png" });
    } finally {
      stage.destroy(); document.body.removeChild(container);
    }
  }

  // "Alani Duzenle" -> frame-based MOCKUP area editor (persistent, from lightbox).
  const [productAreaEdit, setProductAreaEdit] = useState<{ productId: string; mockup: CustomMockup; template: UnifiedTemplate } | null>(null);
  // "Bu fotografta duzenle" -> frameless free design placement (this image only).
  const [freeEdit, setFreeEdit] = useState<{ productId: string; mockupImageUrl: string; designUrl: string; canvasW: number; canvasH: number; initial: { x: number; y: number; w: number; h: number; rotation: number }; opacity: number } | null>(null);

  function editLightboxArea() {
    if (!lightbox) return;
    const pi = productImages.find((p) => p.url === lightbox.urls[lightbox.index]);
    if (!pi?.templateId) return;
    const template = allTemplates.find((t) => t.id === pi.templateId);
    const mockup = customMockups.find((m) => `custom_${m.id}` === pi.templateId);
    if (!template || !mockup) return;
    setLightbox(null);
    setProductAreaEdit({ productId: pi.id, mockup, template });
  }

  // Save edited areas to the MOCKUP (persistent) and re-render this product image.
  async function applyMockupAreaEdit(areas: AreaRect[]) {
    if (!productAreaEdit) return;
    const { productId, mockup } = productAreaEdit;
    setProductAreaEdit(null);
    await saveMockupAreasStudio(mockup.id, areas);
    const reTemplate = customToUnified({ ...mockup, designAreas: areas as any } as CustomMockup);
    const dataUrl = await renderTemplateToDataUrl(reTemplate, areas);
    if (dataUrl) {
      const thumb = await makeThumb(dataUrl);
      setProductImages((prev) => prev.map((p) => p.id === productId ? { ...p, url: dataUrl, thumbUrl: thumb } : p));
    }
  }

  // Open the frameless editor for the current product image (single-area mockups).
  function editLightboxFree() {
    if (!lightbox) return;
    const pi = productImages.find((p) => p.url === lightbox.urls[lightbox.index]);
    if (!pi?.templateId) return;
    const template = allTemplates.find((t) => t.id === pi.templateId);
    if (!template || !template.isCustom || !template.imageUrl) return;
    const a = template.designAreas?.[0];
    if (!a) return;
    const dUrl = resolveAreaUrl(template, a) || lightDesignUrl || darkDesignUrl || designUrl;
    if (!dUrl) return;
    setLightbox(null);
    setFreeEdit({
      productId: pi.id,
      mockupImageUrl: template.imageUrl.startsWith("http") ? template.imageUrl : `${API_BASE}${template.imageUrl}`,
      designUrl: dUrl,
      canvasW: template.canvasWidth,
      canvasH: template.canvasHeight,
      initial: { x: a.x, y: a.y, w: a.width, h: a.height, rotation: a.rotation || 0 },
      opacity: template.opacity / 100,
    });
  }

  // Frameless edit result -> replace ONLY this product image (not persisted).
  async function applyFreeEdit(dataUrl: string) {
    if (!freeEdit) return;
    const { productId } = freeEdit;
    setFreeEdit(null);
    const thumb = await makeThumb(dataUrl);
    setProductImages((prev) => prev.map((p) => p.id === productId ? { ...p, url: dataUrl, thumbUrl: thumb } : p));
  }

  // Upload images from the computer straight into the product images grid.
  function handleProductImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const url = ev.target?.result as string;
        if (!url) return;
        const thumb = await makeThumb(url);
        addToProductImages(url, file.name.replace(/\.[^.]+$/, ""), undefined, thumb);
      };
      reader.readAsDataURL(file);
    });
    if (productImageInputRef.current) productImageInputRef.current.value = "";
  }

  // Check if a URL is already in product images
  function isInProductImages(url: string): boolean {
    return productImages.some(p => p.url === url);
  }

  /** Urun gorselinin tasarimini havuzda ileri/geri al (yerinde yeniden uretir) */
  const [poolBusy, setPoolBusy] = useState<string | null>(null);
  async function shiftPoolDesign(imgId: string, dir: 1 | -1) {
    const pi = productImages.find((p) => p.id === imgId);
    const t = pi?.templateId ? allTemplates.find((x) => x.id === pi.templateId) : undefined;
    if (!pi || !t || designPool.length < 2) return;
    const n = designPool.length;
    const start = (((pi.poolStart ?? 0) + dir) % n + n) % n;
    const pick = planPool([t], { [t.id]: start }).get(t.id);
    setPoolBusy(imgId);
    try {
      const r = await renderTemplateComposite(t, pick);
      if (!r) return;
      setProductImages((prev) => prev.map((p) => (p.id === imgId
        ? { ...p, url: r.url, thumbUrl: r.thumbUrl, poolStart: start, label: `${t.label} · ${designPool[start]?.label ?? ""}` }
        : p)));
    } finally { setPoolBusy(null); }
  }

  function removeProductImage(id: string) {
    setProductImages(prev => prev.filter(p => p.id !== id));
  }

  function reorderProductImages(from: number, to: number) {
    setProductImages((prev) => {
      if (from === to || from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  }


  function setAsMainImage(id: string) {
    setProductImages(prev => {
      const idx = prev.findIndex(p => p.id === id);
      if (idx <= 0) return prev;
      const next = [...prev];
      const [item] = next.splice(idx, 1);
      next.unshift(item);
      return next;
    });
  }

  // Ilham listesine satan mockup atanmissa, TASARIM o mockup'a yerlestirilip
  // urun gorsellerinin 1. sirasina (ana gorsel) konur. templateId "selling_<id>"
  // ile isaretlenir: tasarim degisince ayni oge yeniden uretilip yerinde guncellenir.
  // Tasarimin KENDI satan mockup'i once; yoksa ilham listesininki
  const [designSellingId, setDesignSellingId] = useState<string | null>(null);
  useEffect(() => {
    if (!sourceDesignId) { setDesignSellingId(null); return; }
    let off = false;
    apiClient.getDesign(sourceDesignId)
      .then((d) => { if (!off) setDesignSellingId(d.sellingMockupId ?? null); })
      .catch(() => { /* tasarim alinamazsa plan gecerli */ });
    return () => { off = true; };
  }, [sourceDesignId]);
  const sellingId = designSellingId || productionPlan?.sellingMockupId || null;
  const sellingMockupRec = sellingId ? customMockups.find((m) => m.id === sellingId) : undefined;
  // Ana gorsel uretilemedigi durumlar SESSIZ kalmamali: kullanici satan mockup
  // atiyor ama stüdyoda hicbir sey olmuyordu.
  const [sellingIssue, setSellingIssue] = useState<string | null>(null);
  useEffect(() => {
    if (!sellingId) { setSellingIssue(null); return; }
    const anyDesign = designUrl || lightDesignUrl || darkDesignUrl;
    if (!anyDesign) return;
    if (!sellingMockupRec) {
      // Liste henuz yuklenmemis olabilir; yuklendiyse gercekten yok demektir
      if (customMockups.length > 0) {
        setSellingIssue("Satan mockup bu mağazanın mockup listesinde bulunamadı — ana görsel üretilemedi.");
      }
      return;
    }
    const hasArea = (sellingMockupRec.designAreaW > 20 && sellingMockupRec.designAreaH > 20)
      || (Array.isArray((sellingMockupRec as unknown as { designAreas?: unknown[] }).designAreas)
        && ((sellingMockupRec as unknown as { designAreas: unknown[] }).designAreas.length > 0));
    if (!hasArea) {
      setSellingIssue(`"${sellingMockupRec.label}" mockup'ında baskı alanı tanımlı değil — tasarım yerleştirilemediği için ana görsel üretilmedi.`);
      return;
    }
    setSellingIssue(null);
    let cancelled = false;
    renderTemplateComposite(customToUnified(sellingMockupRec)).then((r) => {
      if (cancelled || !r) return;
      const heroTid = `selling_${sellingId}`;
      setProductImages((prev) => {
        // Satan mockup grubun icinde de varsa IKINCI kez eklenmesin: gruptaki
        // kopya cikarilir, ana gorsel (1. sira) onun yerini tutar.
        const rest = prev.filter((p) => p.templateId !== heroTid && p.templateId !== `custom_${sellingId}`);
        const hero = { id: `img_selling_${Date.now()}`, url: r.url, thumbUrl: r.thumbUrl, label: `Satan Mockup · ${r.label}`, templateId: heroTid };
        // Analizden gelen custom kolaj ANA gorsel kalir; satan mockup onun arkasina
        const imports = rest.filter((p) => p.templateId?.startsWith("import_"));
        const others = rest.filter((p) => !p.templateId?.startsWith("import_"));
        return [...imports.slice(0, 1), hero, ...imports.slice(1), ...others].slice(0, 20);
      });
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellingId, sellingMockupRec, customMockups.length, designUrl, lightDesignUrl, darkDesignUrl]);

  // Add generated lifestyle image to product images
  function addLifestyleToExport(imageUrl: string) {
    addToProductImages(imageUrl, "Lifestyle Foto");
  }

  // Video generation
  async function handleGenerateVideo() {
    if (productImages.length === 0) return;
    setVideoGenerating(true);
    setVideoError(null);
    setVideoUrl(null);

    try {
      // Collect image URLs - upload data URLs first, then resolve all to full URLs
      const imageUrls: string[] = [];
      for (const img of productImages) {
        let url = img.url;
        if (url.startsWith("data:")) {
          // Upload data URL to server first
          const uploaded = await uploadIfDataUrl(url);
          if (uploaded) url = uploaded;
          else continue; // skip if upload failed
        }
        if (url.startsWith("/")) url = `${env.API_URL}${url}`;
        if (url.startsWith("http")) imageUrls.push(url);
      }

      if (imageUrls.length === 0) {
        setVideoError("Görsel bulunamadı. Görselleri önce ekleyin.");
        setVideoGenerating(false);
        return;
      }

      const result = await apiClient.generateSlideshowVideo({
        imageUrls: imageUrls.slice(0, 6),
        aspectRatio: videoAspectRatio,
        duration: videoDuration,
        zoomPercent: videoZoom,
      });

      if (result.videoUrl) {
        const fullVideoUrl = result.videoUrl.startsWith("http")
          ? result.videoUrl
          : `${env.API_URL}${result.videoUrl}`;
        setVideoUrl(fullVideoUrl);
      } else {
        setVideoError("Video oluşturulamadı.");
      }
    } catch (err) {
      setVideoError(err instanceof Error ? err.message : "Video oluşturma hatası");
    } finally {
      setVideoGenerating(false);
    }
  }

  // Listen for Canva import from CanvaEditorPage
  useEffect(() => {
    function handleStorageChange(e: StorageEvent) {
      if (e.key === "canva_import_result" && e.newValue) {
        try {
          const { designUrl } = JSON.parse(e.newValue);
          if (designUrl) {
            setDesignUrl(designUrl);
            setCanvaDesignId(null);
            setCanvaEditUrl(null);
            sessionStorage.removeItem("canva_last_design");
            localStorage.removeItem("canva_import_result");
          }
        } catch { /* ignore */ }
      }
    }
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  async function handleSaveToDesigns() {
    const url = designUrl || lightDesignUrl || darkDesignUrl;
    if (!url) return;
    setIsSavingToDesigns(true);
    try {
      // Convert data URL or server URL to a File, then upload
      let file: File;
      if (url.startsWith("data:")) {
        const res = await fetch(url);
        const blob = await res.blob();
        file = new File([blob], `design-${Date.now()}.png`, { type: "image/png" });
      } else {
        const fullUrl = url.startsWith("http") ? url : `${API_BASE}${url}`;
        const res = await fetch(fullUrl);
        const blob = await res.blob();
        file = new File([blob], `design-${Date.now()}.png`, { type: blob.type || "image/png" });
      }
      await apiClient.uploadDesign(file, { source: "mockup-studio" });
      alert("Tasarım havuzuna kaydedildi!");
    } catch (err) {
      console.error("Save to designs failed:", err);
      alert("Tasarım kaydedilemedi: " + (err as Error).message);
    } finally {
      setIsSavingToDesigns(false);
    }
  }

  async function handleImportFromCanva() {
    if (!canvaDesignId) return;
    setIsImportingFromCanva(true);
    try {
      const apiBase = import.meta.env.VITE_API_URL || "";
      const token = localStorage.getItem("erc_token") || "";

      const exportRes = await fetch(`${apiBase}/api/canva/export-design`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ designId: canvaDesignId }),
      });
      const exportData = await exportRes.json();

      if (!exportRes.ok) {
        alert("Export hatası: " + (exportData.error || "Bilinmeyen hata"));
        return;
      }

      // Get the exported PNG URL
      const urls = exportData.data?.urls;
      const pngUrl = Array.isArray(urls) ? urls[0] : urls;
      if (!pngUrl) {
        alert("Export URL alınamadı");
        return;
      }

      // Fetch the exported image and set as design
      setDesignUrl(pngUrl);
      setCanvaDesignId(null);
      setCanvaEditUrl(null);
      alert("Canva'dan tasarım başarıyla alındı!");
    } catch (err) {
      console.error("Canva import failed:", err);
      alert("Canva'dan alınamadı: " + (err as Error).message);
    } finally {
      setIsImportingFromCanva(false);
    }
  }

  // Secili gruplari disa aktarim parcalarina bol: her grup KENDI gorselleriyle
  // ayri bir taslak uretir (kupa ile tisort ayni listede olamaz).
  // Hicbir gruba ait olmayan ekstra gorseller ilk parcaya eklenir.
  function buildExportBatches(): Array<{
    key: string; name: string; productTypeKey?: string; images: string[];
  }> {
    const imagesFor = (ids: Set<string>) =>
      productImages.filter((p) => p.templateId && ids.has(p.templateId)).map((p) => p.url);

    // Satan mockup (tasarim yerlestirilmis hali) uretilen listenin ANA GORSELI olur:
    // her parcanin gorsel listesinin BASINA eklenir (Etsy 1. gorsel = kapak).
    const heroItem = productImages.find((p) => p.templateId?.startsWith("selling_"));
    const heroUrl = heroItem?.url || null;

    if (activeCollections.length === 0) {
      const urls = productImages.filter((p) => p !== heroItem).map((p) => p.url);
      return [{
        key: "all",
        name: "Yeni Listing",
        images: heroUrl ? [heroUrl, ...urls] : urls,
      }];
    }

    const withHero = (urls: string[]) =>
      heroUrl && !urls.includes(heroUrl) ? [heroUrl, ...urls] : urls;

    const claimed = new Set<string>();
    const batches = activeCollections.map((c) => {
      const ids = new Set(c.mockups.map((m) => `custom_${m.id}`));
      ids.forEach((id) => claimed.add(id));
      return {
        key: c.id,
        name: c.name,
        productTypeKey: c.productTypeKey ?? undefined,
        images: withHero(imagesFor(ids)),
      };
    });

    const extras = productImages
      .filter((p) => p !== heroItem && (!p.templateId || !claimed.has(p.templateId)))
      .map((p) => p.url);
    if (extras.length && batches[0]) batches[0].images = [...batches[0].images, ...extras];

    return batches.filter((b) => b.images.length > 0);
  }

  // Arka planda Listing Builder'a aktar: gorseller+sablon sunucuda islenir,
  // yeni taslak olusturulur. Panelden cikmadan pes pese aktarabilirsin.
  // Birden fazla grup seciliyse HER GRUP icin ayri taslak olusur.
  async function exportToListingBackground(seoOverride?: { title: string; description: string; tags: string[] }) {
    const batches = buildExportBatches();
    for (const batch of batches) {
      await exportOneBatch(batch, seoOverride);
    }
  }

  async function exportOneBatch(
    batch: { key: string; name: string; productTypeKey?: string; images: string[] },
    seoOverride?: { title: string; description: string; tags: string[] },
  ) {
    const eff = seoOverride ?? seoContent;
    const jobId = `x_${Date.now()}_${Math.random().toString(36).slice(2, 6)}_${batch.key}`;
    const baseTitle = eff?.title || batch.name || "Yeni Listing";
    // Coklu grupta kuyrukta hangi urun grubu oldugu belli olsun
    const title = activeCollections.length > 1 ? `${batch.name} · ${baseTitle}` : baseTitle;
    setExportQueue((q) => [{ id: jobId, title, status: "uploading" as const }, ...q].slice(0, 10));
    try {
      const res = await fetch(`${API_BASE}/api/listing-drafts/from-studio`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          images: batch.images,
          productTypeKey: batch.productTypeKey,
          listingTemplateId: selectedListingTemplateId || undefined,
          designId: sourceDesignId || undefined,
          designSku: sourceDesignSku || undefined,
          mockupTemplates: [batch.name],
          title: eff?.title || undefined,
          description: eff?.description || undefined,
          tags: eff?.tags?.length ? eff.tags : undefined,
          videoUrl: videoUrl || undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Aktarılamadı");
      setExportQueue((q) => q.map((j) => (j.id === jobId ? { ...j, status: "done", draftId: json.data?.id } : j)));
    } catch (err) {
      setExportQueue((q) => q.map((j) => (j.id === jobId ? { ...j, status: "error", error: err instanceof Error ? err.message : "Hata" } : j)));
    }
  }

  // Video + Listing Builder'a aktar: TEK istek. Video SUNUCUDA uretilir, sonra
  // taslak olusturulur. Istek sunucuya ulastiktan sonra sayfayi kapatsan bile
  // sunucu isini tamamlar (video + taslak).
  async function handleVideoThenExport() {
    if (productImages.length === 0) return;
    const jobId = `xv_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const title = seoContent?.title || (activeCollection ? activeCollection.name : "Yeni Listing");
    setExportQueue((q) => [{ id: jobId, title: `🎬 ${title}`, status: "uploading" as const, video: true }, ...q].slice(0, 10));
    try {
      const res = await fetch(`${API_BASE}/api/listing-drafts/from-studio`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          images: productImages.map((img) => img.url),
          listingTemplateId: selectedListingTemplateId || undefined,
          designId: sourceDesignId || undefined,
          designSku: sourceDesignSku || undefined,
          mockupTemplates: activeCollection ? [activeCollection.name] : [],
          title: seoContent?.title || undefined,
          description: seoContent?.description || undefined,
          tags: seoContent?.tags?.length ? seoContent.tags : undefined,
          videoUrl: videoUrl || undefined,
          // Video sunucuda uretilsin (ffmpeg) — mevcut video ayarlariyla
          video: { aspectRatio: videoAspectRatio, duration: videoDuration, zoomPercent: videoZoom },
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Aktarılamadı");
      setExportQueue((q) => q.map((j) => (j.id === jobId ? { ...j, status: "done" as const, draftId: json.data?.id } : j)));
    } catch (err) {
      setExportQueue((q) => q.map((j) => (j.id === jobId ? { ...j, status: "error" as const, error: err instanceof Error ? err.message : "Hata" } : j)));
    }
  }

  // Asıl aktarım (denetimden sonra veya denetim gerekmiyorsa çağrılır)
  function runExport(seoOverride?: { title: string; description: string; tags: string[] }) {
    if (productImages.length === 0) return;
    const eff = seoOverride ?? seoContent;
    // Yeni liste: arka plan kuyruguna al (panelden cikmadan devam)
    if (!draftIdFromUrl) { void exportToListingBackground(eff ?? undefined); return; }
    setIsExportingToListing(true);

    // Pass images + video via window object (SPA - JS context survives navigation)
    // This avoids localStorage/sessionStorage size limits for data URLs
    const exportImages = productImages.map(img => ({ id: img.id, url: img.url }));
    (window as unknown as Record<string, unknown>).__studioExportImages = exportImages;
    (window as unknown as Record<string, unknown>).__studioExportDraftId = draftIdFromUrl || null;
    (window as unknown as Record<string, unknown>).__studioExportVideoUrl = videoUrl || null;
    (window as unknown as Record<string, unknown>).__studioExportListingTemplateId = selectedListingTemplateId || null;
    // Kaynak tasarim + kullanilan mockup sablon adlari (tasarim-listing baglama + SKU)
    (window as unknown as Record<string, unknown>).__studioExportDesignId = sourceDesignId || null;
    (window as unknown as Record<string, unknown>).__studioExportDesignSku = sourceDesignSku || null;
    // Tek tek mockup adlari yerine kullanilan mockup GRUBUNUN (collection) adini gonder
    (window as unknown as Record<string, unknown>).__studioExportMockupTemplates = activeCollections.map((c) => c.name);
    if (eff) {
      (window as unknown as Record<string, unknown>).__studioExportTitle = eff.title || null;
      (window as unknown as Record<string, unknown>).__studioExportDescription = eff.description || null;
      (window as unknown as Record<string, unknown>).__studioExportTags = eff.tags?.length ? eff.tags : null;
    }
    // Pass design analysis for AI SEO generation
    if (lifestyleAnalysis) {
      (window as unknown as Record<string, unknown>).__studioExportAnalysis = {
        analysis: lifestyleAnalysis.analysis,
        keywords: lifestyleAnalysis.keywords,
        targetAudience: lifestyleAnalysis.targetAudience,
        productTypes: Array.from(productTypes),
        colors: productColors,
      };
    }

    // Navigate directly to detail view
    if (draftIdFromUrl) {
      navigate(`/listing-builder?id=${draftIdFromUrl}`);
    } else {
      navigate("/listing-builder?new=true");
    }
  }

  // "Listing Builder'a Aktar" butonu: once tasarim ↔ baslik/aciklama/tag tutarliligini
  // dene; tasarim gorseli + baslik varsa denetim modali acilir, yoksa direkt aktar.
  async function handleExportToListing() {
    if (productImages.length === 0) return;
    const rawDesign = designUrl || lightDesignUrl || darkDesignUrl;
    if (rawDesign && seoContent?.title) {
      try {
        const usable = await getImageUrlForAI(rawDesign);
        setConsistencyImageUrl(usable);
        setShowConsistency(true);
        return;
      } catch { /* denetim kurulamadi -> direkt aktar */ }
    }
    runExport();
  }


  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Mockup Studio</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isConceptVertical
              ? "Takı mağazası seçili — bu ekran tekstil/hediyelik üretimi içindir"
              : "Tasarımını farklı ürünlere yerleştir, toplu mockup oluştur ve indir"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openDraftManager}
            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <FolderOpen className="h-4 w-4" />
            {draftSavedMsg || "Taslaklar"}
          </button>
          {/* Video + aktarim: iki adimi tek tikta yapar. Yeri sag ust kose —
              digerleriyle ayni yerde dursun, video panelinde gizli kalmasin. */}
          <button
            onClick={handleVideoThenExport}
            disabled={productImages.length === 0 || !selectedListingTemplateId}
            title={!selectedListingTemplateId
              ? "Önce listing şablonu seçin"
              : "Video oluştur, sonra arka planda Listing Builder kuyruğuna gönder"}
            className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-medium text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Film className="h-4 w-4" /> Video + Aktar
          </button>
          <button
            onClick={productImages.length > 0 && selectedListingTemplateId ? handleExportToListing : undefined}
            disabled={productImages.length === 0 || isExportingToListing || !selectedListingTemplateId}
            title={!selectedListingTemplateId ? "Önce listing şablonu seçin" : undefined}
            className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExportingToListing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            {isExportingToListing ? "Aktarılıyor..." : "Listing Builder'a Aktar"}
            {!isExportingToListing && productImages.length > 0 && <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs">{productImages.length}</span>}
            {!isExportingToListing && <ArrowRight className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Draft restore banner */}
      {showDraftBanner && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-amber-800">
            <Save className="h-4 w-4" />
            <span>Kaydedilmiş bir taslak bulundu. Kaldığı yerden devam etmek ister misiniz?</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleRestoreDraft} className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700">
              Geri Yükle
            </button>
            <button onClick={handleDismissDraft} className="rounded-lg border border-amber-300 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100">
              Sil
            </button>
          </div>
        </div>
      )}

      {/* Sunucu taslak yoneticisi */}
      {showDraftManager && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowDraftManager(false)}>
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <FolderOpen className="h-4 w-4" /> Taslaklar
              </h3>
              <button onClick={() => setShowDraftManager(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Yeni taslak kaydet */}
            <div className="border-b border-gray-100 px-5 py-4">
              <label className="mb-1.5 block text-[11px] font-medium text-gray-500">Bu çalışmayı kaydet</label>
              <div className="flex items-center gap-2">
                <input
                  value={draftNameInput}
                  onChange={(e) => setDraftNameInput(e.target.value)}
                  placeholder="Taslak adı"
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none"
                />
                <button
                  onClick={handleSaveServerDraft}
                  disabled={savingServerDraft || (!designUrl && !lightDesignUrl && !darkDesignUrl && productImages.length === 0)}
                  className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingServerDraft ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Kaydet
                </button>
              </div>
            </div>

            {/* Taslak listesi */}
            <div className="max-h-[50vh] overflow-y-auto px-5 py-4">
              {draftsLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : serverDrafts.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">Henüz kaydedilmiş taslak yok.</p>
              ) : (
                <div className="space-y-2">
                  {serverDrafts.map((d) => (
                    <div key={d.id} className="flex items-center gap-3 rounded-lg border border-gray-200 p-2.5 hover:bg-gray-50">
                      <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded bg-gray-100">
                        {d.thumbnailUrl && <img src={d.thumbnailUrl.startsWith("http") ? d.thumbnailUrl : `${API_BASE}${d.thumbnailUrl}`} alt="" className="h-full w-full object-cover" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-800">{d.name}</p>
                        <p className="text-[11px] text-gray-400">{new Date(d.updatedAt).toLocaleString("tr-TR")}</p>
                      </div>
                      <button
                        onClick={() => handleLoadServerDraft(d.id)}
                        disabled={loadingDraftId === d.id}
                        className="flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {loadingDraftId === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRight className="h-3 w-3" />}
                        Yükle
                      </button>
                      <button
                        onClick={() => handleDeleteServerDraft(d.id)}
                        className="rounded-lg border border-gray-200 p-1.5 text-red-500 hover:bg-red-50"
                        title="Sil"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-12 gap-5">
        {/* Left sidebar */}
        <div className="col-span-3 space-y-4">
          {/* Design upload */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Image className="h-3.5 w-3.5" />
              Tasarım Görseli
            </h3>

            <div className="space-y-2">
              <p className="text-[10px] text-gray-400">
                {singleAreaMode
                  ? "Açık ve koyu tasarım yükle."
                  : "Seçili şablonun alanlarına göre tasarım yükle (her alan + tema için bir slot)."}
              </p>
              {areaSlots.map((slot) => {
                const url = areaKeyDesigns[slot.key];
                const isDark = slot.theme === "dark";
                const sub = singleAreaMode ? "" : ` (${isDark ? "Koyu" : "Açık"})`;
                return (
                  <div key={slot.key} className="rounded-lg border border-gray-200 p-2">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      {isDark ? <Moon className="h-3 w-3 text-gray-500" /> : <Sun className="h-3 w-3 text-amber-500" />}
                      <span className="text-[10px] font-semibold text-gray-600">{slot.label}{sub}</span>
                      {singleAreaMode && (
                        <span className="ml-auto text-[9px] text-gray-400" title="Bu tasarım hangi renk ürüne basılacak">
                          {isDark ? "→ Açık tişört için" : "→ Koyu tişört için"}
                        </span>
                      )}
                    </div>
                    {url ? (
                      <div className="flex items-center gap-2">
                        <div className="rounded bg-gray-800 border border-gray-600 p-1 flex items-center justify-center" style={{ height: 44, width: 44 }}>
                          <img src={url} alt={slot.label} className="max-h-full max-w-full object-contain" />
                        </div>
                        <div className="flex-1 flex flex-col gap-1">
                          <button onClick={() => { setAreaUploadKey(slot.key); areaInputRef.current?.click(); }}
                            className="text-[10px] text-indigo-600 hover:text-indigo-700">Değiştir</button>
                          <button onClick={() => clearAreaKeyDesign(slot.key)}
                            className="text-[10px] text-red-500 hover:text-red-700">Kaldır</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => { setAreaUploadKey(slot.key); areaInputRef.current?.click(); }}
                        className={`flex w-full items-center justify-center gap-1.5 rounded border-2 border-dashed px-2 py-2 text-[10px] transition-colors ${isDark ? "border-gray-400 text-gray-600 hover:border-gray-500 hover:bg-gray-100" : "border-amber-300 text-amber-600 hover:border-amber-400 hover:bg-amber-50"}`}>
                        <Upload className="h-3 w-3" /> {slot.label}{sub} Yükle
                      </button>
                    )}
                  </div>
                );
              })}
              <div className="flex items-center gap-2 pt-1">
                <button onClick={openDesignPicker}
                  className="flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100">
                  <Image className="h-3.5 w-3.5" /> DB'den Tasarım Seç
                </button>
                {hasAnyDesign && (
                  <button onClick={clearAllDesigns}
                    className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 py-1 ml-auto">
                    <Trash2 className="h-3 w-3" /> Temizle
                  </button>
                )}
              </div>
            </div>
            <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleFileUpload} className="hidden" />
            <input ref={areaInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleAreaDesignUpload} className="hidden" />
            <input ref={multiAreaInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleMultiAreaUpload} className="hidden" />

            {showDesignPicker && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowDesignPicker(false)}>
                <div className="max-h-[80vh] w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
                    <h3 className="flex items-center gap-2 text-sm font-bold text-gray-900"><Image className="h-4 w-4 text-indigo-600" /> Kayıtlı Tasarımlarım</h3>
                    <div className="ml-auto mr-2">
                      <ChatGptFilterButton compact value={pickerChatgpt} onChange={(v) => { setPickerChatgpt(v); void loadPickerDesigns(v); }} />
                    </div>
                    <button onClick={() => setShowDesignPicker(false)} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"><X className="h-4 w-4" /></button>
                  </div>
                  <div className="max-h-[68vh] overflow-y-auto p-4">
                    {loadingMyDesigns ? (
                      <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Yükleniyor…</div>
                    ) : myDesigns.length === 0 ? (
                      <p className="py-16 text-center text-sm text-gray-400">{pickerChatgpt === "only" ? "ChatGPT ile oluşturulmuş tasarım yok." : "Kayıtlı tasarım yok. Bir analizde tasarım üret ya da yükle."}</p>
                    ) : (
                      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                        {myDesigns.map((d) => (
                          <button key={d.id} onClick={() => pickDbDesign(d)}
                            className="group overflow-hidden rounded-lg border border-gray-200 hover:border-indigo-400 hover:shadow">
                            <div className="aspect-square w-full bg-gray-50">
                              <img src={d.imageUrl?.startsWith("http") ? d.imageUrl : `${API_BASE}${d.imageUrl}`} alt="" loading="lazy" className="h-full w-full object-contain" />
                            </div>
                            {d.sku && <div className="truncate border-t border-gray-100 px-1.5 py-1 text-center font-mono text-[9px] text-gray-500">{d.sku}</div>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Custom Mockups */}
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <button onClick={() => setShowCustomTab(!showCustomTab)} className="flex w-full items-center justify-between p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                <ImagePlus className="h-3.5 w-3.5" />Özel Mockup'lar
              </h3>
              <div className="flex items-center gap-2">
                {customMockups.length > 0 && <span className="text-[10px] text-indigo-500 font-medium bg-indigo-50 px-1.5 py-0.5 rounded">{customMockups.length}</span>}
                {showCustomTab ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
              </div>
            </button>
            {showCustomTab && (
              <div className="px-4 pb-4 space-y-3">
                <button onClick={() => mockupFileInputRef.current?.click()} disabled={uploadingMockup}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 px-3 py-3 text-xs text-gray-500 hover:border-indigo-400 hover:text-indigo-600 disabled:opacity-50">
                  {uploadingMockup ? <><Loader2 className="h-4 w-4 animate-spin" />Yükleniyor...</> : <><Plus className="h-4 w-4" />Boş Mockup Yükle</>}
                </button>
                <input ref={mockupFileInputRef} type="file" accept="image/png,image/jpeg,image/webp" multiple onChange={handleMockupUpload} className="hidden" />
                {loadingCustom && <div className="flex items-center justify-center py-2"><Loader2 className="h-4 w-4 animate-spin text-gray-400" /></div>}
                {customMockups.length > 0 && (
                  <div className="space-y-1.5">
                    {customMockups.map((m) => {
                      const uid = `custom_${m.id}`; const isSel = selectedTemplates.has(uid);
                      const hasArea = m.designAreaW > 20 && m.designAreaH > 20;
                      return (
                        <div key={m.id} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition-colors ${!hasArea ? "bg-amber-50 text-amber-700 border border-amber-200 opacity-70" : isSel ? "bg-indigo-50 text-indigo-700 border border-indigo-200" : "bg-gray-50 text-gray-600 border border-gray-100 hover:bg-gray-100"}`}>
                          {hasArea ? (
                            <button onClick={() => toggleTemplate(uid)} className="flex-shrink-0">
                              {isSel ? <CheckSquare className="h-3.5 w-3.5 text-indigo-500" /> : <Square className="h-3.5 w-3.5 text-gray-400" />}
                            </button>
                          ) : (
                            <span className="flex-shrink-0 text-[9px] text-amber-500" title="Alan ayarlanmadı">⚠</span>
                          )}
                          <button onClick={() => hasArea ? activateTemplate(uid) : setConfiguringMockup(m)} className="flex-1 text-left truncate">
                            {m.label}
                            {!hasArea && <span className="text-[9px] ml-1 text-amber-500">(Alan ayarla)</span>}
                          </button>
                          <span className="text-[9px] text-gray-400">{m.theme === "dark" ? "D" : "L"}</span>
                          <button onClick={() => setConfiguringMockup(m)} className="p-0.5 hover:bg-indigo-100 rounded flex-shrink-0"><Settings2 className="h-3 w-3 text-indigo-500" /></button>
                          <button onClick={() => handleDeleteCustomMockup(m.id)} className="p-0.5 hover:bg-red-100 rounded flex-shrink-0"><Trash2 className="h-3 w-3 text-red-400" /></button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {!loadingCustom && customMockups.length === 0 && <p className="text-[10px] text-gray-400 text-center py-1">Henüz özel mockup yüklenmedi</p>}
              </div>
            )}
          </div>

          {/* Multi-area design assignment (active custom mockup with >1 area) */}
          {isMultiAreaTemplate(activeTemplateObj) && activeTemplateObj.customMockupId && (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 shadow-sm p-4 space-y-3">
              <h3 className="text-xs font-semibold text-indigo-600 uppercase tracking-wider flex items-center gap-2">
                <Grid3X3 className="h-3.5 w-3.5" />Alan Tasarımları
              </h3>
              <p className="text-[10px] text-gray-500">{activeTemplateObj.label} — boş bırakılan alanlar, alanın açık/koyu ayarına göre örnek tasarımla otomatik dolar. İstersen alana özel tasarım yükle.</p>
              <div className="space-y-2">
                {activeTemplateObj.designAreas!.map((a, idx) => {
                  const key = `${activeTemplateObj.customMockupId}__${a.id}`;
                  const assigned = areaDesignMap[key];
                  const resolved = resolveAreaUrl(activeTemplateObj, a);
                  const auto = !assigned && !!resolved; // filled from the themed sample
                  const named: Record<string, string> = { on: "Ön", arka: "Arka", "sol-kol": "Sol Kol", "sag-kol": "Sağ Kol", yaka: "Yaka", cep: "Cep" };
                  const m = /^alan-(\d+)$/.exec(a.label || "");
                  const name = named[a.label || ""] || (m ? `Alan ${m[1]}` : `Alan ${idx + 1}`);
                  return (
                    <div key={a.id} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-2">
                      <span className="text-[11px] font-medium text-gray-600 w-16 flex-shrink-0 flex items-center gap-1">
                        {a.theme === "dark" ? <Moon className="h-3 w-3 text-gray-500" /> : <Sun className="h-3 w-3 text-amber-500" />}
                        {name}
                      </span>
                      {resolved ? (
                        <img src={resolved} alt={name} className={`h-8 w-8 object-contain rounded border flex-shrink-0 ${auto ? "border-indigo-200 opacity-70" : "border-gray-100"}`} />
                      ) : (
                        <div className="h-8 w-8 rounded border border-dashed border-gray-300 flex-shrink-0" />
                      )}
                      <button onClick={() => { setMultiAreaTarget({ mockupId: activeTemplateObj.customMockupId!, areaId: a.id }); multiAreaInputRef.current?.click(); }}
                        className="flex-1 text-[11px] text-indigo-600 hover:text-indigo-700 text-left">
                        {assigned ? "Değiştir" : auto ? "Otomatik (değiştir)" : "Tasarım yükle"}
                      </button>
                      {assigned && (
                        <button onClick={() => setAreaDesignMap((prev) => { const n = { ...prev }; delete n[key]; return n; })}
                          className="p-0.5 hover:bg-red-100 rounded flex-shrink-0"><X className="h-3 w-3 text-red-400" /></button>
                      )}
                    </div>
                  );
                })}
              </div>
              <button onClick={() => autoGenerateProductImages([activeTemplateObj.id])}
                disabled={isAddingToImages || !activeTemplateObj.designAreas!.some(a => resolveAreaUrl(activeTemplateObj, a))}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                {isAddingToImages ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Görsele Ekle
              </button>
            </div>
          )}

          {/* Groups - show collections as checkboxes */}
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <button onClick={() => setExpandedGroups(!expandedGroups)} className="flex w-full items-center justify-between p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2"><Package className="h-3.5 w-3.5" />Mockup Grupları</h3>
              <div className="flex items-center gap-2">
                {collections.length > 0 && <span className="text-[10px] text-indigo-500 font-medium bg-indigo-50 px-1.5 py-0.5 rounded">{collections.length}</span>}
                {expandedGroups ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
              </div>
            </button>
            {expandedGroups && (
              <div className="px-4 pb-4 space-y-1.5">
                {collections.length > 0 ? collections.map((c) => {
                  const isActive = selectedCollectionIds.includes(c.id);
                  const order = selectedCollectionIds.indexOf(c.id) + 1;
                  const pt = productTypeProfiles.find((p) => p.key === c.productTypeKey);
                  return (
                    <div key={c.id}
                      className={`rounded-lg border px-3 py-2 transition-colors ${isActive ? "bg-indigo-50 border-indigo-300 ring-1 ring-indigo-200" : "bg-gray-50 border-gray-100"}`}>
                      <button onClick={() => selectCollection(c)}
                        className={`flex w-full items-center gap-2 text-xs font-medium ${isActive ? "text-indigo-700" : "text-gray-600"}`}>
                        {isActive ? <CheckSquare className="h-3.5 w-3.5 text-indigo-500" /> : <Square className="h-3.5 w-3.5 text-gray-400" />}
                        {c.name}
                        {/* Coklu secimde uretim/yukleme sirasi */}
                        {isActive && selectedCollectionIds.length > 1 && (
                          <span className="rounded bg-indigo-200 px-1 text-[9px] font-bold text-indigo-800">{order}</span>
                        )}
                        <span className="ml-auto text-[10px] text-gray-400">{c.mockups.length}</span>
                      </button>
                      {/* Urun tipi: bu grup hangi urunun mockup'lari. Coklu grup
                          uretiminde her grup KENDI tipiyle ayri taslak olur. */}
                      <div className="mt-1 flex items-center gap-1">
                        <select
                          value={c.productTypeKey ?? ""}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setCollectionProductType(c.id, e.target.value)}
                          className={`w-full rounded border px-1.5 py-0.5 text-[10px] ${pt ? "border-gray-200 text-gray-600" : "border-amber-300 bg-amber-50 text-amber-700"}`}
                          title="Bu grubun ürün tipi — üretilen taslak bu tipe bağlanır"
                        >
                          <option value="">Ürün tipi atanmamış</option>
                          {productTypeProfiles.map((p) => (
                            <option key={p.key} value={p.key}>{p.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                }) : (
                  <p className="text-[10px] text-gray-400 text-center py-2">Mockup Yönetimi'nden koleksiyon oluşturun</p>
                )}
              </div>
            )}
          </div>

          {/* Batch Actions - Compact */}
          <div className="space-y-1.5">
            {designPool.length > 1 && (
              <div className="rounded-lg border border-fuchsia-200 bg-fuchsia-50/60 p-2 text-[11px]">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-fuchsia-800">{designPool.length} tasarım</span>
                  <select value={poolMode} onChange={(e) => setPoolMode(e.target.value as "cycle" | "random" | "single")}
                    className="rounded border border-fuchsia-200 bg-white px-1 py-0.5">
                    <option value="cycle">Sırayla dağıt</option>
                    <option value="random">Karışık dağıt</option>
                    <option value="single">Tek tasarım</option>
                  </select>
                  {poolMode === "random" && (
                    <button onClick={() => setPoolSeed((x) => x + 1)} className="rounded border border-fuchsia-200 bg-white px-1.5 py-0.5 hover:bg-fuchsia-50">Karıştır</button>
                  )}
                </div>
                <p className="mt-1 truncate text-gray-600" title={designPool.map((d) => d.label).join(" · ")}>{designPool.map((d) => d.label).join(" · ")}</p>
                <p className="text-gray-400">Tek alanlı mockuplar sırayla, çok alanlıda alanlar sırayla; görsellerde ◀ ▶ ile değiştir.</p>
              </div>
            )}
            <button onClick={handleBatchAddToImages} disabled={(!designUrl && !lightDesignUrl && !darkDesignUrl) || selectedTemplates.size === 0 || isAddingToImages}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-green-600 px-3 py-2 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50 transition-colors">
              {isAddingToImages ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Görsellere Ekle ({selectedTemplates.size})
            </button>
            <div className="flex gap-1.5">
              <button onClick={handleBatchExport} disabled={(!designUrl && !lightDesignUrl && !darkDesignUrl) || selectedTemplates.size === 0 || isExporting}
                className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-indigo-600 px-2 py-1.5 text-[10px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                {isExporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <DownloadCloud className="h-3 w-3" />}
                İndir
              </button>
              <button onClick={handleSaveToDesigns} disabled={(!designUrl && !lightDesignUrl && !darkDesignUrl) || isSavingToDesigns}
                className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-violet-600 px-2 py-1.5 text-[10px] font-medium text-white hover:bg-violet-700 disabled:opacity-50 transition-colors">
                {isSavingToDesigns ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                Tasarımlara Kaydet
              </button>
            </div>
          </div>

          {/* Listing template to apply when transferring to the builder */}
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-3">
            <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
              <FileText className="h-3 w-3" /> Listing Şablonu
            </label>
            <select value={selectedListingTemplateId}
              onChange={(e) => {
                setSelectedListingTemplateId(e.target.value);
                setTemplateNeedsPick(!e.target.value);
                try { if (e.target.value) localStorage.setItem("erc_last_listing_template", e.target.value); } catch { /* ignore */ }
              }}
              className={`w-full text-xs border rounded-lg px-2 py-2 bg-white focus:ring-2 focus:ring-emerald-200 focus:outline-none ${
                selectedListingTemplateId ? "border-gray-200" : "border-amber-300 bg-amber-50"
              }`}>
              <option value="">Şablon seçin…</option>
              {listingTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {!selectedListingTemplateId && (
              <p className="mt-1 text-[10px] font-medium text-amber-700">
                {templateNeedsPick ? "Mockup grubu değişti — şablonu yeniden seçin." : "Aktarım için şablon seçin."}
              </p>
            )}
            <p className="text-[9px] text-gray-400 mt-1">
              Aktarınca builder'da otomatik uygulanır.
              {templateImageCount > 0 && ` Şablon  görsel ekler (size chart vb.).`}
            </p>
          </div>

          {/* Kişiselleştirme alanları (kaynak analiz — düzenlenebilir) */}
          {analysisIdFromUrl && persoFields.length > 0 && (
            <div className="rounded-xl border border-pink-200 bg-pink-50/40 shadow-sm p-3">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[10px] font-semibold text-pink-700 uppercase tracking-wider">Kişiselleştirme ({persoFields.length})</label>
                {persoDirty && (
                  <button onClick={savePersoFields} disabled={persoSaving}
                    className="rounded bg-pink-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-pink-700 disabled:opacity-50">
                    {persoSaving ? "…" : "Kaydet"}
                  </button>
                )}
              </div>
              <PersonalizationFieldsEditor fields={persoFields} onChange={(f) => { setPersoFields(f); setPersoDirty(true); }} />
              <p className="text-[9px] text-gray-400 mt-1">Aktarınca listing'e otomatik uygulanır.</p>
            </div>
          )}

          {/* Canva'dan Al banner */}
          {canvaDesignId && (
            <div className="rounded-xl border-2 border-[#7B2FF2] bg-purple-50 p-3 space-y-2">
              <div className="flex items-center gap-2 text-xs text-purple-700 font-medium">
                <ExternalLink className="h-3.5 w-3.5" />
                Canva'da düzenleniyor
              </div>
              <button
                onClick={handleImportFromCanva}
                disabled={isImportingFromCanva}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#7B2FF2] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#6B21D6] disabled:opacity-50 transition-colors"
              >
                {isImportingFromCanva ? <><Loader2 className="h-4 w-4 animate-spin" />Alınıyor...</> : <><Download className="h-4 w-4" />Canva'dan Al</>}
              </button>
              {canvaEditUrl && (
                <button
                  onClick={() => window.open(canvaEditUrl, "_blank")}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-purple-300 px-4 py-2 text-xs font-medium text-purple-700 hover:bg-purple-100 transition-colors"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Canva'ya Dön
                </button>
              )}
              <button
                onClick={() => { setCanvaDesignId(null); setCanvaEditUrl(null); }}
                className="flex w-full items-center justify-center gap-1 text-[10px] text-gray-400 hover:text-gray-600"
              >
                <X className="h-3 w-3" /> Kapat
              </button>
            </div>
          )}
        </div>

        {/* Center - Product Images */}
        <div className="col-span-5">
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Image className="h-4 w-4" />Ürün Görselleri
                {productImages.length > 0 && (
                  <span
                    title={templateImageCount > 0
                      ? `${productImages.length} ürün görseli + ${templateImageCount} şablon görseli (size chart vb.)`
                      : "Etsy en fazla 20 görsel kabul eder"}
                    className={`rounded px-2 py-0.5 text-xs font-medium ${
                      overImageLimit ? "bg-red-50 text-red-600" : "bg-indigo-50 text-indigo-500"
                    }`}
                  >
                    {totalImageCount}/20
                    {templateImageCount > 0 && (
                      <span className="ml-1 font-normal opacity-70">
                        ({productImages.length}+{templateImageCount})
                      </span>
                    )}
                  </span>
                )}
                {isAddingToImages && <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />}
              </h3>
              {productImages.length > 0 && (
                <div className="flex items-center gap-2">
                  <button onClick={handleBatchExport} disabled={productImages.length === 0 || isExporting}
                    className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 bg-indigo-50 px-3 py-1.5 rounded-lg">
                    {isExporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <DownloadCloud className="h-3.5 w-3.5" />}
                    Tümünü İndir
                  </button>
                  <button onClick={() => setProductImages([])} className="text-xs text-gray-400 hover:text-red-500 px-2 py-1">Temizle</button>
                </div>
              )}
            </div>

            {sellingIssue && (
              <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <span>{sellingIssue}</span>
                <Link to="/mockup-manager" target="_blank" rel="noopener noreferrer" className="font-medium text-amber-900 underline">
                  Mockup Yönetimi'nde alan belirle
                </Link>
              </div>
            )}
            {overImageLimit && (
              <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                Toplam {totalImageCount} görsel — Etsy en fazla 20 kabul eder.
                {templateImageCount > 0 && ` Şablon ${templateImageCount} görsel ekliyor (size chart vb.).`}
                {" "}Fazlası aktarımda düşülür; {totalImageCount - 20} görsel çıkarın.
              </div>
            )}
            {productImages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                <Image className="h-12 w-12 text-gray-300 mb-3" />
                <p className="text-sm text-gray-500 font-medium">Henüz ürün görseli eklenmedi</p>
                <p className="text-xs text-gray-400 mt-1 max-w-sm">Sol panelden tasarım yükleyin ve mockup grubu seçin.<br/>Seçili mockup'lar otomatik olarak buraya eklenecek.</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2.5">
                {productImages.map((img, idx) => (
                  <div key={img.id}
                    draggable
                    onDragStart={() => setDragImageIdx(idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { if (dragImageIdx !== null) reorderProductImages(dragImageIdx, idx); setDragImageIdx(null); }}
                    onDragEnd={() => setDragImageIdx(null)}
                    onClick={() => openLightbox(productImages.map((p) => p.url), idx)}
                    title="Tam ekran göster (sürükleyerek sırala)"
                    className={`relative group rounded-lg overflow-hidden border-2 transition-all cursor-zoom-in active:cursor-grabbing ${dragImageIdx === idx ? "opacity-40" : ""} ${idx === 0 ? "border-amber-400 ring-2 ring-amber-100" : "border-gray-200 hover:border-gray-300"}`}>
                    <div className="aspect-square bg-gray-50">
                      <img src={img.thumbUrl || img.url} alt={img.label} decoding="async" className="w-full h-full object-cover pointer-events-none" />
                    </div>
                    {idx === 0 ? (
                      <div className="absolute top-1 left-1 bg-amber-400 text-white text-[8px] font-bold px-1 py-0.5 rounded flex items-center gap-0.5">
                        <Star className="h-2 w-2" />1
                      </div>
                    ) : (
                      <div className="absolute top-1 left-1 bg-black/60 text-white text-[8px] font-bold px-1.5 py-0.5 rounded">
                        {idx + 1}
                      </div>
                    )}
                    {/* Coklu tasarim: bu mockup'in tasarimini degistir */}
                    {designPool.length > 1 && img.templateId && allTemplates.some((x) => x.id === img.templateId) && (
                      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-black/55 px-1 py-0.5 text-[10px] text-white"
                        onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => void shiftPoolDesign(img.id, -1)} disabled={poolBusy === img.id}
                          className="rounded px-1 hover:bg-white/20" title="Önceki tasarım">◀</button>
                        <span className="min-w-0 flex-1 truncate text-center">
                          {poolBusy === img.id ? "…" : designPool[img.poolStart ?? 0]?.label ?? ""}
                        </span>
                        <button onClick={() => void shiftPoolDesign(img.id, 1)} disabled={poolBusy === img.id}
                          className="rounded px-1 hover:bg-white/20" title="Sonraki tasarım">▶</button>
                      </div>
                    )}
                    {/* Corner actions (reorder by drag) */}
                    <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {idx > 0 && (
                        <button onClick={(e) => { e.stopPropagation(); setAsMainImage(img.id); }} className="p-1 bg-amber-400/90 rounded shadow hover:bg-amber-500" title="Ana görsel yap">
                          <Star className="h-3.5 w-3.5 text-white" />
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); removeProductImage(img.id); }} className="p-1 bg-red-500/90 rounded shadow hover:bg-red-600" title="Kaldır">
                        <X className="h-3.5 w-3.5 text-white" />
                      </button>
                    </div>
                  </div>
                ))}
                {productImages.length < 20 && (
                  <button type="button" onClick={() => productImageInputRef.current?.click()}
                    title="Bilgisayardan görsel ekle"
                    className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-1 text-gray-400 text-[10px] hover:border-indigo-400 hover:text-indigo-500 hover:bg-indigo-50/50 transition-colors">
                    <Plus className="h-5 w-5" />
                    {20 - productImages.length} boş
                  </button>
                )}
              </div>
            )}
            <input ref={productImageInputRef} type="file" accept="image/png,image/jpeg,image/webp" multiple onChange={handleProductImageUpload} className="hidden" />
          </div>
        </div>

        {/* Right - Template Grid */}
        <div className="col-span-4">
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2"><Grid3X3 className="h-3.5 w-3.5" />{activeCollections.length > 0 ? activeCollections.map((c) => c.name).join(" + ") : "Templateler"}</h3>
              {/* Acarken havuzu TAZELE: baska sekmede uretilen/yuklenen mockuplar
                  sayfa yenilenmeden gorunmuyordu. */}
              <button onClick={() => {
                setShowAddMockups((s) => {
                  if (!s) { void fetchCustomMockups(); void fetchMockupFolders(); }
                  return !s;
                });
              }}
                className={`flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-lg border transition-colors ${showAddMockups ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-indigo-600 border-indigo-200 hover:bg-indigo-50"}`}>
                <Plus className="h-3 w-3" /> Mockup Ekle
              </button>
            </div>
            <div className="space-y-4 max-h-[calc(100vh-180px)] overflow-y-auto pr-1">
              {customTemplates.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <FolderOpen className="h-10 w-10 text-gray-300 mb-3" />
                  <p className="text-sm text-gray-500 font-medium mb-1">Henüz mockup template eklenmedi</p>
                  <p className="text-xs text-gray-400 mb-4">Mockup Yönetimi sayfasından kendi template'lerinizi yükleyin</p>
                  <a href="/mockup-manager" className="inline-flex items-center gap-2 rounded-lg bg-indigo-50 px-4 py-2 text-xs font-medium text-indigo-600 hover:bg-indigo-100 transition-colors">
                    Mockup Yönetimi'ne Git
                  </a>
                </div>
              ) : (
                <>
                  {/* Active selection grid (collection mockups + extras) */}
                  {gridTemplates.length > 0 ? (
                    <div>
                      <p className="text-[10px] font-semibold text-indigo-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                        <FolderOpen className="h-3 w-3" />{activeCollections.length > 1 ? `${activeCollections.length} Grup Mockup'ları` : activeCollection ? "Koleksiyon Mockup'ları" : "Seçili Mockup'lar"} ({gridTemplates.length})
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {gridTemplates.map((t) => {
                          const cm = customMockups.find((m) => `custom_${m.id}` === t.id);
                          const thumbDesign = t.theme === "dark" && darkDesignUrl ? darkDesignUrl : t.theme === "light" && lightDesignUrl ? lightDesignUrl : designUrl;
                          return (
                            <MockupThumbnail key={t.id} template={t} designUrl={thumbDesign}
                              areaUrls={(t.designAreas ?? []).map((a) => resolveAreaUrl(t, a))}
                              selected={selectedTemplates.has(t.id)} onToggle={() => toggleTemplate(t.id)}
                              onActivate={() => activateTemplate(t.id)} isActive={activeTemplate === t.id}
                              onConfigure={cm ? () => setConfiguringMockup(cm) : undefined} onDelete={cm ? () => handleDeleteCustomMockup(cm.id) : undefined}
                              onAddToImages={() => void addSingleToProductImages(t.id)}
                              added={productImages.some((p) => p.templateId === t.id)} />
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50/50 py-8 text-center">
                      <Package className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-xs text-gray-500 font-medium">Soldan bir mockup grubu (şablon) seçin</p>
                      <p className="text-[10px] text-gray-400 mt-1">veya "Mockup Ekle" ile tek tek ekleyin</p>
                    </div>
                  )}

                </>
              )}
            </div>
          </div>

          {/* Ilham alinan liste: uretilen gorsellerle KARSILASTIRMA icin urun
              gorsellerinin hemen altinda durur. */}
          {sourceListing && (
            <div className="mt-3">
              <SourceListingCard source={sourceListing} />
            </div>
          )}
        </div>
      </div>

      {/* Row 2: Lifestyle + Video - Full Width */}
      <div className="grid grid-cols-12 gap-5 mt-5">
        {/* Lifestyle Photo - Wide */}
        <div className="col-span-8">
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex w-full items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                <Camera className="h-3.5 w-3.5" />LiFESTYLE FOTO
              </h3>
              <div className="flex items-center gap-1.5">
                {autoFilled && <span className="text-[10px] text-indigo-600 font-medium bg-indigo-50 px-1.5 py-0.5 rounded">Analizden dolduruldu</span>}
                {lifestyleAnalysis && <span className="text-[10px] text-emerald-500 font-medium bg-emerald-50 px-1.5 py-0.5 rounded">Analiz Tamam</span>}
              </div>
            </div>
            {showLifestyle && (
              <div className="px-4 pb-4 space-y-3">
                {/* Product Config - always visible */}
                <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-3 space-y-2.5">
                  <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Ürün Ayarları</p>
                  {/* Product Types - multi select */}
                  <div>
                    <span className="text-[10px] text-gray-500 block mb-1">Ürün Tipi:</span>
                    <div className="flex flex-wrap gap-1.5">
                      {PRODUCT_TYPE_OPTIONS.map(opt => {
                        const sel = productTypes.has(opt.id);
                        return (
                          <button key={opt.id} onClick={() => {
                            setProductTypes(prev => {
                              const next = new Set(prev);
                              if (next.has(opt.id)) { if (next.size > 1) next.delete(opt.id); }
                              else next.add(opt.id);
                              return next;
                            });
                          }}
                            className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${sel ? "bg-indigo-100 border-indigo-300 text-indigo-700 font-medium" : "bg-white border-gray-200 text-gray-500 hover:bg-gray-100"}`}>
                            {sel && <span className="mr-0.5">✓</span>}{opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Print Areas */}
                  <div>
                    <span className="text-[10px] text-gray-500 block mb-1">Baskı Alanı:</span>
                    <div className="flex flex-wrap gap-1.5">
                      {PRINT_AREA_OPTIONS.map(opt => {
                        const sel = printAreas.has(opt.id);
                        return (
                          <button key={opt.id} onClick={() => {
                            setPrintAreas(prev => {
                              const next = new Set(prev);
                              if (next.has(opt.id)) { if (next.size > 1) next.delete(opt.id); }
                              else next.add(opt.id);
                              return next;
                            });
                          }}
                            className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${sel ? "bg-emerald-100 border-emerald-300 text-emerald-700 font-medium" : "bg-white border-gray-200 text-gray-500 hover:bg-gray-100"}`}>
                            {sel && <span className="mr-0.5">✓</span>}{opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Colors */}
                  <div>
                    <span className="text-[10px] text-gray-500 block mb-1">Ürün Renkleri:</span>
                    <div className="flex flex-wrap gap-1">
                      {COLOR_OPTIONS.map(c => {
                        const sel = productColors.includes(c);
                        const colorMap: Record<string, string> = { white: "#fff", black: "#000", cream: "#FFFDD0", sand: "#C2B280", gray: "#9CA3AF", navy: "#1E3A5F", sage: "#9DC183", pink: "#F9A8D4", red: "#EF4444", "royal-blue": "#4169E1" };
                        return (
                          <button key={c} onClick={() => {
                            setProductColors(prev => prev.includes(c) ? (prev.length > 1 ? prev.filter(x => x !== c) : prev) : [...prev, c]);
                          }}
                            className={`w-6 h-6 rounded-full border-2 transition-all ${sel ? "border-indigo-500 ring-2 ring-indigo-200 scale-110" : "border-gray-300 hover:border-gray-400"}`}
                            style={{ backgroundColor: colorMap[c] || c }}
                            title={c} />
                        );
                      })}
                    </div>
                  </div>
                  {/* Design variants info */}
                  {(lightDesignUrl || darkDesignUrl) && (
                    <div className="flex items-center gap-2 text-[9px] text-gray-500 bg-white rounded px-2 py-1 border border-gray-200">
                      {lightDesignUrl && <span className="flex items-center gap-0.5"><Sun className="h-3 w-3 text-amber-500" />Açık tasarım</span>}
                      {darkDesignUrl && <span className="flex items-center gap-0.5"><Moon className="h-3 w-3 text-indigo-500" />Koyu tasarım</span>}
                      <span>→ Renk kontrastı otomatik uygulanacak</span>
                    </div>
                  )}
                  {/* AI modelleri — analiz ve lifestyle üretimi AYRI, HER ZAMAN görünür (aiModels yüklenmese de alan durur) */}
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div>
                      <span className="text-[10px] text-gray-500 block mb-1">Analiz Modeli:</span>
                      <select value={lifestyleModel} onChange={e => setLifestyleModel(e.target.value)}
                        className="w-full text-[10px] rounded border border-gray-200 px-2 py-1.5 bg-white">
                        {!aiModels && <option value={lifestyleModel}>Yükleniyor…</option>}
                        {aiModels?.vision.map(m => <option key={m.id} value={m.id}>{modelLabel(m)}</option>)}
                      </select>
                    </div>
                    <div>
                      <span className="text-[10px] text-gray-500 block mb-1">Lifestyle Görsel Modeli:</span>
                      <select value={lifestyleGenModel} onChange={e => setLifestyleGenModel(e.target.value)}
                        className="w-full text-[10px] rounded border border-gray-200 px-2 py-1.5 bg-white">
                        {!aiModels && <option value={lifestyleGenModel}>Yükleniyor…</option>}
                        {aiModels?.imageGen.map(m => <option key={m.id} value={m.id}>{modelLabel(m)}</option>)}
                      </select>
                    </div>
                  </div>
                  {aiModelsError && (
                    <p className="text-[9px] text-red-500">Model listesi yüklenemedi ({aiModelsError}). Sayfayı yenileyin.</p>
                  )}
                </div>

                {!lifestyleAnalysis ? (
                  <div className="space-y-2">
                    <button onClick={handleAnalyzeDesign} disabled={isAnalyzing || (!designUrl && !lightDesignUrl && !darkDesignUrl)}
                      className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-purple-500 to-indigo-500 px-3 py-2.5 text-xs font-medium text-white hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 transition-all">
                      {isAnalyzing ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Analiz Ediliyor...</> : <><Sparkles className="h-3.5 w-3.5" />Tasarımı Analiz Et</>}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="rounded-lg bg-gray-50 p-2.5 space-y-1.5">
                      <p className="text-[10px] text-gray-700 leading-relaxed line-clamp-3">{lifestyleAnalysis.analysis}</p>
                      {lifestyleAnalysis.keywords.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {lifestyleAnalysis.keywords.slice(0, 8).map((kw, i) => (
                            <span key={i} className="text-[9px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded">{kw}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* SEO/GEO title + description (Etsy 2026 rules) */}
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-2.5 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider flex items-center gap-1">
                          <FileText className="h-3 w-3" /> Etsy Başlık & Açıklama (SEO/GEO)
                        </p>
                        <button onClick={handleGenerateSeo} disabled={isGeneratingSeo}
                          className="flex items-center gap-1 text-[10px] font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-2 py-1 rounded disabled:opacity-50">
                          {isGeneratingSeo ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                          {seoContent ? "Yeniden" : "Oluştur"}
                        </button>
                      </div>
                      {seoError && <p className="text-[10px] text-red-500">{seoError}</p>}
                      {showCleanComfortColors && (
                        <button onClick={cleanComfortColors}
                          title="Seçili listing şablonu Comfort Colors değil ama SEO'da 'Comfort Colors' geçiyor — başlık/açıklama/etiketlerden temizle"
                          className="w-full flex items-center justify-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 border border-amber-300 px-2 py-1.5 rounded">
                          <Trash2 className="h-3 w-3" /> "Comfort Colors" kelimelerini temizle (ürün CC değil)
                        </button>
                      )}
                      {seoContent ? (
                        <div className="space-y-2">
                          <div>
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="text-[9px] font-medium text-gray-500 uppercase">Başlık</span>
                              <span className={`text-[9px] ${seoContent.title.length > 140 ? "text-red-500 font-bold" : "text-gray-400"}`}>{seoContent.title.length}/140</span>
                            </div>
                            <textarea readOnly value={seoContent.title} rows={2}
                              className="w-full text-[11px] text-gray-800 bg-white border border-gray-200 rounded px-2 py-1 resize-none" />
                          </div>
                          <div>
                            <span className="text-[9px] font-medium text-gray-500 uppercase block mb-0.5">Açıklama</span>
                            <textarea readOnly value={seoContent.description} rows={6}
                              className="w-full text-[10px] text-gray-700 bg-white border border-gray-200 rounded px-2 py-1 resize-none leading-relaxed" />
                          </div>
                          {seoContent.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {seoContent.tags.map((t, i) => (
                                <span key={i} className="text-[9px] bg-white border border-emerald-200 text-emerald-700 px-1.5 py-0.5 rounded">{t}</span>
                              ))}
                            </div>
                          )}
                          <p className="text-[9px] text-gray-400">Aktarınca builder'da başlık/açıklama/etiket otomatik dolar.</p>
                        </div>
                      ) : (
                        <p className="text-[10px] text-gray-500">Analiz edilen tasarıma göre Etsy kurallarına uygun başlık, açıklama ve 13 etiket üretir.</p>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <button onClick={() => { setLifestyleAnalysis(null); setLifestyleResults([]); }}
                        className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-700">
                        <RefreshCw className="h-3 w-3" />Yeniden Analiz Et
                      </button>
                    </div>

                    {/* Scene header with actions */}
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] font-semibold text-gray-600">Sahne Önerileri:{autoFilled && <span className="ml-1 font-normal text-indigo-500" title="Ürün analizindeki hedef kitle, niş ve tema verilerinden üretildi (ek AI kullanılmadı). Daha isabetli sahneler için 'Yeniden Analiz Et'.">· analizden</span>}</p>
                      <div className="flex items-center gap-2">
                        <button onClick={toggleAllScenes} className="text-[9px] text-indigo-500 hover:text-indigo-700 font-medium">
                          {selectedScenes.size === lifestyleAnalysis.lifestyleScenes.length ? "Hiçbirini Seç" : "Tümünü Seç"}
                        </button>
                        {selectedScenes.size > 0 && (
                          <button onClick={handleBatchGenerate} disabled={isGeneratingBatch}
                            className="flex items-center gap-1 text-[9px] font-medium bg-purple-500 text-white px-2 py-1 rounded hover:bg-purple-600 disabled:opacity-50">
                            {isGeneratingBatch ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                            Seçilenleri Oluştur ({selectedScenes.size})
                          </button>
                        )}
                        {lifestyleResults.some(r => r?.imageUrl && !isInProductImages(r.imageUrl)) && (
                          <button onClick={() => lifestyleResults.forEach(r => { if (r?.imageUrl) addLifestyleToExport(r.imageUrl); })}
                            className="flex items-center gap-1 text-[9px] font-medium bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600">
                            <Plus className="h-3 w-3" />Tümünü Ekle
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Scene grid - 2 columns for wider layout */}
                    <div className="grid grid-cols-2 gap-3">
                      {lifestyleAnalysis.lifestyleScenes.map((scene, idx) => {
                        const result = lifestyleResults[idx];
                        const isSelected = selectedScenes.has(idx);
                        const isEditingThis = editingPrompt?.index === idx;
                        return (
                          <div key={idx} className={`rounded-lg border p-2.5 space-y-2 ${isSelected ? "border-purple-300 bg-purple-50/30" : "border-gray-200"}`}>
                            <div className="flex items-start gap-1.5">
                              <button onClick={() => toggleSceneSelection(idx)} className="flex-shrink-0 mt-0.5">
                                {isSelected ? <CheckSquare className="h-3.5 w-3.5 text-purple-500" /> : <Square className="h-3.5 w-3.5 text-gray-400" />}
                              </button>
                              {isEditingThis ? (
                                <div className="flex-1 space-y-1">
                                  <textarea value={editingPrompt.text} onChange={e => setEditingPrompt({ index: idx, text: e.target.value })}
                                    className="w-full text-[10px] text-gray-700 leading-relaxed border border-gray-300 rounded px-2 py-1 resize-none focus:ring-1 focus:ring-purple-300 focus:border-purple-300"
                                    rows={3} />
                                  <div className="flex gap-1">
                                    <button onClick={() => { handleGenerateLifestyle(editingPrompt.text, idx); setEditingPrompt(null); }}
                                      className="text-[9px] font-medium text-white bg-purple-500 px-2 py-0.5 rounded hover:bg-purple-600">Oluştur</button>
                                    <button onClick={() => setEditingPrompt(null)}
                                      className="text-[9px] text-gray-500 px-2 py-0.5 rounded hover:bg-gray-100">İptal</button>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-[10px] text-gray-600 leading-relaxed flex-1 cursor-pointer hover:text-gray-800"
                                  onClick={() => setEditingPrompt({ index: idx, text: result?.prompt || scene })}
                                  title="Tıkla: promptu düzenle">
                                  {result?.prompt || scene}
                                </p>
                              )}
                            </div>
                            {result?.imageUrl ? (
                              <div className="relative group">
                                <img src={result.imageUrl} alt={`Lifestyle ${idx + 1}`} loading="lazy" decoding="async" className="w-full aspect-square object-cover rounded-lg border border-gray-200 cursor-pointer" onClick={() => openLightbox([result.imageUrl!], 0)} />
                                {isInProductImages(result.imageUrl) && (
                                  <div className="absolute top-1 left-1 bg-green-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                    <CheckSquare className="h-2 w-2" />Eklendi
                                  </div>
                                )}
                                <div className="flex items-center gap-1.5 mt-1.5">
                                  {isInProductImages(result.imageUrl) ? (
                                    <span className="flex items-center gap-1 text-[10px] font-semibold text-green-600 bg-green-50 border border-green-200 px-2.5 py-1 rounded-md">
                                      <CheckSquare className="h-3.5 w-3.5" />Eklendi
                                    </span>
                                  ) : productImages.length >= 20 ? (
                                    <button disabled title="20 görsel limiti dolu — önce Ürün Görselleri'nden birini kaldır"
                                      className="flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-300 px-2.5 py-1 rounded-md cursor-not-allowed">
                                      <Plus className="h-3.5 w-3.5" />20/20 dolu
                                    </button>
                                  ) : (
                                    <button onClick={() => addLifestyleToExport(result.imageUrl!)}
                                      className="flex items-center gap-1 text-[10px] font-bold text-white bg-green-600 hover:bg-green-700 px-3 py-1.5 rounded-md shadow-sm transition-colors">
                                      <Plus className="h-4 w-4" />Görsellere Ekle
                                    </button>
                                  )}
                                  <button onClick={() => setEditingPrompt({ index: idx, text: result.prompt || scene })}
                                    className="flex items-center gap-1 text-[9px] font-medium text-gray-400 hover:text-gray-600 px-2 py-1 rounded ml-auto">
                                    <RefreshCw className="h-3 w-3" />Promptu Düzenle
                                  </button>
                                </div>
                              </div>
                            ) : result?.loading ? (
                              <div className="flex items-center justify-center py-6 bg-gray-50 rounded-lg">
                                <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
                                <span className="text-[10px] text-gray-500 ml-2">Oluşturuluyor...</span>
                              </div>
                            ) : result?.error ? (
                              <p className="text-[10px] text-red-500">{result.error}</p>
                            ) : null}
                            {!isEditingThis && (
                              <button onClick={() => handleGenerateLifestyle(result?.prompt || scene, idx)}
                                disabled={result?.loading}
                                className="flex items-center gap-1 text-[10px] font-medium text-purple-600 hover:text-purple-700 disabled:opacity-50">
                                <Wand2 className="h-3 w-3" />{result?.imageUrl ? "Yeniden Oluştur" : "Oluştur"}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Video Generation - Right Side */}
        <div className="col-span-4">
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2 mb-3">
                <Film className="h-3.5 w-3.5" />VIDEO OLUŞTUR
                {videoUrl && <span className="text-[10px] text-emerald-500 font-medium bg-emerald-50 px-1.5 py-0.5 rounded ml-auto">Hazır</span>}
              </h3>

              <div className="space-y-3">
                {/* Aspect Ratio */}
                <div>
                  <span className="text-[10px] text-gray-600 font-medium block mb-1">Format:</span>
                  <div className="flex gap-1.5">
                    {(["1:1", "16:9", "9:16"] as const).map(ar => (
                      <button key={ar} onClick={() => setVideoAspectRatio(ar)}
                        className={`flex-1 text-[10px] px-2 py-1.5 rounded border text-center ${videoAspectRatio === ar ? "bg-indigo-50 border-indigo-300 text-indigo-700 font-medium" : "border-gray-200 text-gray-500 hover:bg-gray-50"}`}>
                        {ar === "1:1" ? "Kare" : ar === "16:9" ? "Yatay" : "Dikey"}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Duration */}
                <div>
                  <span className="text-[10px] text-gray-600 font-medium block mb-1">Süre:</span>
                  <div className="flex gap-1.5">
                    {([6, 8] as const).map(d => (
                      <button key={d} onClick={() => setVideoDuration(d)}
                        className={`flex-1 text-[10px] px-2 py-1.5 rounded border text-center ${videoDuration === d ? "bg-indigo-50 border-indigo-300 text-indigo-700 font-medium" : "border-gray-200 text-gray-500 hover:bg-gray-50"}`}>
                        {d} saniye
                      </button>
                    ))}
                  </div>
                </div>

                {/* Zoom */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-gray-600 font-medium">Zoom Efekti:</span>
                    <span className="text-[10px] text-indigo-600 font-medium">{videoZoom === 0 ? "Kapalı" : `%${videoZoom}`}</span>
                  </div>
                  <input type="range" min={0} max={15} step={1} value={videoZoom}
                    onChange={e => setVideoZoom(Number(e.target.value))}
                    className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-500" />
                  <div className="flex justify-between text-[8px] text-gray-400 mt-0.5">
                    <span>Kapalı</span><span>%5</span><span>%10</span><span>%15</span>
                  </div>
                </div>

                {/* Generate Button */}
                <button onClick={handleGenerateVideo}
                  disabled={videoGenerating || productImages.length === 0}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-purple-500 to-indigo-500 px-3 py-2.5 text-xs font-medium text-white hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed">
                  {videoGenerating ? (
                    <><Loader2 className="h-4 w-4 animate-spin" />Video Oluşturuluyor...</>
                  ) : (
                    <><Film className="h-4 w-4" />Video Oluştur ({productImages.length} görsel)</>
                  )}
                </button>

                {productImages.length === 0 && (
                  <p className="text-[10px] text-amber-500">Önce ürün görselleri ekleyin.</p>
                )}

                {videoError && (
                  <div className="text-[10px] text-red-500 bg-red-50 rounded p-2">{videoError}</div>
                )}

                {/* Video Preview */}
                {videoUrl && (
                  <div className="space-y-2">
                    <video src={videoUrl} controls autoPlay loop muted
                      className="w-full rounded-lg border border-gray-200" />
                    <div className="flex gap-2">
                      <a href={videoUrl} download="listing-video.mp4"
                        className="flex-1 flex items-center justify-center gap-1 text-[10px] font-medium text-indigo-600 hover:text-indigo-700 bg-indigo-50 px-3 py-1.5 rounded-lg">
                        <Download className="h-3 w-3" />İndir
                      </a>
                      <button onClick={handleExportToListing} disabled={productImages.length === 0}
                        className="flex-1 flex items-center justify-center gap-1 text-[10px] font-medium text-emerald-600 hover:text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-lg">
                        <FileText className="h-3 w-3" />Listing'e Aktar
                      </button>
                    </div>
                    <p className="text-[9px] text-gray-400">Etsy uyumlu: MP4, H.264, max 100MB, 5-15sn önerilen</p>
                  </div>
                )}

                {/* Yerel LTX ile AI video - slideshow'dan bagimsiz, ayni gorseli kullanir */}
                <LtxVideoPanel
                  imageUrl={productImages[0]?.url ?? null}
                  title={seoContent?.title || productImages[0]?.label}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAddMockups && (
        <MockupPickerModal
          templates={customTemplates}
          selected={selectedTemplates}
          onToggle={toggleTemplate}
          onClose={() => setShowAddMockups(false)}
          vars={supplierVars}
          onAssign={assignMockupVariation}
          folders={mockupFolders}
        />
      )}

      {showConsistency && seoContent && (
        <DesignConsistencyModal
          imageUrl={consistencyImageUrl}
          seo={{ title: seoContent.title || "", description: seoContent.description || "", tags: seoContent.tags || [] }}
          targetProductType={Array.from(productTypes)[0] || "t-shirt"}
          niche={lifestyleAnalysis?.targetAudience}
          analysisId={analysisIdFromUrl}
          templateId={selectedListingTemplateId || undefined}
          onApply={(newSeo) => {
            setSeoContent((prev) => ({ ...(prev || {}), title: newSeo.title, description: newSeo.description, tags: newSeo.tags } as typeof prev));
            setShowConsistency(false);
            runExport(newSeo);
          }}
          onProceed={() => { setShowConsistency(false); runExport(); }}
          onClose={() => setShowConsistency(false)}
        />
      )}

      {configuringMockup && (
        <DesignAreaEditor
          mockup={configuringMockup}
          testDesignLight={lightDesignUrl || designUrl}
          testDesignDark={darkDesignUrl || designUrl}
          onSave={(areas) => handleSaveDesignArea(configuringMockup.id, areas)}
          onCancel={() => setConfiguringMockup(null)}
        />
      )}

      {/* Fullscreen lightbox with prev/next navigation */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-8" onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20 text-white z-10">
            <X className="h-6 w-6" />
          </button>
          {(() => {
            const pi = productImages.find((p) => p.url === lightbox.urls[lightbox.index]);
            const template = pi?.templateId ? allTemplates.find((t) => t.id === pi.templateId) : null;
            const editable = pi?.templateId && customMockups.some((m) => `custom_${m.id}` === pi.templateId);
            const singleArea = !!template && (template.designAreas?.length ?? 0) === 1;
            return editable ? (
              <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
                <button onClick={(e) => { e.stopPropagation(); editLightboxArea(); }}
                  className="flex items-center gap-1.5 px-3 py-2 bg-white/90 hover:bg-white text-gray-800 rounded-lg text-sm font-medium shadow" title="Mockup alanını düzenle (kalıcı)">
                  <Settings2 className="h-4 w-4 text-indigo-600" /> Alanı Düzenle
                </button>
                {singleArea && (
                  <button onClick={(e) => { e.stopPropagation(); editLightboxFree(); }}
                    className="flex items-center gap-1.5 px-3 py-2 bg-white/90 hover:bg-white text-gray-800 rounded-lg text-sm font-medium shadow" title="Tasarımı bu fotoğrafta serbestçe taşı/boyutlandır (sadece bu görsel)">
                    <Move className="h-4 w-4 text-emerald-600" /> Bu fotoğrafta düzenle
                  </button>
                )}
              </div>
            ) : null;
          })()}
          {lightbox.urls.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); lbStep(-1); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 rounded-full hover:bg-white/20 text-white z-10" title="Önceki (←)">
                <ChevronLeft className="h-7 w-7" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); lbStep(1); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 rounded-full hover:bg-white/20 text-white z-10" title="Sonraki (→)">
                <ChevronRight className="h-7 w-7" />
              </button>
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/40 px-3 py-1 rounded-full">
                {lightbox.index + 1} / {lightbox.urls.length}
              </div>
            </>
          )}
          <img src={lightbox.urls[lightbox.index]} alt="Önizleme" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* Re-edit a product image's design area */}
      {/* "Alani Duzenle" -> mockup area editor (persistent) */}
      {productAreaEdit && (
        <DesignAreaEditor
          mockup={productAreaEdit.mockup}
          testDesignLight={lightDesignUrl || designUrl}
          testDesignDark={darkDesignUrl || designUrl}
          onSave={(areas) => applyMockupAreaEdit(areas)}
          onCancel={() => setProductAreaEdit(null)}
        />
      )}
      {/* "Bu fotografta duzenle" -> frameless free design placement (this image only) */}
      {freeEdit && (
        <FreeDesignEditor
          mockupImageUrl={freeEdit.mockupImageUrl}
          designUrl={freeEdit.designUrl}
          canvasW={freeEdit.canvasW}
          canvasH={freeEdit.canvasH}
          initial={freeEdit.initial}
          opacity={freeEdit.opacity}
          onSave={applyFreeEdit}
          onCancel={() => setFreeEdit(null)}
        />
      )}

      {/* Listing Builder aktarim kuyrugu (arka plan) */}
      {exportQueue.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 w-72 rounded-xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
            <span className="text-xs font-semibold text-gray-700">Listing Builder Aktarımı</span>
            <button onClick={() => setExportQueue((q) => q.filter((j) => j.status === "uploading"))} className="text-[10px] text-gray-400 hover:text-gray-600">Temizle</button>
          </div>
          {exportQueue.some((j) => j.status === "uploading") && (
            <div className="flex items-start gap-1.5 border-b border-emerald-100 bg-emerald-50 px-3 py-2 text-[10px] font-medium text-emerald-700">
              <CheckCircle className="mt-0.5 h-3 w-3 flex-shrink-0" />
              <span>Kuyruğa eklendi. İşlem sunucuda sürüyor — bu sekmeyi kapatabilirsiniz.</span>
            </div>
          )}
          <div className="max-h-64 overflow-y-auto divide-y divide-gray-50">
            {exportQueue.map((j) => (
              <div key={j.id} className="flex items-center gap-2 px-3 py-2">
                <span className={`h-2 w-2 flex-shrink-0 rounded-full ${j.status === "done" ? "bg-emerald-500" : j.status === "error" ? "bg-red-500" : "bg-amber-400 animate-pulse"}`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[11px] font-medium text-gray-700" title={j.title}>{j.title}</p>
                  <p className="text-[10px] text-gray-400">
                    {j.status === "uploading"
                      ? (j.video ? "Video üretiliyor + aktarılıyor… (kapatabilirsiniz)" : "Aktarılıyor… (kapatabilirsiniz)")
                      : j.status === "done" ? "Aktarıldı ✓" : `Hata: ${j.error || "?"}`}
                  </p>
                </div>
                {j.status === "done" && j.draftId && (
                  <button onClick={() => navigate(`/listing-builder?id=${j.draftId}`)} className="flex-shrink-0 rounded border border-indigo-200 bg-indigo-50 px-2 py-1 text-[10px] font-medium text-indigo-600 hover:bg-indigo-100">Aç</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
